import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { formatDateTime } from "../i18n/formatters";
import { adminApi, accountApi, type AuditLogEntry, type RequestLogEntry } from "../lib/api";
import { describeRequest } from "../lib/request-log-describe";
import { compressImageFile } from "../lib/image-compress";
import { Database, RefreshCw, Menu, X, Save, Trash2, Plus, Pencil, Copy, User as UserIcon, Users as UsersIcon, ArrowLeft, Search, SlidersHorizontal, Eye, Check, ArrowUp, ArrowDown, Target, Clock, Award, MessageCircle, ExternalLink, AlertTriangle, Calendar, RotateCcw, FileText, ChevronLeft, ChevronRight, Trophy, Home, Mail, Languages, Sparkles } from "lucide-react";
import { AdminHomeGrid } from "../components/AdminHomeGrid";
import { useResizable } from "../hooks/useResizable";
import { usePanelResize } from "../hooks/usePanelResize";
import { ResizeHandles, PanelDivider } from "../components/ui/resize-handles";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
} from "../components/ui/dropdown-menu";
import { Badge } from "../components/ui/badge";
import { ConfirmDeleteDialog } from "../components/ui/confirm-dialog";
import { buyLinkProblem, licenceCodeToSlug } from "../lib/buy-link";
import {
  useListViews,
  ViewSelector,
  ViewEditorModal,
  type FilterValue as ViewFilterValue,
} from "../components/admin/list-views";
import { BadgeBankView } from "../components/BadgeBankView";
import { InterChallengesView } from "../components/InterChallengesView";
import { AdminCatalogContextsTab } from "../components/AdminCatalogContextsTab";
import { AdminTrialInvitationsTab } from "../components/AdminTrialInvitationsTab";
import { CustomLicenceWizard } from "../components/CustomLicenceWizard";
import { sessionsPerOneShotPack, ONESHOT_VALIDITY_MONTHS } from "../lib/oneshot-constants";

interface TableData {
  data: Record<string, unknown>[];
  count: number;
  columns: string[];
  /**
   * Table que ce profil LIT sans pouvoir la modifier (servi par `GET /api/admin/tables`, règle
   * `canAccessTable` de `src/api/admin.ts`) — décision Q1 du 01/10/2026 : le COO lit
   * `planned_sessions`, `competency_snapshots` et `manager_reviews`. Aucun geste d'écriture affiché.
   */
  readOnly?: boolean;
}

type Tables = Record<string, TableData>;

const TABLE_LABELS: Record<string, string> = {
  user_entity_access: "Portefeuilles consultants",
  cockpit_signals: "Alertes cockpit",
  campaigns: "Campagnes",
  campaign_items: "Briques de campagne",
  planned_sessions: "Sessions planifiées",
  campaign_member_rhythm: "Rythmes de campagne",
  user_session_streak: "Séries de sessions",
  competency_snapshots: "Photos de niveau (hebdo)",
  manager_reviews: "Revues du lundi",
  manager_nudges: "Relances manager",
  users: "Users",
  client_accounts: "Client Accounts",
  simulators: "Simulateurs",
  licences: "Produits (abos & packs)",
  user_licences: "User Licences",
  simulator_licences: "Simulator Licences",
  scenario_requests: "Scenario Requests",
  scenarios: "Scénarios",
  audio_sessions: "Audio Sessions",

  targets: "Cibles",
  target_simulator_fit: "Compatibilité cas ↔ simulateur",
  case_simulator_brief: "Briefs cas ↔ simulateur",
  case_scenario_translations: "Cas génériques traduits (langue d'entraînement)",
  personae: "Personae",
  scenario_chat_messages: "Scenario Chat Messages",
  n8n_workflows: "N8N Workflows",
  sessions: "Sessions",
  feedbacks: "Feedbacks",
  messages: "Messages",
  categories: "Catégories",
  feedback_scores: "Feedback Scores",
  evaluation_criteria_translations: "Critères traduits (langues d'entraînement)",
  challenges: "Challenges",
  challenge_teams: "Challenge Teams",
  challenge_participants: "Challenge Participants",
  challenge_notifications: "Challenge Notifications",
  user_memories: "User Memories",
  ai_prompts: "Prompts IA",
  offers: "Offres",
  subscriptions: "Abonnements",
  contexts: "Contextes",
  model_pricing: "Tarifs modèles (LLM/Realtime)",
  // « S'exercer » (DRILL)
  exercise_sheets: "S'exercer — Fiches d'exercice",
  learning_capsules: "S'exercer — Capsules learning",
  drill_sessions: "S'exercer — Sessions d'exercices",
  drill_messages: "S'exercer — Transcripts d'exercices",
  user_capsule_progress: "S'exercer — Complétion capsules",
  capsule_links: "S'exercer — Liaisons capsules",
  exercise_sheet_methodo_sheets: "S'exercer — Liaisons méthodo",
  activity_sectors: "S'exercer — Secteurs d'activité",
  drill_sector_rotation: "S'exercer — Rotation secteurs",
};

const TABLE_ORDER = [
  "users",
  "client_accounts",
  "user_entity_access",
  "simulators",
  "licences",
  "user_licences",
  "simulator_licences",
  "scenario_requests",
  "scenarios",
  "audio_sessions",

  "targets",
  "target_simulator_fit",
  "case_simulator_brief",
  "case_scenario_translations",
  "personae",
  "scenario_chat_messages",
  "n8n_workflows",
  "sessions",
  "feedbacks",
  "messages",
  "categories",
  "feedback_scores",
  "evaluation_criteria_translations",
  "challenges",
  "challenge_teams",
  "challenge_participants",
  "challenge_notifications",
  "user_memories",
  "ai_prompts",
  "cockpit_signals",
  "campaigns",
  "campaign_items",
  "manager_nudges",
  "planned_sessions",
  "campaign_member_rhythm",
  "user_session_streak",
  "competency_snapshots",
  "manager_reviews",
  "offers",
  "subscriptions",
  "contexts",
  "model_pricing",
  // « S'exercer » (DRILL)
  "exercise_sheets",
  "learning_capsules",
  "drill_sessions",
  "drill_messages",
  "user_capsule_progress",
  "capsule_links",
  "exercise_sheet_methodo_sheets",
  "activity_sectors",
  "drill_sector_rotation",
];

const READ_ONLY_FIELDS = new Set(["id", "createdAt", "updatedAt"]);
const HIDDEN_FIELDS = new Set(["id", "createdAt", "updatedAt", "zohoCrmContactId"]);
const TABLES_WITHOUT_CREATE = new Set(["users"]);

function getProfileTypeOptions() {
  return [
    { value: "ADM", label: i18n.t("admin.profileTypes.ADM") },
    { value: "CSL", label: i18n.t("admin.profileTypes.CSL") },
    { value: "COO", label: i18n.t("admin.profileTypes.COO") },
    { value: "MNG", label: i18n.t("admin.profileTypes.MNG") },
    { value: "ORG", label: i18n.t("admin.profileTypes.ORG") },
    { value: "GES", label: i18n.t("admin.profileTypes.GES") },
    { value: "BNF", label: i18n.t("admin.profileTypes.BNF") },
    { value: "RH", label: i18n.t("admin.profileTypes.RH") },
  ];
}

function getStatusOptions() {
  return [
    { value: "created", label: "Créé" },
    { value: "invited", label: i18n.t("admin.statusTypes.invited") },
    { value: "active", label: i18n.t("admin.statusTypes.active") },
    { value: "disabled", label: i18n.t("admin.statusTypes.disabled") },
  ];
}

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? i18n.t("admin.boolTrue") : i18n.t("admin.boolFalse");
  if (value instanceof Date) return formatDateTime(value.toISOString());
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return formatDateTime(value);
  }
  if (typeof value === "object") {
    const json = JSON.stringify(value);
    return json.length > 80 ? json.slice(0, 80) + "..." : json;
  }
  if (typeof value === "string" && value.length > 80) {
    return value.slice(0, 80) + "...";
  }
  return String(value);
}

// Mapping des champs FK → table source + fonction d'affichage
const FK_DISPLAY: Record<string, { table: string; display: (row: Record<string, unknown>) => string }> = {
  clientAccountId: { table: "client_accounts", display: (r) => String(r.name || r.id) },
  userId: { table: "users", display: (r) => `${r.firstName || ""} ${r.lastName || ""}`.trim() || String(r.email || r.id) },
  licenceId: { table: "licences", display: (r) => String(r.name || r.id) },
  simulatorId: { table: "simulators", display: (r) => String(r.name || r.id) },
  feedbackId: { table: "feedbacks", display: (r) => String(r.id) },
  feedbackAudioSessionId: { table: "audio_sessions", display: (r) => String(r.id) },
  selectedScenarioId: { table: "scenarios", display: (r) => String(r.id) },
  simulationAudioSessionId: { table: "audio_sessions", display: (r) => String(r.id) },
  scenarioId: { table: "scenarios", display: (r) => String(r.id) },
  scenarioPropositionId: { table: "scenarios", display: (r) => String(r.id) },
  scenarioRequestId: { table: "scenario_requests", display: (r) => String(r.id) },
  creatorId: { table: "users", display: (r) => `${r.firstName || ""} ${r.lastName || ""}`.trim() || String(r.email || r.id) },
  createdBy: { table: "users", display: (r) => `${r.firstName || ""} ${r.lastName || ""}`.trim() || String(r.email || r.id) },
  audioSessionId: { table: "audio_sessions", display: (r) => String(r.id) },
  sessionId: { table: "sessions", display: (r) => String(r.id) },
  challengeId: { table: "challenges", display: (r) => String(r.title || r.id) },
  teamId: { table: "challenge_teams", display: (r) => String(r.name || r.id) },
};

// --- Filtres : types et constantes ---

type ColumnType = "text" | "enum" | "fk" | "date" | "boolean" | "number" | "id";

function getEnumColumns(): Record<string, { value: string; label: string }[]> {
  return {
    profileType: getProfileTypeOptions(),
    status: getStatusOptions(),
    simulatorType: [
      { value: "training", label: "Training" },
      { value: "coach", label: "Coach" },
    ],
    civility: [
      { value: "M.", label: "M." },
      { value: "Mme", label: "Mme" },
    ],
    role: [
      { value: "coach", label: "Coach" },
      { value: "user", label: "User" },
      { value: "agent", label: "Agent" },
      { value: "chuchoteur", label: "Chuchoteur" },
      { value: "feedback", label: "Feedback" },
      { value: "fait_etabli", label: "Fait établi" },
    ],
    challengeType: [
      { value: "mandatory", label: "Obligatoire" },
      { value: "optional", label: "Optionnel" },
    ],
    primaryObjective: [
      { value: "most_rdv", label: "Plus de RDV" },
      { value: "cumulative_score", label: "Score cumulé" },
      { value: "best_average", label: "Meilleure moyenne" },
    ],
    secondaryObjective: [
      { value: "most_rdv", label: "Plus de RDV" },
      { value: "cumulative_score", label: "Score cumulé" },
      { value: "best_average", label: "Meilleure moyenne" },
    ],
    teamAssignmentMode: [
      { value: "org_assigned", label: "Assigné par l'org" },
      { value: "participant_choice", label: "Choix participant" },
    ],
  };
}

const BOOLEAN_COLUMNS = new Set(["isActive"]);

function detectColumnType(col: string): ColumnType {
  if (col === "id") return "id";
  if (FK_DISPLAY[col]) return "fk";
  if (getEnumColumns()[col]) return "enum";
  if (BOOLEAN_COLUMNS.has(col)) return "boolean";
  if (col === "timestamp" || col.endsWith("At")) return "date";
  if (col === "price" || col.endsWith("Score")) return "number";
  return "text";
}

const DEFAULT_HIDDEN: Record<string, Set<string>> = {
  users: new Set(["id", "profilePicture", "zohoCrmContactId", "linkedinUrl"]),
  client_accounts: new Set(["id", "description", "tva", "zohoCrmAccountId", "zohoBillingAccountId"]),
  simulators: new Set(["id", "systemPrompt"]),
  scenario_requests: new Set(["id"]),
  // Textes longs masqués en grille (illisibles en colonne) — restent éditables via la fiche.
  // selectedIndustry est court ET utile au diagnostic (N8N a-t-il renvoyé une valeur
  // reconnue ?) → volontairement PAS masqué.
  scenarios: new Set(["id", "userMessage", "raw", "variables", "priorContext", "prospectCompanyDescription"]),
  audio_sessions: new Set(["id"]),

  targets: new Set(["id", "decisionMakerProfile", "painPoints"]),
  sessions: new Set(["id"]),
  scenario_chat_messages: new Set(["id", "content"]),
  n8n_workflows: new Set(["id"]),
  feedbacks: new Set(["id", "content", "raw"]),
};

const GLOBAL_HIDDEN = new Set(["id", "systemPrompt"]);

type FilterValue =
  | { type: "text"; value: string }
  | { type: "enum"; value: string }
  | { type: "fk"; value: string }
  | { type: "date"; from: string; to: string }
  | { type: "boolean"; value: "yes" | "no" }
  | { type: "number"; min: string; max: string };

function isFilterActive(f: FilterValue): boolean {
  switch (f.type) {
    case "text":
    case "enum":
    case "fk":
      return f.value !== "";
    case "boolean":
      return true;
    case "date":
      return f.from !== "" || f.to !== "";
    case "number":
      return f.min !== "" || f.max !== "";
  }
}

function summarizeFilter(
  filter: FilterValue,
  col: string,
  lookupMaps?: LookupMaps
): string {
  switch (filter.type) {
    case "text":
      return `"${filter.value}"`;
    case "enum": {
      const opts = getEnumColumns()[col];
      const opt = opts?.find((o) => o.value === filter.value);
      return opt ? opt.label : filter.value;
    }
    case "fk": {
      if (lookupMaps && lookupMaps[col]) {
        return lookupMaps[col].get(filter.value) || filter.value;
      }
      return filter.value;
    }
    case "date": {
      const parts: string[] = [];
      if (filter.from) parts.push(`${i18n.t("admin.dateFrom")} ${filter.from}`);
      if (filter.to) parts.push(`${i18n.t("admin.dateTo")} ${filter.to}`);
      return parts.join(" ");
    }
    case "boolean":
      return filter.value === "yes" ? i18n.t("admin.boolTrue") : i18n.t("admin.boolFalse");
    case "number": {
      const parts: string[] = [];
      if (filter.min) parts.push(`≥ ${filter.min}`);
      if (filter.max) parts.push(`≤ ${filter.max}`);
      return parts.join(" ");
    }
  }
}

function matchesFilter(
  row: Record<string, unknown>,
  col: string,
  filter: FilterValue
): boolean {
  const raw = row[col];
  const str = raw === null || raw === undefined ? "" : String(raw);

  switch (filter.type) {
    case "text":
      return str.toLowerCase().includes(filter.value.toLowerCase());
    case "enum":
    case "fk":
      return str === filter.value;
    case "boolean":
      return filter.value === "yes"
        ? raw === true || str === "true"
        : raw === false || str === "false";
    case "date": {
      if (!str) return false;
      const d = str.slice(0, 10); // YYYY-MM-DD
      if (filter.from && d < filter.from) return false;
      if (filter.to && d > filter.to) return false;
      return true;
    }
    case "number": {
      const n = parseFloat(str);
      if (isNaN(n)) return false;
      if (filter.min !== "" && n < parseFloat(filter.min)) return false;
      if (filter.max !== "" && n > parseFloat(filter.max)) return false;
      return true;
    }
  }
}

function renderFilterControl(
  col: string,
  colType: ColumnType,
  currentFilter: FilterValue | undefined,
  setFilter: (col: string, filter: FilterValue | undefined) => void,
  lookupMaps?: LookupMaps
) {
  const inputClass =
    "w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#384872]/30 focus:border-[#384872]";

  switch (colType) {
    case "text":
    case "id":
      return (
        <input
          type="text"
          placeholder={i18n.t("admin.contains")}
          value={currentFilter?.type === "text" ? currentFilter.value : ""}
          onChange={(e) =>
            e.target.value
              ? setFilter(col, { type: "text", value: e.target.value })
              : setFilter(col, undefined)
          }
          className={inputClass}
        />
      );
    case "enum":
      return (
        <select
          value={currentFilter?.type === "enum" ? currentFilter.value : ""}
          onChange={(e) =>
            e.target.value
              ? setFilter(col, { type: "enum", value: e.target.value })
              : setFilter(col, undefined)
          }
          className={inputClass}
        >
          <option value="">{i18n.t("admin.all")}</option>
          {(getEnumColumns()[col] || []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    case "fk": {
      const map = lookupMaps?.[col];
      const entries = map ? Array.from(map.entries()) : [];
      return (
        <select
          value={currentFilter?.type === "fk" ? currentFilter.value : ""}
          onChange={(e) =>
            e.target.value
              ? setFilter(col, { type: "fk", value: e.target.value })
              : setFilter(col, undefined)
          }
          className={inputClass}
        >
          <option value="">{i18n.t("admin.all")}</option>
          {entries.map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
      );
    }
    case "date": {
      const from = currentFilter?.type === "date" ? currentFilter.from : "";
      const to = currentFilter?.type === "date" ? currentFilter.to : "";
      return (
        <div className="flex gap-1">
          <input
            type="date"
            value={from}
            onChange={(e) => {
              const newFrom = e.target.value;
              if (!newFrom && !to) {
                setFilter(col, undefined);
              } else {
                setFilter(col, { type: "date", from: newFrom, to });
              }
            }}
            className={inputClass + " flex-1"}
            title={i18n.t("admin.dateFrom")}
          />
          <input
            type="date"
            value={to}
            onChange={(e) => {
              const newTo = e.target.value;
              if (!from && !newTo) {
                setFilter(col, undefined);
              } else {
                setFilter(col, { type: "date", from, to: newTo });
              }
            }}
            className={inputClass + " flex-1"}
            title={i18n.t("admin.dateTo")}
          />
        </div>
      );
    }
    case "boolean":
      return (
        <select
          value={currentFilter?.type === "boolean" ? currentFilter.value : ""}
          onChange={(e) =>
            e.target.value
              ? setFilter(col, {
                  type: "boolean",
                  value: e.target.value as "yes" | "no",
                })
              : setFilter(col, undefined)
          }
          className={inputClass}
        >
          <option value="">{i18n.t("admin.all")}</option>
          <option value="yes">{i18n.t("admin.boolTrue")}</option>
          <option value="no">{i18n.t("admin.boolFalse")}</option>
        </select>
      );
    case "number": {
      const min = currentFilter?.type === "number" ? currentFilter.min : "";
      const max = currentFilter?.type === "number" ? currentFilter.max : "";
      return (
        <div className="flex gap-1">
          <input
            type="number"
            placeholder={i18n.t("admin.min")}
            value={min}
            onChange={(e) => {
              const newMin = e.target.value;
              if (!newMin && !max) {
                setFilter(col, undefined);
              } else {
                setFilter(col, { type: "number", min: newMin, max });
              }
            }}
            className={inputClass + " flex-1"}
          />
          <input
            type="number"
            placeholder={i18n.t("admin.max")}
            value={max}
            onChange={(e) => {
              const newMax = e.target.value;
              if (!min && !newMax) {
                setFilter(col, undefined);
              } else {
                setFilter(col, { type: "number", min, max: newMax });
              }
            }}
            className={inputClass + " flex-1"}
          />
        </div>
      );
    }
  }
}

type LookupMaps = Record<string, Map<string, string>>;

function buildLookupMaps(tables: Tables): LookupMaps {
  const maps: LookupMaps = {};
  for (const [field, resolver] of Object.entries(FK_DISPLAY)) {
    const tableData = tables[resolver.table];
    if (tableData) {
      const map = new Map<string, string>();
      for (const row of tableData.data) {
        map.set(String(row.id), resolver.display(row));
      }
      maps[field] = map;
    }
  }
  return maps;
}

function summarizeJsonb(col: string, value: unknown): string | null {
  if (!value || typeof value !== "object") return null;
  const arr = Array.isArray(value) ? value : null;
  if (col === "scoringCriteria" && arr) {
    const labels = arr.map((c: any) => c.labelFr || c.label || c.key).filter(Boolean);
    return labels.length ? labels.join(", ") : null;
  }
  if (col === "formSchema" && arr) {
    const labels = arr.map((f: any) => f.labelFr || f.labelEn || f.key).filter(Boolean);
    return labels.length ? labels.join(", ") : null;
  }
  if (col === "scores" && !Array.isArray(value)) {
    const entries = Object.entries(value as Record<string, unknown>);
    return entries.length ? entries.map(([k, v]) => `${k}: ${v}`).join(", ") : null;
  }
  return null;
}

function resolveValue(col: string, value: unknown, lookupMaps?: LookupMaps): string {
  if (value === null || value === undefined) return "—";
  if (lookupMaps && lookupMaps[col]) {
    const resolved = lookupMaps[col].get(String(value));
    if (resolved) return resolved;
  }
  const summary = summarizeJsonb(col, value);
  if (summary) {
    return summary.length > 80 ? summary.slice(0, 80) + "..." : summary;
  }
  return formatCellValue(value);
}

function UserFormBody({
  formData,
  onChange,
  tables,
}: {
  formData: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  tables?: Tables;
}) {
  const { t } = useTranslation();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Compression côté client (canvas) avant stockage de la photo de profil.
    const dataUrl = await compressImageFile(file);
    onChange("profilePicture", dataUrl);
  };

  const str = (key: string) => {
    const v = formData[key];
    return v === null || v === undefined ? "" : String(v);
  };

  return (
    <>
      <div className="flex items-center gap-4 mb-2">
        <div
          className="relative h-16 w-16 cursor-pointer group flex-shrink-0"
          onClick={() => fileInputRef.current?.click()}
        >
          <Avatar className="h-16 w-16">
            <AvatarImage src={str("profilePicture")} />
            <AvatarFallback className="text-lg bg-slate-100">
              {str("firstName")?.[0] || ""}{str("lastName")?.[0] || ""}
            </AvatarFallback>
          </Avatar>
          <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Pencil className="h-4 w-4 text-white" />
          </div>
        </div>
        <div className="flex-1">
          <Label className="text-xs text-slate-500">{t("admin.photoLabel")}</Label>
          <Input
            type="text"
            placeholder={t("admin.photoUrlPlaceholder")}
            value={str("profilePicture")}
            onChange={(e) => onChange("profilePicture", e.target.value)}
            className="mt-1"
          />
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            className="hidden"
            onChange={handleFileSelect}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.civility")}</Label>
          <Select
            value={str("civility") || undefined}
            onValueChange={(value) => onChange("civility", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("admin.select")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="M.">M.</SelectItem>
              <SelectItem value="Mme">Mme</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.email")}</Label>
          <Input
            type="email"
            autoComplete="email"
            value={str("email")}
            onChange={(e) => onChange("email", e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.firstName")}</Label>
          <Input
            value={str("firstName")}
            onChange={(e) => onChange("firstName", e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.lastName")}</Label>
          <Input
            value={str("lastName")}
            onChange={(e) => onChange("lastName", e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.phone")}</Label>
          <Input
            type="tel"
            value={str("phoneNumber")}
            onChange={(e) => onChange("phoneNumber", e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.linkedin")}</Label>
          <Input
            type="url"
            placeholder="https://linkedin.com/in/..."
            value={str("linkedinUrl")}
            onChange={(e) => onChange("linkedinUrl", e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.profileType")}</Label>
          <Select
            value={str("profileType") || undefined}
            onValueChange={(value) => onChange("profileType", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("admin.select")} />
            </SelectTrigger>
            <SelectContent>
              {getProfileTypeOptions().map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.status")}</Label>
          <Select
            value={str("status") || undefined}
            onValueChange={(value) => onChange("status", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("admin.select")} />
            </SelectTrigger>
            <SelectContent>
              {getStatusOptions().map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Langue</Label>
          <Select
            value={str("language") || "fr"}
            onValueChange={(value) => onChange("language", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("admin.select")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fr">Français</SelectItem>
              <SelectItem value="en">English</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-slate-500">{t("admin.clientAccountId")}</Label>
        {(() => {
          const accountId = str("clientAccountId");
          if (!accountId) return <Input value="" placeholder={t("admin.clientAccountIdPlaceholder")} onChange={(e) => onChange("clientAccountId", e.target.value)} />;
          const account = (tables?.client_accounts?.data ?? []).find((a: any) => a.id === accountId);
          const accountName = account ? String((account as any).name || accountId) : accountId;
          return <Input value={accountName} readOnly className="bg-slate-50" />;
        })()}
      </div>
    </>
  );
}

const IMAGE_FIELDS = new Set(["logoUrl", "profilePicture", "icon", "image"]);

const COLOR_FIELDS = new Set(["color"]);
const URL_FIELDS = new Set(["linkedinUrl", "urlGpt", "prospectUrl", "baseUrl"]);
const JSON_FIELDS = new Set(["raw", "variables", "presentationJson", "content", "scores", "scoringCriteria"]);
const SCORE_FIELDS = new Set([
  "totalScore", "totalTokens",
  "durationInMinutes", "numberOfMessages", "price",
]);

// Section groupings for tables that benefit from it
const VIEW_SECTIONS: Record<string, { label: string; fields: string[] }[]> = {
  simulators: [
    { label: "GENERAL", fields: ["id", "name", "description", "simulatorType", "isActive", "sortOrder"] },
    { label: "APPARENCE", fields: ["icon", "image", "color", "presentationJson"] },
    { label: "IA", fields: ["modeleOpenai", "urlGpt", "systemPrompt"] },
    { label: "METADATA", fields: ["createdAt", "updatedAt", "createdBy"] },
  ],
  users: [
    { label: "IDENTITE", fields: ["id", "civility", "firstName", "lastName", "email", "phoneNumber", "profilePicture"] },
    { label: "PROFIL", fields: ["profileType", "status", "language", "linkedinUrl", "clientAccountId"] },
    { label: "METADATA", fields: ["createdAt", "updatedAt", "zohoCrmContactId"] },
  ],
};

function renderViewField(
  col: string,
  value: unknown,
  lookupMaps: LookupMaps | undefined,
  tables: Tables | undefined,
  onFkClick?: (table: string, id: string) => void
): React.ReactNode {
  // Empty value
  if (value === null || value === undefined || value === "") {
    return <span className="text-slate-300 italic text-sm">--</span>;
  }

  // If value is an object/array, serialize it for display
  const strVal = (typeof value === "object") ? JSON.stringify(value) : String(value);

  // Image fields
  if (IMAGE_FIELDS.has(col)) {
    const isDataUrl = strVal.startsWith("data:");
    const isUrl = strVal.startsWith("http");
    if (isDataUrl || isUrl) {
      return (
        <div className="flex items-center gap-2">
          <img src={strVal} alt={col} loading="lazy" className="h-12 w-12 rounded-md object-cover border flex-shrink-0" />
          <span className="text-xs text-slate-400 truncate max-w-[200px]">{isDataUrl ? "data:image/..." : strVal}</span>
        </div>
      );
    }
    return <span className="text-sm text-slate-800">{strVal}</span>;
  }

  // Color fields
  if (COLOR_FIELDS.has(col)) {
    return (
      <div className="flex items-center gap-2">
        <span className="h-5 w-5 rounded-full border border-slate-200 flex-shrink-0" style={{ backgroundColor: strVal }} />
        <span className="text-sm font-mono text-slate-600">{strVal}</span>
      </div>
    );
  }

  // Boolean fields
  if (BOOLEAN_COLUMNS.has(col) || typeof value === "boolean") {
    const boolVal = value === true || strVal === "true";
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
        boolVal ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
      }`}>
        <span className={`h-1.5 w-1.5 rounded-full ${boolVal ? "bg-emerald-500" : "bg-slate-400"}`} />
        {boolVal ? i18n.t("admin.boolTrue") : i18n.t("admin.boolFalse")}
      </span>
    );
  }

  // FK fields
  if (FK_DISPLAY[col]) {
    const resolved = lookupMaps?.[col]?.get(strVal);
    const fkTable = FK_DISPLAY[col].table;
    return (
      <button
        type="button"
        onClick={() => onFkClick?.(fkTable, strVal)}
        className="inline-flex items-center gap-1 text-sm text-[#384872] hover:underline"
      >
        {resolved || strVal}
        <ExternalLink className="h-3 w-3 opacity-50" />
      </button>
    );
  }

  // URL fields
  if (URL_FIELDS.has(col) || (typeof value === "string" && /^https?:\/\//.test(strVal))) {
    return (
      <a href={strVal} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate block max-w-[300px]">
        {strVal}
      </a>
    );
  }

  // Date fields
  if (col === "timestamp" || col.endsWith("At") || col === "endTime") {
    return <span className="text-sm text-slate-700">{formatDateTime(strVal)}</span>;
  }

  // Enum fields
  if (getEnumColumns()[col]) {
    const opts = getEnumColumns()[col];
    const opt = opts?.find((o) => o.value === strVal);
    return (
      <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
        {opt ? opt.label : strVal}
      </span>
    );
  }

  // Score/number fields
  if (SCORE_FIELDS.has(col)) {
    return <span className="text-sm font-bold font-mono text-slate-800">{strVal}</span>;
  }

  // Scoring criteria — human-readable display
  if (col === "scoringCriteria") {
    let items: { key: string; labelFr?: string; label?: string; type?: string; max?: number }[] = [];
    try {
      items = typeof value === "string" ? JSON.parse(value) : Array.isArray(value) ? value : [];
    } catch { /* ignore */ }
    if (items.length > 0) {
      return (
        <div className="flex flex-wrap gap-1.5">
          {items.map((c, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-slate-100 border border-slate-200 text-slate-700">
              <span className="font-medium">{c.labelFr || c.label || c.key}</span>
              <span className="text-slate-400">({c.type === "boolean" ? "bool" : `0-${c.max || 10}`})</span>
            </span>
          ))}
        </div>
      );
    }
  }

  // JSON / long text fields
  if (JSON_FIELDS.has(col) || col === "systemPrompt" || (typeof value === "string" && strVal.length > 200)) {
    let formatted = strVal;
    try {
      const parsed = typeof value === "string" ? JSON.parse(strVal) : value;
      formatted = JSON.stringify(parsed, null, 2);
    } catch {
      // Not JSON, keep as is
    }
    return (
      <pre className="text-xs bg-slate-50 border rounded-md px-3 py-2 overflow-x-auto max-h-[200px] overflow-y-auto whitespace-pre-wrap font-mono text-slate-700">
        {formatted}
      </pre>
    );
  }

  // Default text
  return <span className="text-sm text-slate-800 break-all whitespace-pre-wrap">{formatCellValue(value)}</span>;
}

function GenericFormBody({
  columns,
  formData,
  isCreate,
  onChange,
}: {
  columns: string[];
  formData: Record<string, unknown>;
  isCreate: boolean;
  onChange: (key: string, value: unknown) => void;
}) {
  return (
    <>
      {columns.map((col) => {
        const isReadOnly = !isCreate && READ_ONLY_FIELDS.has(col);
        const value = formData[col];
        const displayValue = value === null || value === undefined ? "" : String(value);

        if (IMAGE_FIELDS.has(col) && !isReadOnly) {
          return (
            <ImageField
              key={col}
              label={col}
              value={displayValue}
              onChange={(val) => onChange(col, val)}
            />
          );
        }

        return (
          <div key={col}>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              {col}
              {isReadOnly && (
                <span className="ml-1 text-slate-400">{i18n.t("admin.readOnly")}</span>
              )}
            </label>
            {isReadOnly ? (
              <div className="px-3 py-2 bg-slate-50 border rounded-md text-sm text-slate-500 truncate">
                {formatCellValue(value)}
              </div>
            ) : displayValue.length > 100 ? (
              <textarea
                value={displayValue}
                onChange={(e) => onChange(col, e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#384872]/30 focus:border-[#384872] resize-y"
              />
            ) : (
              <input
                type="text"
                value={displayValue}
                onChange={(e) => onChange(col, e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#384872]/30 focus:border-[#384872]"
              />
            )}
          </div>
        );
      })}
    </>
  );
}

function ImageField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
}) {
  const { t } = useTranslation();
  const fileRef = React.useRef<HTMLInputElement>(null);
  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Redimensionnement + ré-encodage WebP côté client (canvas) avant stockage :
    // plafonne le poids de toute image future (≤ 512 px) sans lib serveur.
    const dataUrl = await compressImageFile(file);
    onChange(dataUrl);
  };
  const isDataUrl = value.startsWith("data:");
  const displayValue = isDataUrl ? "data:image/… (base64, " + Math.round(value.length / 1024) + " Ko)" : value;
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-500">{label}</Label>
      <div className="flex items-center gap-2">
        {value && (
          <img
            src={value}
            alt={label}
            className="h-10 w-10 rounded-md object-cover border flex-shrink-0"
          />
        )}
        <Input
          type="text"
          placeholder={t("admin.imageUrlPlaceholder")}
          value={isDataUrl ? displayValue : value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={isDataUrl}
          className={`flex-1 ${isDataUrl ? "text-slate-400 italic" : ""}`}
        />
        {value && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange("")}
            className="flex-shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50"
            title="Effacer"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileRef.current?.click()}
          className="flex-shrink-0"
        >
          {t("admin.browse")}
        </Button>
        <input
          type="file"
          ref={fileRef}
          accept="image/*"
          className="hidden"
          onChange={handleFile}
        />
      </div>
    </div>
  );
}

interface ScoringCriterionItem {
  key: string;
  label: string;
  labelFr?: string;
  type: "score" | "boolean";
  max?: number;
}

function ScoringCriteriaEditor({
  value,
  onChange,
}: {
  value: unknown;
  onChange: (val: ScoringCriterionItem[]) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const parseCriteria = (): ScoringCriterionItem[] => {
    if (Array.isArray(value)) return value as ScoringCriterionItem[];
    if (typeof value === "string") {
      try { return JSON.parse(value); } catch { return []; }
    }
    return [];
  };

  const [criteria, setCriteria] = React.useState<ScoringCriterionItem[]>(parseCriteria);

  React.useEffect(() => {
    setCriteria(parseCriteria());
  }, [value]);

  const update = (newCriteria: ScoringCriterionItem[]) => {
    setCriteria(newCriteria);
    onChange(newCriteria);
  };

  const updateItem = (index: number, field: keyof ScoringCriterionItem, val: string | number) => {
    const next = [...criteria];
    next[index] = { ...next[index], [field]: val };
    update(next);
  };

  const addItem = () => {
    update([...criteria, { key: "", label: "", labelFr: "", type: "score", max: 10 }]);
  };

  const removeItem = (index: number) => {
    update(criteria.filter((_, i) => i !== index));
  };

  const moveItem = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= criteria.length) return;
    const next = [...criteria];
    [next[index], next[target]] = [next[target], next[index]];
    update(next);
  };

  // Résumé inline : bouton + pills
  const summary = (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-slate-500">{t("admin.scoringCriteria")}</Label>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-[11px] px-2.5 py-1 rounded-md bg-[#384872] text-white hover:bg-[#384872]/90 transition-colors flex items-center gap-1"
        >
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
          {criteria.length > 0 ? t("admin.editScoring") : t("admin.addScoring")}
        </button>
      </div>
      {criteria.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {criteria.map((c, i) => (
            <span key={i} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] bg-slate-100 border border-slate-200 text-slate-600">
              {c.labelFr || c.label || c.key}
              <span className="text-slate-400">({c.type === "boolean" ? "bool" : c.max || 10})</span>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-slate-400 italic">{t("admin.noScoringCriteria")}</p>
      )}
    </div>
  );

  if (!open) return summary;

  // Modale d'édition redimensionnable
  return (
    <>
      {summary}
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40" role="dialog" aria-modal="true" onClick={() => setOpen(false)}>
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-xl shadow-2xl flex flex-col"
          style={{ width: "min(680px, 90vw)", maxHeight: "80vh", resize: "both", overflow: "hidden", minWidth: 400, minHeight: 300 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b bg-slate-50 rounded-t-xl flex-shrink-0">
            <h3 className="text-sm font-semibold text-slate-800">{t("admin.scoringCriteria")}</h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={addItem}
                className="text-xs px-3 py-1.5 rounded-md bg-[#384872] text-white hover:bg-[#384872]/90 transition-colors"
              >
                + {t("admin.addCriterion")}
              </button>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600 p-1">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 py-3">
            {criteria.length === 0 ? (
              <p className="text-sm text-slate-400 italic text-center py-8">{t("admin.noScoringCriteria")}</p>
            ) : (
              <div className="space-y-2">
                {/* Column headers */}
                <div className="grid grid-cols-[1fr_1.2fr_1.2fr_70px_50px_68px] gap-2 px-1 text-[10px] uppercase tracking-wide text-slate-400 font-medium">
                  <span>{t("admin.apiKey")}</span>
                  <span>{t("admin.labelFr")}</span>
                  <span>{t("admin.labelEn")}</span>
                  <span>{t("admin.criterionType")}</span>
                  <span>Max</span>
                  <span></span>
                </div>

                {criteria.map((c, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1.2fr_1.2fr_70px_50px_68px] gap-2 items-center">
                    <input
                      value={c.key}
                      onChange={(e) => updateItem(i, "key", e.target.value)}
                      placeholder="camelCaseKey"
                      className="px-2 py-1.5 border rounded text-xs font-mono bg-white focus:outline-none focus:ring-1 focus:ring-[#384872]/30 min-w-0"
                    />
                    <input
                      value={c.labelFr || ""}
                      onChange={(e) => updateItem(i, "labelFr", e.target.value)}
                      placeholder="Label français"
                      className="px-2 py-1.5 border rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#384872]/30 min-w-0"
                    />
                    <input
                      value={c.label}
                      onChange={(e) => updateItem(i, "label", e.target.value)}
                      placeholder="English label"
                      className="px-2 py-1.5 border rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#384872]/30 min-w-0"
                    />
                    <select
                      value={c.type}
                      onChange={(e) => updateItem(i, "type", e.target.value as "score" | "boolean")}
                      className="px-1 py-1.5 border rounded text-xs bg-white focus:outline-none cursor-pointer"
                    >
                      <option value="score">Score</option>
                      <option value="boolean">Bool</option>
                    </select>
                    {c.type === "score" ? (
                      <input
                        type="number"
                        value={c.max ?? 10}
                        onChange={(e) => updateItem(i, "max", Number(e.target.value) || 10)}
                        className="px-1.5 py-1.5 border rounded text-xs text-center bg-white focus:outline-none min-w-0"
                        min={1}
                      />
                    ) : (
                      <span></span>
                    )}
                    <div className="flex items-center justify-end gap-0.5">
                      <button type="button" onClick={() => moveItem(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-slate-600 disabled:opacity-20 p-0.5">
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
                      </button>
                      <button type="button" onClick={() => moveItem(i, 1)} disabled={i === criteria.length - 1} className="text-slate-400 hover:text-slate-600 disabled:opacity-20 p-0.5">
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      <button type="button" onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600 p-0.5">
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end px-5 py-3 border-t bg-slate-50 rounded-b-xl flex-shrink-0">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs px-4 py-1.5 rounded-md bg-[#384872] text-white hover:bg-[#384872]/90 transition-colors"
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── FormSchema Visual Editor ──

interface FormFieldItem {
  key: string;
  type: "text" | "textarea" | "select" | "checkbox" | "date";
  labelFr: string;
  labelEn: string;
  label?: string;
  required?: boolean;
  conditionalOn?: string;
  placeholder?: string;
  options?: { value: string; label?: string; labelFr?: string; labelEn?: string }[];
}

function FormSchemaEditor({ value, onChange }: { value: unknown; onChange: (val: FormFieldItem[]) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const parseFields = (): FormFieldItem[] => {
    if (Array.isArray(value)) return value as FormFieldItem[];
    if (typeof value === "string") { try { return JSON.parse(value); } catch { return []; } }
    return [];
  };

  const [fields, setFields] = React.useState<FormFieldItem[]>(parseFields);
  React.useEffect(() => { setFields(parseFields()); }, [value]);

  const update = (next: FormFieldItem[]) => { setFields(next); onChange(next); };
  const updateField = (i: number, key: string, val: any) => { const next = [...fields]; next[i] = { ...next[i]!, [key]: val }; update(next); };
  const addField = () => { update([...fields, { key: "", type: "text", labelFr: "", labelEn: "", required: false }]); };
  const removeField = (i: number) => { update(fields.filter((_, idx) => idx !== i)); };
  const moveField = (i: number, dir: -1 | 1) => {
    const t = i + dir;
    if (t < 0 || t >= fields.length) return;
    const next = [...fields];
    [next[i], next[t]] = [next[t], next[i]];
    update(next);
  };

  // Options management for select fields
  const addOption = (fieldIndex: number) => {
    const next = [...fields];
    const opts = [...(next[fieldIndex]!.options || []), { value: "", labelFr: "", labelEn: "" }];
    next[fieldIndex] = { ...next[fieldIndex]!, options: opts };
    update(next);
  };
  const updateOption = (fieldIndex: number, optIndex: number, key: string, val: string) => {
    const next = [...fields];
    const opts = [...(next[fieldIndex]!.options || [])];
    opts[optIndex] = { ...opts[optIndex]!, [key]: val };
    // Auto-sync: if labelFr is set but not label, set label too
    if (key === "labelFr" && !opts[optIndex]!.label) opts[optIndex]!.label = val;
    next[fieldIndex] = { ...next[fieldIndex]!, options: opts };
    update(next);
  };
  const removeOption = (fieldIndex: number, optIndex: number) => {
    const next = [...fields];
    const opts = (next[fieldIndex]!.options || []).filter((_, i) => i !== optIndex);
    next[fieldIndex] = { ...next[fieldIndex]!, options: opts.length ? opts : undefined };
    update(next);
  };

  const summary = (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-slate-500">Formulaire (formSchema)</Label>
        <button type="button" onClick={() => setOpen(true)}
          className="text-[11px] px-2.5 py-1 rounded-md bg-[#384872] text-white hover:bg-[#384872]/90 transition-colors flex items-center gap-1">
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
          {fields.length > 0 ? "Modifier" : "Configurer"}
        </button>
      </div>
      {fields.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {fields.map((f, i) => (
            <span key={i} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] bg-slate-100 border border-slate-200 text-slate-600">
              {f.labelFr || f.label || f.key}
              <span className="text-slate-400">({f.type}{f.required ? "*" : ""})</span>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-slate-400 italic">Aucun champ configuré</p>
      )}
    </div>
  );

  if (!open) return summary;

  return (
    <>
      {summary}
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40" role="dialog" aria-modal="true" onClick={() => setOpen(false)}>
        <div onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-xl shadow-2xl flex flex-col"
          style={{ width: "min(800px, 95vw)", maxHeight: "85vh", resize: "both", overflow: "hidden", minWidth: 500, minHeight: 400 }}>

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b bg-slate-50 rounded-t-xl flex-shrink-0">
            <h3 className="text-sm font-semibold text-slate-800">Éditeur de formulaire</h3>
            <div className="flex items-center gap-2">
              <button type="button" onClick={addField}
                className="text-xs px-3 py-1.5 rounded-md bg-[#384872] text-white hover:bg-[#384872]/90 transition-colors">
                + Ajouter un champ
              </button>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600 p-1">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 py-3">
            {fields.length === 0 ? (
              <p className="text-sm text-slate-400 italic text-center py-8">Aucun champ. Cliquez sur "Ajouter un champ" pour commencer.</p>
            ) : (
              <div className="space-y-4">
                {fields.map((f, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-2 bg-white">
                    {/* Row 1: key, type, required, actions */}
                    <div className="flex items-center gap-2">
                      <input value={f.key} onChange={(e) => updateField(i, "key", e.target.value)}
                        placeholder="clé (camelCase)" className="px-2 py-1 border rounded text-xs font-mono bg-white focus:outline-none focus:ring-1 focus:ring-[#384872]/30 w-36" />
                      <select value={f.type} onChange={(e) => updateField(i, "type", e.target.value)}
                        className="px-2 py-1 border rounded text-xs bg-white focus:outline-none cursor-pointer">
                        <option value="text">Texte</option>
                        <option value="textarea">Zone de texte</option>
                        <option value="select">Liste déroulante</option>
                        <option value="checkbox">Case à cocher</option>
                        <option value="date">Date</option>
                      </select>
                      <label className="flex items-center gap-1 text-xs text-slate-600 cursor-pointer">
                        <input type="checkbox" checked={!!f.required} onChange={(e) => updateField(i, "required", e.target.checked)} className="rounded" />
                        Requis
                      </label>
                      <div className="flex-1" />
                      <button type="button" onClick={() => moveField(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-slate-600 disabled:opacity-20 p-0.5">
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
                      </button>
                      <button type="button" onClick={() => moveField(i, 1)} disabled={i === fields.length - 1} className="text-slate-400 hover:text-slate-600 disabled:opacity-20 p-0.5">
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      <button type="button" onClick={() => removeField(i)} className="text-red-400 hover:text-red-600 p-0.5">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {/* Row 2: labels */}
                    <div className="grid grid-cols-2 gap-2">
                      <input value={f.labelFr || ""} onChange={(e) => updateField(i, "labelFr", e.target.value)}
                        placeholder="Label français *" className="px-2 py-1 border rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#384872]/30" />
                      <input value={f.labelEn || ""} onChange={(e) => updateField(i, "labelEn", e.target.value)}
                        placeholder="Label anglais" className="px-2 py-1 border rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#384872]/30" />
                    </div>
                    {/* Row 3: placeholder + conditionalOn */}
                    <div className="grid grid-cols-2 gap-2">
                      <input value={f.placeholder || ""} onChange={(e) => updateField(i, "placeholder", e.target.value)}
                        placeholder="Placeholder (optionnel)" className="px-2 py-1 border rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#384872]/30" />
                      <input value={f.conditionalOn || ""} onChange={(e) => updateField(i, "conditionalOn", e.target.value)}
                        placeholder="Conditionnel sur (clé)" className="px-2 py-1 border rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#384872]/30" />
                    </div>
                    {/* Row 4: options (select only) */}
                    {f.type === "select" && (
                      <div className="pl-3 border-l-2 border-[#384872]/20 space-y-1.5 mt-1">
                        <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Options de la liste</p>
                        {(f.options || []).map((opt, oi) => (
                          <div key={oi} className="flex items-center gap-1.5">
                            <input value={opt.value} onChange={(e) => updateOption(i, oi, "value", e.target.value)}
                              placeholder="valeur" className="px-1.5 py-1 border rounded text-[11px] font-mono bg-white focus:outline-none w-28" />
                            <input value={opt.labelFr || opt.label || ""} onChange={(e) => updateOption(i, oi, "labelFr", e.target.value)}
                              placeholder="Label FR *" className="px-1.5 py-1 border rounded text-[11px] bg-white focus:outline-none flex-1" />
                            <input value={opt.labelEn || ""} onChange={(e) => updateOption(i, oi, "labelEn", e.target.value)}
                              placeholder="Label EN" className="px-1.5 py-1 border rounded text-[11px] bg-white focus:outline-none flex-1" />
                            <button type="button" onClick={() => removeOption(i, oi)} className="text-red-400 hover:text-red-600 p-0.5 shrink-0">
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                        <button type="button" onClick={() => addOption(i)}
                          className="text-[10px] text-[#384872] hover:text-[#384872]/70 font-medium">
                          + Ajouter une option
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end px-5 py-3 border-t bg-slate-50 rounded-b-xl flex-shrink-0">
            <button type="button" onClick={() => setOpen(false)}
              className="text-xs px-4 py-1.5 rounded-md bg-[#384872] text-white hover:bg-[#384872]/90 transition-colors">
              Fermer
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function SimulatorFormBody({
  formData,
  onChange,
  tables,
}: {
  formData: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  tables: Tables;
}) {
  const { t } = useTranslation();
  const str = (key: string) => {
    const v = formData[key];
    return v === null || v === undefined ? "" : String(v);
  };

  // Licences disponibles
  const allLicences = tables.licences?.data ?? [];
  // Licences déjà associées (via simulator_licences)
  const simulatorId = str("id");
  const allSimulatorLicences = (tables.simulator_licences?.data ?? []) as Record<string, unknown>[];
  const linkedLicenceIds = new Set(
    allSimulatorLicences
      .filter((sl) => String(sl.simulatorId) === simulatorId)
      .map((sl) => String(sl.licenceId))
  );

  // État local pour les licences sélectionnées
  const [selectedLicences, setSelectedLicences] = React.useState<Set<string>>(() => linkedLicenceIds);

  // Stocker les changements de licences dans formData pour que EditModal puisse les lire
  React.useEffect(() => {
    onChange("_selectedLicences", Array.from(selectedLicences));
    onChange("_originalLicences", Array.from(linkedLicenceIds));
  }, [selectedLicences]);

  const toggleLicence = (licenceId: string) => {
    setSelectedLicences((prev) => {
      const next = new Set(prev);
      if (next.has(licenceId)) {
        next.delete(licenceId);
      } else {
        next.add(licenceId);
      }
      return next;
    });
  };

  return (
    <>
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">{t("admin.name")}</Label>
        <Input
          value={str("name")}
          onChange={(e) => onChange("name", e.target.value)}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-slate-500">{t("admin.descriptionLabel")}</Label>
        <textarea
          value={str("description")}
          onChange={(e) => onChange("description", e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#384872]/30 focus:border-[#384872] resize-y"
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.simulatorType")}</Label>
          <Select
            value={str("simulatorType") || undefined}
            onValueChange={(value) => onChange("simulatorType", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("admin.select")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="training">Training</SelectItem>
              <SelectItem value="coach">Coach</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.category")}</Label>
          <Select
            value={str("category") || undefined}
            onValueChange={(value) => onChange("category", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("admin.select")} />
            </SelectTrigger>
            <SelectContent>
              {(tables.categories?.data ?? []).map((cat: any) => (
                <SelectItem key={cat.name} value={cat.name}>
                  <span className="flex items-center gap-2">
                    {cat.color && <span className="h-2.5 w-2.5 rounded-full inline-block" style={{ backgroundColor: cat.color }} />}
                    {cat.labelFr || cat.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.status")}</Label>
          <button
            type="button"
            onClick={() => onChange("isActive", formData.isActive === false ? true : formData.isActive === true ? false : false)}
            className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
              formData.isActive !== false
                ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                : "bg-slate-50 border-slate-200 text-slate-400"
            }`}
          >
            <span className={`h-2.5 w-2.5 rounded-full ${formData.isActive !== false ? "bg-emerald-500" : "bg-slate-300"}`} />
            {formData.isActive !== false ? t("admin.activeStatus") : t("admin.inactiveStatus")}
          </button>
        </div>
      </div>

      {/* Types de cas supportés (caseTypes) */}
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Types de cas supportés</Label>
        {(() => {
          const current = Array.isArray(formData.caseTypes) ? (formData.caseTypes as string[]) : ["B2B"];
          const toggle = (type: "B2B" | "B2C") => {
            const has = current.includes(type);
            let next = has ? current.filter((t) => t !== type) : [...current, type];
            if (next.length === 0) next = [type]; // au moins 1 type sélectionné
            onChange("caseTypes", next);
          };
          return (
            <div className="flex gap-2">
              {(["B2B", "B2C"] as const).map((type) => {
                const active = current.includes(type);
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => toggle(type)}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                      active ? "bg-[#384872] text-white border-[#384872]" : "bg-white text-slate-500 border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    {active && <Check className="h-3.5 w-3.5" />}
                    {type}
                  </button>
                );
              })}
            </div>
          );
        })()}
        <p className="text-xs text-muted-foreground">Détermine quels types (B2B / B2C) ce simulateur peut gérer dans le wizard de création d'un cas de simulation.</p>
      </div>


      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Ordre d'affichage</Label>
        <Input
          type="number"
          value={str("sortOrder")}
          onChange={(e) => onChange("sortOrder", parseInt(e.target.value) || 0)}
          className="w-32"
        />
        <p className="text-xs text-slate-400">Indépendant par type (training / coach). Plus petit = affiché en premier.</p>
      </div>

      <ImageField
        label={t("admin.icon")}
        value={str("icon")}
        onChange={(val) => onChange("icon", val)}
      />
      <ImageField
        label={t("admin.image")}
        value={str("image")}
        onChange={(val) => onChange("image", val)}
      />

      {/* Color */}
      <div className="space-y-1.5">
        <Label className="text-xs text-slate-500">{t("admin.color")}</Label>
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            {["#F49390", "#A6D3F9", "#F6C847", "#7CBF94", "#988EF2", "#2563EB", "#384872", "#F59E0B"].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => onChange("color", c)}
                className={`h-7 w-7 rounded-full border-2 transition-transform ${
                  str("color") === c ? "border-slate-800 scale-110" : "border-transparent hover:scale-105"
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <input
            type="color"
            value={str("color") || "#384872"}
            onChange={(e) => onChange("color", e.target.value)}
            className="h-7 w-7 rounded border cursor-pointer"
          />
          {str("color") && (
            <span className="text-xs text-slate-400 font-mono">{str("color")}</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.openaiModel")}</Label>
          <Input
            value={str("modeleOpenai")}
            onChange={(e) => onChange("modeleOpenai", e.target.value)}
            placeholder="gpt-4o, gpt-4o-mini..."
          />
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.urlGpt")}</Label>
          <Input
            type="url"
            value={str("urlGpt")}
            onChange={(e) => onChange("urlGpt", e.target.value)}
            placeholder="https://..."
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-slate-500">{t("admin.systemPrompt")}</Label>
        <textarea
          value={str("systemPrompt")}
          onChange={(e) => onChange("systemPrompt", e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#384872]/30 focus:border-[#384872] resize-y"
        />
      </div>

      {/* Scoring Criteria Editor */}
      <ScoringCriteriaEditor
        value={formData.scoringCriteria}
        onChange={(val) => onChange("scoringCriteria", val)}
      />

      <RelationSearchSelect
        label={t("admin.requiredLicences")}
        placeholder={t("admin.searchLicence")}
        allItems={allLicences}
        selectedIds={selectedLicences}
        onToggle={toggleLicence}
      />
    </>
  );
}

function CategoryFormBody({
  formData,
  onChange,
}: {
  formData: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  const { t } = useTranslation();
  const str = (key: string) => {
    const v = formData[key];
    return v === null || v === undefined ? "" : String(v);
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.name")} (code)</Label>
          <Input
            value={str("name")}
            onChange={(e) => onChange("name", e.target.value)}
            placeholder="ex: sales, customer_care"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Ordre d'affichage</Label>
          <Input
            type="number"
            value={str("sortOrder")}
            onChange={(e) => onChange("sortOrder", parseInt(e.target.value) || 0)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Label FR</Label>
          <Input
            value={str("labelFr")}
            onChange={(e) => onChange("labelFr", e.target.value)}
            placeholder="Sales"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Label EN</Label>
          <Input
            value={str("labelEn")}
            onChange={(e) => onChange("labelEn", e.target.value)}
            placeholder="Sales"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-500">{t("admin.color")} (fond)</Label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={str("color") || "#f6c847"}
              onChange={(e) => onChange("color", e.target.value)}
              className="h-8 w-8 rounded border cursor-pointer"
            />
            <Input
              value={str("color")}
              onChange={(e) => onChange("color", e.target.value)}
              placeholder="#f6c847"
              className="flex-1"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-500">{t("admin.color")} (texte)</Label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={str("textColor") || "#5c4813"}
              onChange={(e) => onChange("textColor", e.target.value)}
              className="h-8 w-8 rounded border cursor-pointer"
            />
            <Input
              value={str("textColor")}
              onChange={(e) => onChange("textColor", e.target.value)}
              placeholder="#5c4813"
              className="flex-1"
            />
          </div>
        </div>
      </div>

      {/* Preview */}
      {str("labelFr") && (
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Aperçu</Label>
          <div className="flex items-center gap-2">
            <span
              className="text-xs font-medium px-3 py-1 rounded-full"
              style={{
                backgroundColor: str("color") || "#e5e7eb",
                color: str("textColor") || "#374151",
              }}
            >
              {str("labelFr")}
            </span>
          </div>
        </div>
      )}
    </>
  );
}

function RelationSearchSelect({
  label,
  placeholder,
  allItems,
  selectedIds,
  onToggle,
  displayField = "name",
}: {
  label: string;
  placeholder: string;
  allItems: Record<string, unknown>[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  displayField?: string;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = allItems.filter((item) =>
    String(item[displayField] ?? item.id).toLowerCase().includes(search.toLowerCase())
  );

  const selectedItems = allItems.filter((item) => selectedIds.has(String(item.id)));

  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-slate-500">{label}</Label>
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedItems.map((item) => (
            <span
              key={String(item.id)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[#384872] text-white"
            >
              {String(item[displayField] ?? item.id)}
              <button
                type="button"
                onClick={() => onToggle(String(item.id))}
                className="hover:bg-white/20 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div ref={containerRef} className="relative">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setOpen(true)}
            className="w-full pl-8 pr-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#384872]/30 focus:border-[#384872]"
          />
        </div>
        {open && (
          <div className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg max-h-[180px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-400">{t("admin.noResults")}</div>
            ) : (
              filtered.map((item) => {
                const itemId = String(item.id);
                const isSelected = selectedIds.has(itemId);
                return (
                  <button
                    key={itemId}
                    type="button"
                    onClick={() => onToggle(itemId)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${
                      isSelected ? "text-[#384872] font-medium" : "text-slate-700"
                    }`}
                  >
                    <span>{String(item[displayField] ?? item.id)}</span>
                    {isSelected && <Check className="h-4 w-4 text-[#384872]" />}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FKSingleSelect({
  label,
  placeholder,
  allItems,
  selectedId,
  onSelect,
  displayFn = (item) => String(item.name ?? item.email ?? item.id),
}: {
  label: string;
  placeholder: string;
  allItems: Record<string, unknown>[];
  selectedId: string;
  onSelect: (id: string) => void;
  displayFn?: (item: Record<string, unknown>) => string;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = allItems.filter((item) =>
    displayFn(item).toLowerCase().includes(search.toLowerCase())
  );

  const selectedItem = allItems.find((item) => String(item.id) === selectedId);

  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-slate-500">{label}</Label>
      {selectedItem && (
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[#384872] text-white">
            {displayFn(selectedItem)}
            <button
              type="button"
              onClick={() => onSelect("")}
              className="hover:bg-white/20 rounded-full p-0.5"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        </div>
      )}
      <div ref={containerRef} className="relative">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setOpen(true)}
            className="w-full pl-8 pr-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#384872]/30 focus:border-[#384872]"
          />
        </div>
        {open && (
          <div className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg max-h-[180px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-400">{t("admin.noResults")}</div>
            ) : (
              filtered.map((item) => {
                const itemId = String(item.id);
                const isSelected = itemId === selectedId;
                return (
                  <button
                    key={itemId}
                    type="button"
                    onClick={() => {
                      onSelect(itemId);
                      setOpen(false);
                      setSearch("");
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${
                      isSelected ? "text-[#384872] font-medium" : "text-slate-700"
                    }`}
                  >
                    <span>{displayFn(item)}</span>
                    {isSelected && <Check className="h-4 w-4 text-[#384872]" />}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const PRESET_COLORS = [
  "#384872", "#5B7ECF", "#3B82F6", "#06B6D4", "#10B981",
  "#F59E0B", "#EF4444", "#EC4899", "#8B5CF6", "#6B7280",
];

/**
 * Lien d'achat d'un produit à remettre à un nouveau client (hors landing page), affiché en
 * consultation ET en modification de la fiche — règle `buyLinkProblem`, la même que celle
 * du serveur (/buy/:slug).
 */
function ProductBuyLink({ product }: { product: Record<string, unknown> }) {
  const { t } = useTranslation();
  const [copied, setCopied] = React.useState(false);
  const text = (key: string) => (product[key] === null || product[key] === undefined ? "" : String(product[key]));
  const problem = buyLinkProblem({
    code: text("code"),
    isActive: product.isActive === undefined || product.isActive === null ? true : Boolean(product.isActive),
    isTrial: Boolean(product.isTrial),
    tier: text("tier"),
    zohoPlanCode: text("zohoPlanCode"),
  });
  const buyUrl = problem
    ? ""
    : `${typeof window !== "undefined" ? window.location.origin : ""}/buy/${licenceCodeToSlug(text("code"))}`;

  return (
    <div className="space-y-1" data-testid="product-buy-link">
      <Label className="text-xs text-slate-500">{t("admin.buyLink.label")}</Label>
      {buyUrl ? (
        <div className="flex items-center gap-2">
          <Input
            readOnly
            value={buyUrl}
            className="font-mono text-xs"
            onFocus={(e) => e.currentTarget.select()}
          />
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard?.writeText(buyUrl).then(() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
              });
            }}
            className="shrink-0 rounded-md border px-2 py-1.5 text-xs hover:bg-slate-50"
            title={t("admin.buyLink.copy")}
          >
            {copied ? t("admin.buyLink.copied") : t("admin.buyLink.copy")}
          </button>
        </div>
      ) : (
        <p className="text-xs text-[#926B00]">{t(`admin.buyLink.problem.${problem}`)}</p>
      )}
      {buyUrl && (
        <p className="text-[11px] text-slate-400">
          {t(product.isOneShot ? "admin.buyLink.hintPack" : "admin.buyLink.hintSubscription")}
        </p>
      )}
    </div>
  );
}

function LicenceFormBody({
  formData,
  onChange,
  tables,
}: {
  formData: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  tables: Tables;
}) {
  const { t } = useTranslation();
  const str = (key: string) => {
    const v = formData[key];
    return v === null || v === undefined ? "" : String(v);
  };

  // Simulators liés via simulator_licences (relation inverse)
  const licenceId = str("id");
  const allSimulators = tables.simulators?.data ?? [];
  const allSimulatorLicences = (tables.simulator_licences?.data ?? []) as Record<string, unknown>[];
  const linkedSimulatorIds = new Set(
    allSimulatorLicences
      .filter((sl) => String(sl.licenceId) === licenceId)
      .map((sl) => String(sl.simulatorId))
  );

  const [selectedSimulators, setSelectedSimulators] = React.useState<Set<string>>(() => linkedSimulatorIds);

  React.useEffect(() => {
    onChange("_selectedSimulators", Array.from(selectedSimulators));
    onChange("_originalSimulators", Array.from(linkedSimulatorIds));
  }, [selectedSimulators]);

  const toggleSimulator = (simId: string) => {
    setSelectedSimulators((prev) => {
      const next = new Set(prev);
      if (next.has(simId)) next.delete(simId);
      else next.add(simId);
      return next;
    });
  };

  // Zoho plans
  const [zohoPlans, setZohoPlans] = React.useState<Array<{ planCode: string; name: string; productName: string; recurringPrice: number; interval: number; intervalUnit: string }>>([]);
  const [zohoLoading, setZohoLoading] = React.useState(false);
  const [zohoError, setZohoError] = React.useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = React.useState<string>("");

  React.useEffect(() => {
    setZohoLoading(true);
    adminApi.getZohoPlans()
      .then((plans) => {
        setZohoPlans(plans);
        setZohoError(null);
        // Auto-select product if a plan is already linked
        const currentPlanCode = str("zohoPlanCode");
        if (currentPlanCode) {
          const linked = plans.find((p) => p.planCode === currentPlanCode);
          if (linked) setSelectedProduct(linked.productName);
        }
      })
      .catch((err) => setZohoError(err.message))
      .finally(() => setZohoLoading(false));
  }, []);

  const zohoProducts = React.useMemo(() => {
    const productSet = new Map<string, number>();
    for (const p of zohoPlans) {
      productSet.set(p.productName, (productSet.get(p.productName) || 0) + 1);
    }
    return Array.from(productSet.entries()).map(([name, count]) => ({ name, count }));
  }, [zohoPlans]);

  const filteredPlans = React.useMemo(() => {
    if (!selectedProduct) return [];
    return zohoPlans.filter((p) => p.productName === selectedProduct);
  }, [zohoPlans, selectedProduct]);

  const isActiveValue = formData.isActive === undefined || formData.isActive === null ? true : Boolean(formData.isActive);
  const isTrialValue = Boolean(formData.isTrial);
  const isOneShotValue = Boolean(formData.isOneShot);

  return (
    <>
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Code (r\u00e9f\u00e9rence interne, unique) *</Label>
        <Input
          value={str("code")}
          onChange={(e) => onChange("code", e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))}
          placeholder="ex: PROSPECTOR_V1"
          className="font-mono"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-slate-500">{t("admin.nameRequired")} (visible client)</Label>
        <Input
          value={str("name")}
          onChange={(e) => onChange("name", e.target.value)}
          placeholder={t("admin.licenceName")}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-slate-500">{t("admin.descriptionLabel")}</Label>
        <textarea
          value={str("description")}
          onChange={(e) => onChange("description", e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#384872]/30 focus:border-[#384872] resize-y"
        />
      </div>

      <div className="flex items-center gap-3">
        <Label className="text-xs text-slate-500">Statut</Label>
        <button
          type="button"
          onClick={() => onChange("isActive", !isActiveValue)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            isActiveValue ? "bg-green-500" : "bg-slate-300"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              isActiveValue ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
        <span className={`text-sm ${isActiveValue ? "text-green-600 font-medium" : "text-slate-400"}`}>
          {isActiveValue ? "Active" : "D\u00e9sactiv\u00e9e"}
        </span>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs text-slate-500">{t("admin.color")}</Label>
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            {PRESET_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => onChange("color", c)}
                className={`h-7 w-7 rounded-full border-2 transition-transform ${
                  str("color") === c ? "border-slate-800 scale-110" : "border-transparent hover:scale-105"
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <input
            type="color"
            value={str("color") || "#384872"}
            onChange={(e) => onChange("color", e.target.value)}
            className="h-7 w-7 rounded border cursor-pointer"
            title={t("admin.customColor")}
          />
          {str("color") && (
            <span className="text-xs text-slate-400 font-mono">{str("color")}</span>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-slate-500">Plan Zoho Billing</Label>
        {zohoLoading ? (
          <p className="text-xs text-slate-400">Chargement des plans Zoho...</p>
        ) : zohoError ? (
          <p className="text-xs text-red-500">Zoho: {zohoError}</p>
        ) : (
          <div className="space-y-2">
            <Select
              value={selectedProduct || undefined}
              onValueChange={(value) => {
                setSelectedProduct(value === "__none__" ? "" : value);
                onChange("zohoPlanCode", null);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="1. Choisir un produit Zoho" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">
                  <span className="text-slate-400">Aucun produit</span>
                </SelectItem>
                {zohoProducts.map((prod) => (
                  <SelectItem key={prod.name} value={prod.name}>
                    {prod.name} ({prod.count} plan{prod.count > 1 ? "s" : ""})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedProduct && (
              <Select
                value={str("zohoPlanCode") || undefined}
                onValueChange={(value) => onChange("zohoPlanCode", value === "__none__" ? null : value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="2. Choisir un plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">
                    <span className="text-slate-400">Aucun plan</span>
                  </SelectItem>
                  {filteredPlans.map((plan) => (
                    <SelectItem key={plan.planCode} value={plan.planCode}>
                      {plan.name} — {plan.recurringPrice}€/{plan.interval === 1 ? "" : plan.interval}{plan.intervalUnit === "months" ? "mois" : plan.intervalUnit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {str("zohoPlanCode") && (
              <p className="text-xs text-slate-400 font-mono">Code: {str("zohoPlanCode")}</p>
            )}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-slate-500">{t("admin.limitType") || "Type de limite"}</Label>
        <Select
          value={str("limitType") || "permanent"}
          onValueChange={(value) => onChange("limitType", value)}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="permanent">{t("admin.permanent")}</SelectItem>
            <SelectItem value="date">{t("admin.limitedByDate")}</SelectItem>
            <SelectItem value="simulations">{t("admin.limitedBySimulations")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Tier (abonnement)</Label>
          <Select value={str("tier") || undefined} onValueChange={(value) => onChange("tier", value)}>
            <SelectTrigger className="w-full"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="starter">Starter</SelectItem>
              <SelectItem value="focus">Focus</SelectItem>
              <SelectItem value="premium">Premium</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Nb de simulateurs</Label>
          <Input
            type="number"
            min={0}
            value={str("maxSimulators")}
            onChange={(e) => onChange("maxSimulators", e.target.value === "" ? null : parseInt(e.target.value, 10))}
            placeholder="0 = tous"
          />
          <p className="text-[11px] text-slate-400">0 = tous les simulateurs actifs</p>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Prix HT (centimes / user / mois)</Label>
          <Input
            type="number"
            min={0}
            value={str("priceHt")}
            onChange={(e) => onChange("priceHt", e.target.value === "" ? null : parseInt(e.target.value, 10))}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Périodicité</Label>
          <Select value={str("billingInterval") || undefined} onValueChange={(value) => onChange("billingInterval", value)}>
            <SelectTrigger className="w-full"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Mensuelle</SelectItem>
              <SelectItem value="quarterly">Trimestrielle</SelectItem>
              <SelectItem value="yearly">Annuelle</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Label className="text-xs text-slate-500">Période d'essai</Label>
        <button
          type="button"
          onClick={() => onChange("isTrial", !isTrialValue)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            isTrialValue ? "bg-[#7CBF94]" : "bg-slate-300"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              isTrialValue ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
        <span className={`text-sm ${isTrialValue ? "text-[#1E7A3C] font-medium" : "text-slate-400"}`}>
          {isTrialValue ? "Licence d'offre d'essai" : "Licence standard"}
        </span>
      </div>

      {/* Produit "one-shot" : pack de crédits à achat unique (pas d'abonnement récurrent). */}
      <div className="flex items-center gap-3">
        <Label className="text-xs text-slate-500">Pack one-shot</Label>
        <button
          type="button"
          onClick={() => onChange("isOneShot", !isOneShotValue)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            isOneShotValue ? "bg-[#988EF2]" : "bg-slate-300"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              isOneShotValue ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
        <span className={`text-sm ${isOneShotValue ? "text-[#5A4BBE] font-medium" : "text-slate-400"}`}>
          {isOneShotValue ? "Pack (crédits, achat unique)" : "Abonnement récurrent"}
        </span>
      </div>

      {isOneShotValue && (
        <div className="space-y-3 rounded-lg border border-[#988EF2]/40 bg-[#988EF2]/5 p-3">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Nombre de crédits du pack</Label>
            <Input
              type="number"
              min={1}
              value={str("sessionsPerPack")}
              onChange={(e) => onChange("sessionsPerPack", e.target.value === "" ? null : parseInt(e.target.value, 10))}
              placeholder="50 par défaut"
            />
            <p className="text-[11px] text-slate-400">
              1 crédit = 1 session complète OU 1 exercice « S'exercer ». Vide = 50 par défaut.
            </p>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Quantité maximale par commande</Label>
            <Input
              type="number"
              min={1}
              value={str("maxQuantity")}
              onChange={(e) => onChange("maxQuantity", e.target.value === "" ? null : parseInt(e.target.value, 10))}
              placeholder="Vide = illimité"
            />
            <p className="text-[11px] text-slate-400">
              À <strong>1</strong>, le sélecteur de quantité disparaît du formulaire de création de
              compte et la quantité est écrêtée côté serveur — pour un pack d'essai offert, dont on
              ne veut qu'un exemplaire par prospect.
            </p>
          </div>

        </div>
      )}

      <ProductBuyLink product={formData} />

      <RelationSearchSelect
        label={t("admin.linkedSimulators")}
        placeholder={t("admin.searchSimulator")}
        allItems={allSimulators}
        selectedIds={selectedSimulators}
        onToggle={toggleSimulator}
      />
    </>
  );
}

function ScenarioRequestFormBody({
  formData,
  onChange,
  tables,
}: {
  formData: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  tables: Tables;
}) {
  const { t } = useTranslation();
  const str = (key: string) => {
    const v = formData[key];
    return v === null || v === undefined ? "" : String(v);
  };

  const allUsers = (tables.users?.data ?? []) as Record<string, unknown>[];

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.difficulty")}</Label>
          <Input
            value={str("difficulty")}
            onChange={(e) => onChange("difficulty", e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.industry")}</Label>
          <Input
            value={str("industry")}
            onChange={(e) => onChange("industry", e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.companySize")}</Label>
          <Input
            value={str("companySize")}
            onChange={(e) => onChange("companySize", e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.prospectType")}</Label>
          <Input
            value={str("prospectType")}
            onChange={(e) => onChange("prospectType", e.target.value)}
          />
        </div>
      </div>

      <FKSingleSelect
        label={t("admin.creator")}
        placeholder={t("admin.searchUser")}
        allItems={allUsers}
        selectedId={str("creatorId")}
        onSelect={(id) => onChange("creatorId", id || null)}
        displayFn={(u) => `${u.firstName || ""} ${u.lastName || ""}`.trim() || String(u.email || u.id)}
      />
    </>
  );
}

function ScenarioFormBody({
  formData,
  onChange,
  tables,
}: {
  formData: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  tables: Tables;
}) {
  const { t } = useTranslation();
  const str = (key: string) => {
    const v = formData[key];
    return v === null || v === undefined ? "" : String(v);
  };

  const allUsers = (tables.users?.data ?? []) as Record<string, unknown>[];
  const allScenarioRequests = (tables.scenario_requests?.data ?? []) as Record<string, unknown>[];

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Company Name *</Label>
          <Input
            value={str("companyName")}
            onChange={(e) => onChange("companyName", e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Prospect Name *</Label>
          <Input
            value={str("prospectName")}
            onChange={(e) => onChange("prospectName", e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Prospect Gender</Label>
          <Input
            value={str("prospectGender")}
            onChange={(e) => onChange("prospectGender", e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Model</Label>
          <Input
            value={str("model")}
            onChange={(e) => onChange("model", e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Prompt Version</Label>
          <Input
            value={str("promptVersion")}
            onChange={(e) => onChange("promptVersion", e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Total Tokens</Label>
          <Input
            type="number"
            value={str("totalTokens")}
            onChange={(e) => onChange("totalTokens", e.target.value ? parseInt(e.target.value) : null)}
          />
        </div>
      </div>

      <FKSingleSelect
        label="User"
        placeholder={t("admin.searchUser")}
        allItems={allUsers}
        selectedId={str("userId")}
        onSelect={(id) => onChange("userId", id || null)}
        displayFn={(u) => `${u.firstName || ""} ${u.lastName || ""}`.trim() || String(u.email || u.id)}
      />

      <FKSingleSelect
        label="Scenario Request"
        placeholder="Rechercher un scenario request..."
        allItems={allScenarioRequests}
        selectedId={str("scenarioRequestId")}
        onSelect={(id) => onChange("scenarioRequestId", id || null)}
        displayFn={(sr) => String(sr.id)}
      />

      <div className="space-y-1">
        <Label className="text-xs text-slate-500">User Message (markdown)</Label>
        <textarea
          value={str("userMessage")}
          onChange={(e) => onChange("userMessage", e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#384872]/30 focus:border-[#384872] resize-y"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Raw</Label>
        <textarea
          value={str("raw")}
          onChange={(e) => onChange("raw", e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#384872]/30 focus:border-[#384872] resize-y"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Variables</Label>
        <textarea
          value={str("variables")}
          onChange={(e) => onChange("variables", e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#384872]/30 focus:border-[#384872] resize-y"
        />
      </div>
    </>
  );
}


function FeedbackFormBody({
  formData,
  onChange,
  tables,
}: {
  formData: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  tables: Tables;
}) {
  const { t } = useTranslation();
  const str = (key: string) => {
    const v = formData[key];
    return v === null || v === undefined ? "" : String(v);
  };

  const allUsers = (tables.users?.data ?? []) as Record<string, unknown>[];

  const metaFields = [
    { key: "totalScore", label: "Total Score" },
    { key: "totalTokens", label: "Total Tokens" },
  ];

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        {metaFields.map(({ key, label }) => (
          <div key={key} className="space-y-1">
            <Label className="text-xs text-slate-500">{label}</Label>
            <Input
              type="number"
              value={str(key)}
              onChange={(e) => onChange(key, e.target.value ? parseInt(e.target.value) : null)}
            />
          </div>
        ))}
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Scores (JSON)</Label>
        <textarea
          className="w-full font-mono text-xs border rounded p-2 min-h-[80px]"
          value={typeof formData.scores === "object" ? JSON.stringify(formData.scores, null, 2) : str("scores")}
          onChange={(e) => {
            try { onChange("scores", JSON.parse(e.target.value)); } catch { onChange("scores", e.target.value); }
          }}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Summary</Label>
        <Input
          value={str("summary")}
          onChange={(e) => onChange("summary", e.target.value)}
        />
      </div>

      <FKSingleSelect
        label="Created By"
        placeholder={t("admin.searchUser")}
        allItems={allUsers}
        selectedId={str("createdBy")}
        onSelect={(id) => onChange("createdBy", id || null)}
        displayFn={(u) => `${u.firstName || ""} ${u.lastName || ""}`.trim() || String(u.email || u.id)}
      />

      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Content (markdown)</Label>
        <textarea
          value={str("content")}
          onChange={(e) => onChange("content", e.target.value)}
          rows={5}
          className="w-full px-3 py-2 border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#384872]/30 focus:border-[#384872] resize-y"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Raw</Label>
        <textarea
          value={str("raw")}
          onChange={(e) => onChange("raw", e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#384872]/30 focus:border-[#384872] resize-y"
        />
      </div>
    </>
  );
}


const TARGET_COMPANY_TYPE_OPTIONS = [
  "Micro-entreprises",
  "TPE/TPI",
  "PME/PMI",
  "ETI",
  "Grandes entreprises",
  "Secteur public",
];

const TARGET_INDUSTRY_OPTIONS = [
  "Administration publique",
  "Agriculture",
  "Agroalimentaire",
  "Armée/Sécurité",
  "Audit/Gestion",
  "Automotive",
  "Banque/Assurance",
  "BTP/Architecture",
  "Chimie",
  "Commerce de gros",
  "Communication/Marketing/Pub",
  "Construction (aéronautique/ferroviaire/navale)",
  "Culture/Arts/Design",
  "Droit/Justice",
  "Edition/Journalisme",
  "Education/Formation",
  "Energie",
  "Environnement",
  "Hôtellerie/Restauration",
  "Immobilier",
  "Industrie",
  "Informatique & Télécoms",
  "Logistique/Transport",
  "Maintenance/Entretien",
  "Mode/Industrie textile",
  "Santé/Pharmaceutique",
  "Services professionnels",
  "ESS/ONG",
  "Sport/Loisirs/Tourisme",
];

function TargetFormBody({
  formData,
  onChange,
  tables,
}: {
  formData: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  tables: Tables;
}) {
  const { t } = useTranslation();
  const str = (key: string) => {
    const v = formData[key];
    return v === null || v === undefined ? "" : String(v);
  };

  const allUsers = (tables.users?.data ?? []) as Record<string, unknown>[];
  const allClientAccounts = (tables.client_accounts?.data ?? []) as Record<string, unknown>[];

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.companySize")}</Label>
          <Select value={str("companySize")} onValueChange={(v) => onChange("companySize", v)}>
            <SelectTrigger><SelectValue placeholder={t("admin.selectType")} /></SelectTrigger>
            <SelectContent>
              {TARGET_COMPANY_TYPE_OPTIONS.map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{t("admin.industry")}</Label>
          <Select value={str("industry")} onValueChange={(v) => onChange("industry", v)}>
            <SelectTrigger><SelectValue placeholder={t("admin.selectIndustry")} /></SelectTrigger>
            <SelectContent>
              {TARGET_INDUSTRY_OPTIONS.map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-slate-500">{t("admin.prospectUrl")}</Label>
        <Input type="url" value={str("prospectUrl")} onChange={(e) => onChange("prospectUrl", e.target.value)} placeholder="https://www.exemple-prospect.com" />
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-slate-500">{t("admin.targetRole")}</Label>
        <Input value={str("targetRole")} onChange={(e) => onChange("targetRole", e.target.value)} placeholder="Ex: Directeur Commercial, DSI..." />
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-slate-500">{t("admin.decisionMakerProfile")}</Label>
        <textarea
          value={str("decisionMakerProfile")}
          onChange={(e) => onChange("decisionMakerProfile", e.target.value)}
          rows={3}
          placeholder={t("admin.decisionMakerPlaceholder")}
          className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#384872]/30 focus:border-[#384872] resize-y"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-slate-500">{t("admin.painPoints")}</Label>
        <textarea
          value={str("painPoints")}
          onChange={(e) => onChange("painPoints", e.target.value)}
          rows={3}
          placeholder={t("admin.painPointsPlaceholder")}
          className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#384872]/30 focus:border-[#384872] resize-y"
        />
      </div>

      <FKSingleSelect
        label={t("admin.accountLabel")}
        placeholder={t("admin.searchClientAccount")}
        allItems={allClientAccounts}
        selectedId={str("clientAccountId")}
        onSelect={(id) => onChange("clientAccountId", id || null)}
        displayFn={(c) => String(c.name || c.id)}
      />

      <FKSingleSelect
        label={t("admin.createdBy")}
        placeholder={t("admin.searchUser")}
        allItems={allUsers}
        selectedId={str("createdBy")}
        onSelect={(id) => onChange("createdBy", id || null)}
        displayFn={(u) => `${u.firstName || ""} ${u.lastName || ""}`.trim() || String(u.email || u.id)}
      />
    </>
  );
}

function SessionFormBody({
  formData,
  onChange,
  tables,
}: {
  formData: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  tables: Tables;
}) {
  const { t } = useTranslation();
  const str = (key: string) => {
    const v = formData[key];
    return v === null || v === undefined ? "" : String(v);
  };

  const allUsers = (tables.users?.data ?? []) as Record<string, unknown>[];
  const allSimulators = (tables.simulators?.data ?? []) as Record<string, unknown>[];
  const allFeedbacks = (tables.feedbacks?.data ?? []) as Record<string, unknown>[];
  const allAudioSessions = (tables.audio_sessions?.data ?? []) as Record<string, unknown>[];
  const allScenarioRequests = (tables.scenario_requests?.data ?? []) as Record<string, unknown>[];
  const allScenarios = (tables.scenarios?.data ?? []) as Record<string, unknown>[];
  const allChallenges = (tables.challenges?.data ?? []) as Record<string, unknown>[];
  const allPersonae = (tables.personae?.data ?? []) as Record<string, unknown>[];

  return (
    <>
      <FKSingleSelect
        label="Simulator"
        placeholder={t("admin.searchSimulator")}
        allItems={allSimulators}
        selectedId={str("simulatorId")}
        onSelect={(id) => onChange("simulatorId", id || null)}
        displayFn={(s) => String(s.name || s.id)}
      />

      <FKSingleSelect
        label="Challenge"
        placeholder="Rechercher un challenge..."
        allItems={allChallenges}
        selectedId={str("challengeId")}
        onSelect={(id) => onChange("challengeId", id || null)}
        displayFn={(c) => String(c.title || c.id)}
      />

      <FKSingleSelect
        label="Persona"
        placeholder="Rechercher une persona..."
        allItems={allPersonae}
        selectedId={str("personaId")}
        onSelect={(id) => onChange("personaId", id || null)}
        displayFn={(p) => `${p.function || ""} — ${p.category || ""}`.trim() || String(p.id)}
      />

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Duration (minutes)</Label>
          <Input type="number" value={str("durationInMinutes")} onChange={(e) => onChange("durationInMinutes", e.target.value ? Number(e.target.value) : null)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Number of Messages</Label>
          <Input type="number" value={str("numberOfMessages")} onChange={(e) => onChange("numberOfMessages", e.target.value ? Number(e.target.value) : null)} />
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-slate-500">End Time</Label>
        <Input type="datetime-local" value={str("endTime") ? str("endTime").slice(0, 16) : ""} onChange={(e) => onChange("endTime", e.target.value || null)} />
      </div>

      <FKSingleSelect
        label="Feedback"
        placeholder={t("admin.searchFeedback")}
        allItems={allFeedbacks}
        selectedId={str("feedbackId")}
        onSelect={(id) => onChange("feedbackId", id || null)}
        displayFn={(f) => String(f.id)}
      />

      <FKSingleSelect
        label="Feedback Audio Session"
        placeholder="Rechercher une audio session..."
        allItems={allAudioSessions}
        selectedId={str("feedbackAudioSessionId")}
        onSelect={(id) => onChange("feedbackAudioSessionId", id || null)}
        displayFn={(a) => String(a.id)}
      />

      <FKSingleSelect
        label="Scenario Request"
        placeholder={t("admin.searchScenarioRequest")}
        allItems={allScenarioRequests}
        selectedId={str("scenarioRequestId")}
        onSelect={(id) => onChange("scenarioRequestId", id || null)}
        displayFn={(r) => String(r.id)}
      />

      <FKSingleSelect
        label="Selected Scenario"
        placeholder={t("admin.searchScenario")}
        allItems={allScenarios}
        selectedId={str("selectedScenarioId")}
        onSelect={(id) => onChange("selectedScenarioId", id || null)}
        displayFn={(s) => String(s.id)}
      />

      <FKSingleSelect
        label="Simulation Audio Session"
        placeholder="Rechercher une audio session..."
        allItems={allAudioSessions}
        selectedId={str("simulationAudioSessionId")}
        onSelect={(id) => onChange("simulationAudioSessionId", id || null)}
        displayFn={(a) => String(a.id)}
      />

      <FKSingleSelect
        label="User"
        placeholder={t("admin.searchUser")}
        allItems={allUsers}
        selectedId={str("userId")}
        onSelect={(id) => onChange("userId", id || null)}
        displayFn={(u) => `${u.firstName || ""} ${u.lastName || ""}`.trim() || String(u.email || u.id)}
      />

      <FKSingleSelect
        label="Created By"
        placeholder={t("admin.searchUser")}
        allItems={allUsers}
        selectedId={str("createdBy")}
        onSelect={(id) => onChange("createdBy", id || null)}
        displayFn={(u) => `${u.firstName || ""} ${u.lastName || ""}`.trim() || String(u.email || u.id)}
      />
    </>
  );
}

const SCENARIO_CHAT_ROLE_OPTIONS = [
  { value: "assistant", label: "Assistant" },
  { value: "user", label: "User" },
  { value: "system", label: "System" },
];

function ScenarioChatMessageFormBody({
  formData,
  onChange,
  tables,
}: {
  formData: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  tables: Tables;
}) {
  const { t } = useTranslation();
  const str = (key: string) => {
    const v = formData[key];
    return v === null || v === undefined ? "" : String(v);
  };

  const allUsers = (tables.users?.data ?? []) as Record<string, unknown>[];
  const allScenarios = (tables.scenarios?.data ?? []) as Record<string, unknown>[];
  const allScenarioRequests = (tables.scenario_requests?.data ?? []) as Record<string, unknown>[];

  return (
    <>
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Role *</Label>
        <Select value={str("role")} onValueChange={(v) => onChange("role", v)}>
          <SelectTrigger><SelectValue placeholder={t("admin.selectPlaceholder")} /></SelectTrigger>
          <SelectContent>
            {SCENARIO_CHAT_ROLE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <FKSingleSelect
        label="Scenario Proposition"
        placeholder={t("admin.searchScenario")}
        allItems={allScenarios}
        selectedId={str("scenarioPropositionId")}
        onSelect={(id) => onChange("scenarioPropositionId", id || null)}
        displayFn={(s) => String(s.id)}
      />

      <FKSingleSelect
        label="Scenario Request"
        placeholder={t("admin.searchScenarioRequest")}
        allItems={allScenarioRequests}
        selectedId={str("scenarioRequestId")}
        onSelect={(id) => onChange("scenarioRequestId", id || null)}
        displayFn={(r) => String(r.id)}
      />

      <FKSingleSelect
        label="Created By"
        placeholder={t("admin.searchUser")}
        allItems={allUsers}
        selectedId={str("createdBy")}
        onSelect={(id) => onChange("createdBy", id || null)}
        displayFn={(u) => `${u.firstName || ""} ${u.lastName || ""}`.trim() || String(u.email || u.id)}
      />

      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Content</Label>
        <textarea
          value={str("content")}
          onChange={(e) => onChange("content", e.target.value)}
          rows={6}
          className="w-full px-3 py-2 border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#384872]/30 focus:border-[#384872] resize-y"
        />
      </div>
    </>
  );
}

function AiPromptFormBody({
  formData,
  onChange,
  isView,
}: {
  formData: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  isView: boolean;
}) {
  const str = (key: string) => {
    const v = formData[key];
    return v === null || v === undefined ? "" : String(v);
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Code (identifiant unique)</Label>
          <Input value={str("code")} onChange={(e) => onChange("code", e.target.value)} disabled={isView} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Nom</Label>
          <Input value={str("name")} onChange={(e) => onChange("name", e.target.value)} disabled={isView} />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Description</Label>
        <Input value={str("description")} onChange={(e) => onChange("description", e.target.value)} disabled={isView} placeholder="Description de l'agent..." />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Modèle</Label>
          <Input value={str("model")} onChange={(e) => onChange("model", e.target.value)} disabled={isView} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Température (0-10)</Label>
          <Input type="number" min="0" max="10" value={str("temperature")} onChange={(e) => onChange("temperature", parseInt(e.target.value) || 0)} disabled={isView} />
        </div>
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-slate-500">Prompt système</Label>
          <span className="text-xs text-slate-400">{str("systemPrompt").length} caractères</span>
        </div>
        <textarea
          className="w-full font-mono text-sm border rounded-lg p-4 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-200 transition-colors resize-y"
          style={{ minHeight: "500px" }}
          value={str("systemPrompt")}
          onChange={(e) => onChange("systemPrompt", e.target.value)}
          disabled={isView}
          placeholder="Entrez le prompt système de l'agent IA..."
        />
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-400">Variables (cliquez pour insérer) :</span>
          {["{{companyDescription}}", "{{offers}}"].map((v) => (
            <button
              key={v}
              type="button"
              className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded hover:bg-blue-100 transition-colors font-mono"
              onClick={() => {
                const current = str("systemPrompt");
                onChange("systemPrompt", current + "\n" + v);
              }}
            >
              {v}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

function N8nWorkflowFormBody({
  formData,
  onChange,
  tables,
}: {
  formData: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  tables: Tables;
}) {
  const { t } = useTranslation();
  const str = (key: string) => {
    const v = formData[key];
    return v === null || v === undefined ? "" : String(v);
  };

  const allUsers = (tables.users?.data ?? []) as Record<string, unknown>[];

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 space-y-1">
          <Label className="text-xs text-slate-500">Base URL</Label>
          <Input value={str("baseUrl")} onChange={(e) => onChange("baseUrl", e.target.value)} placeholder="https://..." />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Coach Agent</Label>
          <Input value={str("coachAgent")} onChange={(e) => onChange("coachAgent", e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Feedback Agent</Label>
          <Input value={str("feedbackAgent")} onChange={(e) => onChange("feedbackAgent", e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Scenario Agent</Label>
          <Input value={str("scenarioAgent")} onChange={(e) => onChange("scenarioAgent", e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Simulator Agent</Label>
          <Input value={str("simulatorAgent")} onChange={(e) => onChange("simulatorAgent", e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Drill Agent (S'exercer)</Label>
          <Input value={str("drillAgent")} onChange={(e) => onChange("drillAgent", e.target.value)} placeholder="chemin du webhook drillAgent" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Coach Prepa Agent</Label>
          <Input value={str("prepMeetingAgent")} onChange={(e) => onChange("prepMeetingAgent", e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Coach Prepa Chat Agent</Label>
          <Input value={str("prepMeetingChatAgent")} onChange={(e) => onChange("prepMeetingChatAgent", e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Memory Post Session</Label>
          <Input value={str("memoryPostSession")} onChange={(e) => onChange("memoryPostSession", e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Memory Preferences</Label>
          <Input value={str("memoryPreferences")} onChange={(e) => onChange("memoryPreferences", e.target.value)} />
        </div>
      </div>

      <FKSingleSelect
        label="Created By"
        placeholder={t("admin.searchUser")}
        allItems={allUsers}
        selectedId={str("createdBy")}
        onSelect={(id) => onChange("createdBy", id || null)}
        displayFn={(u) => `${u.firstName || ""} ${u.lastName || ""}`.trim() || String(u.email || u.id)}
      />
    </>
  );
}

function AudioSessionFormBody({
  formData,
  onChange,
  tables,
}: {
  formData: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  tables: Tables;
}) {
  const { t } = useTranslation();
  const str = (key: string) => {
    const v = formData[key];
    return v === null || v === undefined ? "" : String(v);
  };

  const allUsers = (tables.users?.data ?? []) as Record<string, unknown>[];

  return (
    <FKSingleSelect
      label="Created By"
      placeholder={t("admin.searchUser")}
      allItems={allUsers}
      selectedId={str("createdBy")}
      onSelect={(id) => onChange("createdBy", id || null)}
      displayFn={(u) => `${u.firstName || ""} ${u.lastName || ""}`.trim() || String(u.email || u.id)}
    />
  );
}

// ─── Editable text component for WYSIWYG ───
function EditableText({
  value,
  onChange,
  tag = "span",
  className = "",
  placeholder = "",
  multiline = false,
}: {
  value: string;
  onChange: (v: string) => void;
  tag?: "h2" | "h4" | "p" | "span";
  className?: string;
  placeholder?: string;
  multiline?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  React.useEffect(() => { setDraft(value); }, [value]);

  const commit = () => {
    setEditing(false);
    if (draft !== value) onChange(draft);
  };

  if (editing) {
    if (multiline) {
      return (
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Escape") { setDraft(value); setEditing(false); } }}
          autoFocus
          rows={3}
          className={`w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 resize-y ${className}`}
          placeholder={placeholder}
        />
      );
    }
    return (
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setDraft(value); setEditing(false); } }}
        autoFocus
        className={`w-full px-2 py-0.5 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 ${className}`}
        placeholder={placeholder}
      />
    );
  }

  const Tag = tag;
  return (
    <Tag
      onClick={() => setEditing(true)}
      className={`cursor-pointer hover:bg-blue-50/50 rounded px-1 -mx-1 transition-colors ${!value ? "text-slate-300 italic" : ""} ${className}`}
      title="Cliquer pour modifier"
    >
      {value || placeholder || "..."}
    </Tag>
  );
}

// ─── WYSIWYG Simulator Editor Modal ───
function SimulatorWysiwygModal({
  formData,
  onChange,
  tables,
  onClose,
  onSave,
  saving,
  error,
  onDelete,
  deleting,
  showDeleteDialog,
  onConfirmDelete,
  onCancelDelete,
  isCreate = false,
}: {
  formData: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  tables: Tables;
  onClose: () => void;
  onSave: () => void;
  saving: boolean;
  error: string | null;
  onDelete?: () => void;
  deleting?: boolean;
  showDeleteDialog?: boolean;
  onConfirmDelete?: () => void;
  onCancelDelete?: () => void;
  isCreate?: boolean;
}) {
  const { t } = useTranslation();
  const str = (key: string) => {
    const v = formData[key];
    return v === null || v === undefined ? "" : String(v);
  };

  // Parse presentationJson
  const presentation = React.useMemo(() => {
    try {
      return JSON.parse(str("presentationJson") || "{}");
    } catch {
      return {};
    }
  }, [formData.presentationJson]);

  const updatePresentation = (key: string, value: unknown) => {
    const next = { ...presentation, [key]: value };
    onChange("presentationJson", JSON.stringify(next));
  };

  const steps: { title: string; description: string }[] = presentation.steps || [];
  const tips: string[] = presentation.tips || [];
  const skills: string[] = presentation.skills || [];
  const agentColor = str("color") || "#988EF2";

  // Licences
  const allLicences = tables.licences?.data ?? [];
  const simulatorId = str("id");
  const allSimulatorLicences = (tables.simulator_licences?.data ?? []) as Record<string, unknown>[];
  const linkedLicenceIds = new Set(
    allSimulatorLicences
      .filter((sl) => String(sl.simulatorId) === simulatorId)
      .map((sl) => String(sl.licenceId))
  );
  const [selectedLicences, setSelectedLicences] = React.useState<Set<string>>(() => linkedLicenceIds);

  React.useEffect(() => {
    onChange("_selectedLicences", Array.from(selectedLicences));
    onChange("_originalLicences", Array.from(linkedLicenceIds));
  }, [selectedLicences]);

  const toggleLicence = (licenceId: string) => {
    setSelectedLicences((prev) => {
      const next = new Set(prev);
      if (next.has(licenceId)) next.delete(licenceId);
      else next.add(licenceId);
      return next;
    });
  };

  // Skills management
  const [newSkill, setNewSkill] = React.useState("");
  const addSkill = () => {
    if (newSkill.trim()) {
      updatePresentation("skills", [...skills, newSkill.trim()]);
      setNewSkill("");
    }
  };

  // Resize hooks
  const { size: wysiwygSize, containerRef: wysiwygRef, onPointerDown: wysiwygPointerDown } = useResizable({
    minWidth: 700,
    minHeight: 500,
    maxWidth: typeof window !== "undefined" ? window.innerWidth - 32 : 1600,
    maxHeight: typeof window !== "undefined" ? window.innerHeight - 32 : 1200,
  });
  const { panelWidth, onDividerPointerDown } = usePanelResize({
    initialWidth: 288,
    minWidth: 200,
    maxWidth: 480,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        ref={wysiwygRef}
        className="relative bg-white rounded-xl shadow-2xl flex flex-col m-4"
        style={{
          width: wysiwygSize.width ? `${wysiwygSize.width}px` : undefined,
          maxWidth: wysiwygSize.width ? undefined : "64rem",
          height: wysiwygSize.height ? `${wysiwygSize.height}px` : undefined,
          maxHeight: wysiwygSize.height ? undefined : "92vh",
        }}
      >
        <ResizeHandles onPointerDown={wysiwygPointerDown} />
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b">
          <h3 className="text-lg font-semibold text-[#384872]">
            {isCreate ? t("admin.create") : t("admin.edit")} — Simulateur
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100 transition-colors">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {/* Body: 2 columns */}
        <div className="flex-1 overflow-hidden flex">
          {/* ── Left sidebar: Technical params ── */}
          <div className="flex-shrink-0 border-r overflow-y-auto px-4 py-4 space-y-3 bg-slate-50/50" style={{ width: `${panelWidth}px` }}>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Parametres</h4>

            <div className="space-y-1">
              <Label className="text-xs text-slate-500">{t("admin.name")}</Label>
              <Input value={str("name")} onChange={(e) => onChange("name", e.target.value)} className="text-sm" />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-500">{t("admin.descriptionLabel")}</Label>
              <textarea
                value={str("description")}
                onChange={(e) => onChange("description", e.target.value)}
                rows={2}
                className="w-full px-3 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#384872]/30 focus:border-[#384872] resize-y"
              />
            </div>

            {/* Descriptions courtes FR + EN affichées sur la page d'accueil (max 150 car.) */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-slate-500">{t("admin.shortDescriptionFr")}</Label>
                <span className={`text-[10px] tabular-nums ${str("descriptionFr").length >= 150 ? "text-[#C0423F] font-medium" : "text-slate-400"}`}>
                  {str("descriptionFr").length}/150
                </span>
              </div>
              <textarea
                value={str("descriptionFr")}
                onChange={(e) => onChange("descriptionFr", e.target.value.slice(0, 150))}
                maxLength={150}
                rows={2}
                placeholder={t("admin.shortDescriptionHint")}
                className="w-full px-3 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#384872]/30 focus:border-[#384872] resize-y"
              />
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-slate-500">{t("admin.shortDescriptionEn")}</Label>
                <span className={`text-[10px] tabular-nums ${str("descriptionEn").length >= 150 ? "text-[#C0423F] font-medium" : "text-slate-400"}`}>
                  {str("descriptionEn").length}/150
                </span>
              </div>
              <textarea
                value={str("descriptionEn")}
                onChange={(e) => onChange("descriptionEn", e.target.value.slice(0, 150))}
                maxLength={150}
                rows={2}
                placeholder={t("admin.shortDescriptionHint")}
                className="w-full px-3 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#384872]/30 focus:border-[#384872] resize-y"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">{t("admin.simulatorType")}</Label>
                <Select value={str("simulatorType") || undefined} onValueChange={(v) => onChange("simulatorType", v)}>
                  <SelectTrigger className="w-full text-sm"><SelectValue placeholder={t("admin.select")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="training">Training</SelectItem>
                    <SelectItem value="coach">Coach</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">{t("admin.status")}</Label>
                <button
                  type="button"
                  onClick={() => onChange("isActive", formData.isActive === false ? true : formData.isActive === true ? false : false)}
                  className={`w-full flex items-center justify-center gap-1.5 px-2 py-2 rounded-md border text-xs font-medium transition-colors ${
                    formData.isActive !== false
                      ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                      : "bg-slate-50 border-slate-200 text-slate-400"
                  }`}
                >
                  <span className={`h-2 w-2 rounded-full ${formData.isActive !== false ? "bg-emerald-500" : "bg-slate-300"}`} />
                  {formData.isActive !== false ? t("admin.activeStatus") : t("admin.inactiveStatus")}
                </button>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-500">{t("admin.category")}</Label>
              <Select value={str("category") || undefined} onValueChange={(v) => onChange("category", v)}>
                <SelectTrigger className="w-full text-sm"><SelectValue placeholder={t("admin.select")} /></SelectTrigger>
                <SelectContent>
                  {(tables.categories?.data ?? []).map((cat: any) => (
                    <SelectItem key={cat.name} value={cat.name}>
                      <span className="flex items-center gap-2">
                        {cat.color && <span className="h-2 w-2 rounded-full inline-block" style={{ backgroundColor: cat.color }} />}
                        {cat.labelFr || cat.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Types de cas supportés (caseTypes) */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Types de cas supportés</Label>
              {(() => {
                const current = Array.isArray(formData.caseTypes) ? (formData.caseTypes as string[]) : ["B2B"];
                const toggle = (type: "B2B" | "B2C") => {
                  const has = current.includes(type);
                  let next = has ? current.filter((t) => t !== type) : [...current, type];
                  if (next.length === 0) next = [type];
                  onChange("caseTypes", next);
                };
                return (
                  <div className="flex gap-2">
                    {(["B2B", "B2C"] as const).map((type) => {
                      const active = current.includes(type);
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => toggle(type)}
                          className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md border text-xs font-medium transition-colors ${
                            active ? "bg-[#384872] text-white border-[#384872]" : "bg-white text-slate-500 border-slate-300 hover:bg-slate-50"
                          }`}
                        >
                          {active && <Check className="h-3 w-3" />}
                          {type}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Ordre d'affichage</Label>
              <Input type="number" value={str("sortOrder")} onChange={(e) => onChange("sortOrder", parseInt(e.target.value) || 0)} className="text-sm w-20" />
            </div>

            <ImageField label={t("admin.icon")} value={str("icon")} onChange={(val) => onChange("icon", val)} />
            <ImageField label={t("admin.image")} value={str("image")} onChange={(val) => onChange("image", val)} />
            <ImageField
              label="Avatar (Presentation JSON)"
              value={String(presentation.avatar || "")}
              onChange={(val) => updatePresentation("avatar", val)}
            />

            {/* Color */}
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">{t("admin.color")}</Label>
              <div className="flex items-center gap-2 flex-wrap">
                {["#F49390", "#A6D3F9", "#F6C847", "#7CBF94", "#988EF2", "#2563EB", "#384872", "#F59E0B"].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => onChange("color", c)}
                    className={`h-6 w-6 rounded-full border-2 transition-transform ${
                      str("color") === c ? "border-slate-800 scale-110" : "border-transparent hover:scale-105"
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
                <input
                  type="color"
                  value={str("color") || "#384872"}
                  onChange={(e) => onChange("color", e.target.value)}
                  className="h-6 w-6 rounded border cursor-pointer"
                />
              </div>
            </div>

            {/* Tags */}
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Tags</Label>
              <div className="space-y-2">
                {(Array.isArray(formData.tags) ? formData.tags : []).map((tag: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-1.5">
                    <input
                      type="color"
                      value={tag.color || "#E8D5F5"}
                      onChange={(e) => {
                        const tags = [...(formData.tags as any[])];
                        tags[idx] = { ...tags[idx], color: e.target.value };
                        onChange("tags", tags);
                      }}
                      className="h-5 w-5 rounded border cursor-pointer flex-shrink-0"
                    />
                    <Input
                      value={tag.label || ""}
                      onChange={(e) => {
                        const tags = [...(formData.tags as any[])];
                        tags[idx] = { ...tags[idx], label: e.target.value };
                        onChange("tags", tags);
                      }}
                      className="text-xs h-7 flex-1"
                      placeholder="Ex: Sales, SAV..."
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const tags = (formData.tags as any[]).filter((_: any, i: number) => i !== idx);
                        onChange("tags", tags.length ? tags : null);
                      }}
                      className="text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <div className="flex flex-wrap gap-1">
                  {[
                    { label: "Sales", color: "#D5F5E3" },
                    { label: "Customer Care", color: "#E8D5F5" },
                    { label: "B2B", color: "#D5E8F5" },
                    { label: "B2C", color: "#FDE8D5" },
                  ].map((preset) => {
                    const alreadyExists = (formData.tags as any[] || []).some((t: any) => t.label === preset.label);
                    if (alreadyExists) return null;
                    return (
                      <button
                        key={preset.label}
                        type="button"
                        onClick={() => onChange("tags", [...(Array.isArray(formData.tags) ? formData.tags : []) as any[], preset])}
                        className="text-[10px] px-2 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-700 transition-colors"
                      >
                        + {preset.label}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => onChange("tags", [...(Array.isArray(formData.tags) ? formData.tags : []) as any[], { label: "", color: "#F5E6D5" }])}
                    className="text-[10px] px-2 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-700 transition-colors"
                  >
                    + Personnalisé
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-500">{t("admin.openaiModel")}</Label>
              <Input value={str("modeleOpenai")} onChange={(e) => onChange("modeleOpenai", e.target.value)} placeholder="gpt-4o, gpt-4o-mini..." className="text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">{t("admin.urlGpt")}</Label>
              <Input type="url" value={str("urlGpt")} onChange={(e) => onChange("urlGpt", e.target.value)} placeholder="https://..." className="text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">{t("admin.systemPrompt")}</Label>
              <textarea
                value={str("systemPrompt")}
                onChange={(e) => onChange("systemPrompt", e.target.value)}
                rows={3}
                className="w-full px-3 py-1.5 border rounded-md text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#384872]/30 focus:border-[#384872] resize-y"
              />
            </div>

            {/* Scoring Criteria */}
            <ScoringCriteriaEditor
              value={formData.scoringCriteria}
              onChange={(val) => onChange("scoringCriteria", val)}
            />

            {/* Form Schema (for coaches) */}
            <FormSchemaEditor
              value={formData.formSchema}
              onChange={(val) => onChange("formSchema", val)}
            />

            {/* Licences */}
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">{t("admin.requiredLicences")}</Label>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {allLicences.map((lic) => {
                  const id = String(lic.id);
                  const checked = selectedLicences.has(id);
                  return (
                    <label key={id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-100 rounded px-1 py-0.5">
                      <input type="checkbox" checked={checked} onChange={() => toggleLicence(id)} className="rounded" />
                      <span className={checked ? "font-medium text-slate-800" : "text-slate-500"}>{String(lic.name || lic.code || id)}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          <PanelDivider onPointerDown={onDividerPointerDown} />

          {/* ── Right: WYSIWYG Preview ── */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="max-w-3xl mx-auto space-y-4">
              {/* Agent header */}
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl px-5 pt-4 pb-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    {str("image") ? (
                      <img src={str("image")} alt={str("name")} loading="lazy" className="h-16 w-16" />
                    ) : (
                      <div
                        className="h-16 w-16 rounded-2xl flex items-center justify-center text-2xl"
                        style={{ backgroundColor: agentColor, color: "white" }}
                      >
                        {str("icon") || "?"}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <EditableText
                      value={str("name")}
                      onChange={(v) => onChange("name", v)}
                      tag="h2"
                      className="text-2xl font-bold mb-1"
                      placeholder="Nom du simulateur"
                    />
                    <EditableText
                      value={presentation.fullDescription || ""}
                      onChange={(v) => updatePresentation("fullDescription", v)}
                      tag="p"
                      className="text-sm mb-3"
                      placeholder="Description complete de l'agent..."
                      multiline
                    />
                    <div className="flex flex-wrap gap-3">
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white shadow-sm">
                        <Target className="h-4 w-4 flex-shrink-0" style={{ color: "#F99002" }} />
                        <EditableText
                          value={presentation.objective || ""}
                          onChange={(v) => updatePresentation("objective", v)}
                          tag="span"
                          className="text-sm font-medium"
                          placeholder="Objectif"
                        />
                      </div>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white shadow-sm">
                        <Clock className="h-4 w-4 flex-shrink-0" style={{ color: "#72B76E" }} />
                        <EditableText
                          value={presentation.duration || ""}
                          onChange={(v) => updatePresentation("duration", v)}
                          tag="span"
                          className="text-sm font-medium"
                          placeholder="5-10 minutes"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Skills */}
              <div>
                <h3 className="text-lg font-semibold mb-2 flex items-center gap-2" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
                  <Award className="h-5 w-5" style={{ color: "#F6C847" }} />
                  {t("agentPresentation.skillsWorked")}
                </h3>
                <div className="flex flex-wrap gap-2 items-center">
                  {skills.map((skill, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium" style={{ backgroundColor: "#E8F5E9", color: "#2E7D32" }}>
                      <span
                        className="cursor-pointer"
                        onClick={() => {
                          const v = prompt("Modifier la competence :", skill);
                          if (v !== null) {
                            const next = [...skills];
                            next[i] = v;
                            updatePresentation("skills", next);
                          }
                        }}
                      >
                        {skill}
                      </span>
                      <button
                        type="button"
                        onClick={() => updatePresentation("skills", skills.filter((_, j) => j !== i))}
                        className="text-green-700/50 hover:text-red-500 ml-0.5 text-xs font-bold"
                      >
                        x
                      </button>
                    </span>
                  ))}
                  <form
                    onSubmit={(e) => { e.preventDefault(); addSkill(); }}
                    className="inline-flex items-center gap-1"
                  >
                    <input
                      value={newSkill}
                      onChange={(e) => setNewSkill(e.target.value)}
                      placeholder="+ Ajouter..."
                      className="px-2 py-1 border border-dashed border-green-300 rounded-full text-sm text-green-700 bg-green-50/50 w-28 focus:outline-none focus:border-green-500"
                    />
                  </form>
                </div>
              </div>

              {/* Steps */}
              <div>
                <h3 className="text-lg font-semibold mb-2 flex items-center gap-2" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
                  <MessageCircle className="h-5 w-5" style={{ color: "#3B82F6" }} />
                  {t("agentPresentation.simulationSteps")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {steps.map((step, i) => (
                    <div key={i} className="relative group flex items-start gap-3 p-3 rounded-xl bg-slate-50">
                      <div className="flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: "#384872" }}>
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <EditableText
                          value={step.title}
                          onChange={(v) => {
                            const next = [...steps];
                            next[i] = { ...next[i], title: v };
                            updatePresentation("steps", next);
                          }}
                          tag="h4"
                          className="font-semibold text-sm"
                          placeholder="Titre"
                        />
                        <EditableText
                          value={step.description}
                          onChange={(v) => {
                            const next = [...steps];
                            next[i] = { ...next[i], description: v };
                            updatePresentation("steps", next);
                          }}
                          tag="p"
                          className="text-xs"
                          placeholder="Description"
                          multiline
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => updatePresentation("steps", steps.filter((_, j) => j !== i))}
                        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 text-xs bg-white rounded-full h-5 w-5 flex items-center justify-center shadow-sm transition-opacity"
                      >
                        x
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => updatePresentation("steps", [...steps, { title: "", description: "" }])}
                    className="flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-slate-200 text-slate-400 hover:border-blue-300 hover:text-blue-500 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="text-sm">Etape</span>
                  </button>
                </div>
              </div>

              {/* Tips */}
              <div>
                <h3 className="text-lg font-semibold mb-2 flex items-center gap-2" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
                  <Check className="h-5 w-5" style={{ color: "#72B76E" }} />
                  {t("agentPresentation.tipsForSuccess")}
                </h3>
                <ul className="space-y-2">
                  {tips.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2 group">
                      <Check className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: "#72B76E" }} />
                      <div className="flex-1">
                        <EditableText
                          value={tip}
                          onChange={(v) => {
                            const next = [...tips];
                            next[i] = v;
                            updatePresentation("tips", next);
                          }}
                          tag="span"
                          className="text-sm"
                          placeholder="Conseil..."
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => updatePresentation("tips", tips.filter((_, j) => j !== i))}
                        className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 text-xs transition-opacity"
                      >
                        x
                      </button>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => updatePresentation("tips", [...tips, ""])}
                  className="mt-2 flex items-center gap-1 text-sm text-blue-500 hover:text-blue-700"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Ajouter un conseil
                </button>
              </div>

              {/* How it works — static */}
              <div className="rounded-xl p-4" style={{ backgroundColor: "#FFF7ED" }}>
                <h3 className="text-base font-semibold mb-2 flex items-center gap-2" style={{ color: "#384872" }}>
                  🎙 {t("agentPresentation.howItWorks")}
                </h3>
                <p className="text-xs text-slate-400 italic">Bloc statique (contenu i18n, non editable)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="px-6 py-2 text-sm text-red-600 bg-red-50 border-t border-red-100">
            {error}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t bg-slate-50 rounded-b-xl">
          {!isCreate && onDelete ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={onDelete}
                disabled={deleting}
                className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
                {t("admin.deleteBtn")}
              </Button>
              {onConfirmDelete && onCancelDelete && (
                <ConfirmDeleteDialog
                  open={!!showDeleteDialog}
                  onConfirm={onConfirmDelete}
                  onCancel={onCancelDelete}
                  itemName={String(formData.name || "")}
                  loading={!!deleting}
                />
              )}
            </>
          ) : <div />}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>{t("admin.cancel")}</Button>
            <Button
              size="sm"
              onClick={onSave}
              disabled={saving}
              className="gap-2 bg-[#384872] hover:bg-[#2d3a5c] text-white"
            >
              <Save className="h-4 w-4" />
              {saving ? t("admin.saving") : isCreate ? t("admin.create") : t("admin.saveBtn")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ViewModeBody({
  record,
  columns,
  tableName,
  tables,
  onFkClick,
}: {
  record: Record<string, unknown>;
  columns: string[];
  tableName: string;
  tables?: Tables;
  onFkClick?: (table: string, id: string) => void;
}) {
  const lookupMaps = tables ? buildLookupMaps(tables) : undefined;
  const sections = VIEW_SECTIONS[tableName];

  if (sections) {
    // Grouped display
    const usedFields = new Set(sections.flatMap((s) => s.fields));
    const extraFields = columns.filter((c) => !usedFields.has(c));

    return (
      <div className="space-y-4">
        {sections.map((section) => {
          const visibleFields = section.fields.filter((f) => columns.includes(f));
          if (visibleFields.length === 0) return null;
          return (
            <div key={section.label}>
              <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2 pb-1 border-b border-slate-100">
                {section.label}
              </h4>
              <div className="space-y-2">
                {visibleFields.map((col) => (
                  <div key={col} className="flex gap-3">
                    <span className="text-xs text-slate-500 w-[140px] text-right flex-shrink-0 pt-0.5">{col}</span>
                    <div className="flex-1 min-w-0">
                      {renderViewField(col, record[col], lookupMaps, tables, onFkClick)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {extraFields.length > 0 && (
          <div>
            <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2 pb-1 border-b border-slate-100">
              AUTRES
            </h4>
            <div className="space-y-2">
              {extraFields.map((col) => (
                <div key={col} className="flex gap-3">
                  <span className="text-xs text-slate-500 w-[140px] text-right flex-shrink-0 pt-0.5">{col}</span>
                  <div className="flex-1 min-w-0">
                    {renderViewField(col, record[col], lookupMaps, tables, onFkClick)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Linear display (all other tables) with type-aware rendering
  return (
    <div className="space-y-2">
      {columns.map((col) => (
        <div key={col} className="flex gap-3">
          <span className="text-xs text-slate-500 w-[140px] text-right flex-shrink-0 pt-0.5">{col}</span>
          <div className="flex-1 min-w-0">
            {renderViewField(col, record[col], lookupMaps, tables, onFkClick)}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Section hiérarchie d'entités dans la modale d'un compte : choisir un compte
 * parent (rattachement, validé anti-cycle/profondeur côté serveur) + lister les
 * entités filles directes. (Vague 3 · incrément 1.)
 */
function EntityHierarchySection({
  account,
  tables,
  disabled,
  readOnly,
}: {
  account: Record<string, unknown>;
  tables?: Tables;
  disabled?: boolean;
  /** Lecture seule : affiche le parent en texte (pas de sélecteur, pas d'écriture). */
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const accountId = String(account.id || "");
  const [parentId, setParentId] = useState<string>(String(account.parentClientAccountId || ""));
  const [children, setChildren] = useState<Array<{ id: string; name: string; isActive: boolean }>>([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  const allAccounts = ((tables?.client_accounts?.data ?? []) as Array<Record<string, unknown>>)
    .filter((a) => String(a.id) !== accountId); // exclut soi-même (validation complète côté serveur)
  const parentName = parentId
    ? String((allAccounts.find((a) => String(a.id) === parentId)?.name) || parentId)
    : "";

  const loadChildren = async () => {
    try {
      setChildren(await adminApi.getAccountChildren(accountId));
    } catch { /* silencieux : liste vide si erreur */ }
  };
  useEffect(() => {
    if (accountId) loadChildren();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const save = async (newParent: string | null) => {
    setSaving(true);
    setMsg(null);
    try {
      await adminApi.setAccountParent(accountId, newParent);
      setParentId(newParent || "");
      setMsg({ type: "ok", text: t("admin.entityParentSaved") });
      loadChildren();
    } catch (e: any) {
      setMsg({ type: "err", text: e.message || "Erreur" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t space-y-3">
      <p className="text-sm font-semibold text-slate-700">{t("admin.entityHierarchy")}</p>

      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">{t("admin.parentAccount")}</label>
        {readOnly ? (
          <p className="text-sm">{parentName || t("admin.noParent")}</p>
        ) : (
          <>
            <select
              className="w-full text-sm border rounded p-2 bg-white disabled:opacity-60"
              value={parentId}
              disabled={disabled || saving}
              onChange={(e) => save(e.target.value || null)}
            >
              <option value="">{t("admin.noParent")}</option>
              {allAccounts.map((a) => (
                <option key={String(a.id)} value={String(a.id)}>{String(a.name)}</option>
              ))}
            </select>
            <p className="text-[11px] text-slate-400 mt-1">{t("admin.parentAccountHint")}</p>
          </>
        )}
      </div>

      {msg && !readOnly && (
        <p className={`text-xs ${msg.type === "ok" ? "text-green-600" : "text-red-600"}`}>{msg.text}</p>
      )}

      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">
          {t("admin.attachedEntities")} ({children.length})
        </label>
        {children.length === 0 ? (
          <p className="text-xs text-slate-400">{t("admin.noAttachedEntities")}</p>
        ) : (
          <ul className="space-y-1">
            {children.map((c) => (
              <li key={c.id} className="text-sm flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${c.isActive ? "bg-green-500" : "bg-slate-300"}`} />
                {c.name}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function EditModal({
  record,
  columns: columnsProp,
  tableName,
  mode,
  onClose,
  onSaved,
  onDuplicated,
  tables,
}: {
  record: Record<string, unknown>;
  columns: string[];
  tableName: string;
  mode: "edit" | "create" | "view";
  onClose: () => void;
  onSaved: () => void;
  /**
   * Appelé après une duplication réussie. Le parent peut basculer la modale
   * sur la nouvelle row en mode "edit" pour que l'admin modifie directement
   * les champs à ajuster (cf. feature dupliquer demandée le 27/05/2026).
   */
  onDuplicated?: (newRecord: Record<string, unknown>) => void;
  tables?: Tables;
}) {
  const [formData, setFormData] = useState<Record<string, unknown>>({ ...record });
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  // Table en lecture seule pour ce profil (Q1 du 01/10/2026) : la fiche s'ouvre en lecture, sans
  // modifier, dupliquer ni supprimer — le serveur refuse de toute façon (403).
  const tableReadOnly = !!tables?.[tableName]?.readOnly;
  const [currentMode, setCurrentMode] = useState<"edit" | "create" | "view">(tableReadOnly ? "view" : mode);

  const isCreate = currentMode === "create";
  const isView = currentMode === "view";
  const isUserTable = tableName === "users";
  const isSimulatorTable = tableName === "simulators";
  const isLicenceTable = tableName === "licences";
  const isScenarioTable = tableName === "scenarios";
  const isScenarioRequestTable = tableName === "scenario_requests";
  const isTargetTable = tableName === "targets";
  const isScenarioChatMessageTable = tableName === "scenario_chat_messages";
  const isN8nWorkflowTable = tableName === "n8n_workflows";
  const isAudioSessionTable = tableName === "audio_sessions";
  const isAiPromptTable = tableName === "ai_prompts";
  const isClientAccountTable = tableName === "client_accounts";

  const isSessionTable = tableName === "sessions";
  const isFeedbackTable = tableName === "feedbacks";
  const isCategoryTable = tableName === "categories";

  // Pour les tables génériques, filtrer les colonnes masquées
  const genericColumns = columnsProp.filter((col) => !HIDDEN_FIELDS.has(col));

  const handleChange = (key: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const SIMULATOR_FIELDS = ["name", "description", "descriptionFr", "descriptionEn", "simulatorType", "category", "caseTypes", "isActive", "sortOrder", "icon", "image", "color", "presentationJson", "scoringCriteria", "formSchema", "modeleOpenai", "urlGpt", "systemPrompt", "tags"];
  const LICENCE_FIELDS = ["code", "name", "description", "color", "isActive", "isTrial", "isOneShot", "sessionsPerPack", "zohoPlanCode", "priceHt", "maxSimulators", "billingInterval", "tier"];
  const AI_PROMPT_FIELDS = ["code", "name", "description", "model", "temperature", "systemPrompt"];
  const SCENARIO_FIELDS = ["companyName", "prospectName", "prospectGender", "model", "promptVersion", "totalTokens", "userId", "scenarioRequestId", "userMessage", "raw", "variables", "selectedIndustry", "prospectCompanyDescription", "priorContext"];
  const SCENARIO_REQUEST_FIELDS = ["difficulty", "industry", "companySize", "prospectType", "personaId", "decisionMakerProfile", "painPoints", "creatorId"];
  const TARGET_FIELDS = ["companySize", "industry", "clientAccountId", "createdBy"];
  const SCENARIO_CHAT_MESSAGE_FIELDS = ["content", "role", "scenarioPropositionId", "scenarioRequestId", "createdBy"];
  const N8N_WORKFLOW_FIELDS = ["baseUrl", "coachAgent", "feedbackAgent", "scenarioAgent", "simulatorAgent", "drillAgent", "prepMeetingAgent", "prepMeetingChatAgent", "memoryPostSession", "memoryPreferences", "createdBy"];
  const AUDIO_SESSION_FIELDS = ["createdBy"];
  const SESSION_FIELDS = ["simulatorId", "challengeId", "personaId", "durationInMinutes", "endTime", "feedbackId", "feedbackAudioSessionId", "numberOfMessages", "scenarioRequestId", "selectedScenarioId", "simulationAudioSessionId", "userId", "createdBy"];
  const FEEDBACK_FIELDS = ["scores", "content", "raw", "summary", "totalScore", "totalTokens", "createdBy"];
  const CATEGORY_FIELDS = ["name", "labelFr", "labelEn", "color", "textColor", "sortOrder"];

  // Sync simulator_licences after saving a simulator
  const syncSimulatorLicences = async (simulatorId: string) => {
    const selected = (formData._selectedLicences as string[]) || [];
    const original = (formData._originalLicences as string[]) || [];
    const selectedSet = new Set(selected);
    const originalSet = new Set(original);

    // Licences to add
    const toAdd = selected.filter((id) => !originalSet.has(id));
    // Licences to remove
    const toRemove = original.filter((id) => !selectedSet.has(id));

    const allSimulatorLicences = (tables?.simulator_licences?.data ?? []) as Record<string, unknown>[];

    for (const licenceId of toAdd) {
      await adminApi.createRecord("simulator_licences", { simulatorId, licenceId });
    }

    for (const licenceId of toRemove) {
      const linkRecord = allSimulatorLicences.find(
        (sl) => String(sl.simulatorId) === simulatorId && String(sl.licenceId) === licenceId
      );
      if (linkRecord) {
        await adminApi.deleteRecord("simulator_licences", String(linkRecord.id));
      }
    }
  };

  // Sync simulator_licences from licence side (inverse relation)
  const syncLicenceSimulators = async (licenceId: string) => {
    const selected = (formData._selectedSimulators as string[]) || [];
    const original = (formData._originalSimulators as string[]) || [];
    const selectedSet = new Set(selected);
    const originalSet = new Set(original);

    const toAdd = selected.filter((id) => !originalSet.has(id));
    const toRemove = original.filter((id) => !selectedSet.has(id));

    const allSimulatorLicences = (tables?.simulator_licences?.data ?? []) as Record<string, unknown>[];

    for (const simulatorId of toAdd) {
      await adminApi.createRecord("simulator_licences", { simulatorId, licenceId });
    }

    for (const simulatorId of toRemove) {
      const linkRecord = allSimulatorLicences.find(
        (sl) => String(sl.simulatorId) === simulatorId && String(sl.licenceId) === licenceId
      );
      if (linkRecord) {
        await adminApi.deleteRecord("simulator_licences", String(linkRecord.id));
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isCreate) {
        const data: Record<string, unknown> = {};
        const keys = isUserTable
          ? ["civility", "firstName", "lastName", "email", "phoneNumber", "linkedinUrl", "profileType", "status", "profilePicture", "clientAccountId", "language"]
          : isSimulatorTable
          ? SIMULATOR_FIELDS
          : isLicenceTable
          ? LICENCE_FIELDS
          : isScenarioTable
          ? SCENARIO_FIELDS
          : isScenarioRequestTable
          ? SCENARIO_REQUEST_FIELDS
          : isTargetTable
          ? TARGET_FIELDS
          : isScenarioChatMessageTable
          ? SCENARIO_CHAT_MESSAGE_FIELDS
          : isN8nWorkflowTable
          ? N8N_WORKFLOW_FIELDS
          : isAudioSessionTable
          ? AUDIO_SESSION_FIELDS
          : isSessionTable
          ? SESSION_FIELDS
          : isFeedbackTable
          ? FEEDBACK_FIELDS
          : isCategoryTable
          ? CATEGORY_FIELDS
          : genericColumns;
        for (const key of keys) {
          if (formData[key] !== undefined && formData[key] !== "") {
            data[key] = formData[key];
          }
        }
        const created = await adminApi.createRecord(tableName, data);
        if (isSimulatorTable && created?.id) {
          await syncSimulatorLicences(String(created.id));
        }
        if (isLicenceTable && created?.id) {
          await syncLicenceSimulators(String(created.id));
        }
      } else {
        const normalize = (v: unknown) => (v === "" || v === undefined || v === null) ? null : v;
        const updates: Record<string, unknown> = {};
        const fields = isUserTable
          ? ["civility", "firstName", "lastName", "email", "phoneNumber", "linkedinUrl", "profileType", "status", "profilePicture", "clientAccountId", "language"]
          : isSimulatorTable ? SIMULATOR_FIELDS
          : isLicenceTable ? LICENCE_FIELDS
          : isScenarioTable ? SCENARIO_FIELDS
          : isScenarioRequestTable ? SCENARIO_REQUEST_FIELDS
          : isTargetTable ? TARGET_FIELDS
          : isScenarioChatMessageTable ? SCENARIO_CHAT_MESSAGE_FIELDS
          : isN8nWorkflowTable ? N8N_WORKFLOW_FIELDS
          : isAiPromptTable ? AI_PROMPT_FIELDS
          : isAudioSessionTable ? AUDIO_SESSION_FIELDS
          : isSessionTable ? SESSION_FIELDS
          : isFeedbackTable ? FEEDBACK_FIELDS
          : isCategoryTable ? CATEGORY_FIELDS
          : genericColumns.filter((k) => !READ_ONLY_FIELDS.has(k));
        for (const key of fields) {
          updates[key] = normalize(formData[key]);
        }
        await adminApi.updateRecord(tableName, String(record.id), updates);
        if (isSimulatorTable) {
          await syncSimulatorLicences(String(record.id));
        }
        if (isLicenceTable) {
          await syncLicenceSimulators(String(record.id));
        }
      }
      onSaved();
    } catch (err: any) {
      setError(err.message || t("admin.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDeleteRecord = async () => {
    setDeleting(true);
    setError(null);
    try {
      await adminApi.deleteRecord(tableName, String(record.id));
      setShowDeleteDialog(false);
      onSaved();
    } catch (err: any) {
      setError(err.message || t("admin.deleteError"));
    } finally {
      setDeleting(false);
    }
  };

  const [duplicating, setDuplicating] = useState(false);
  const handleDuplicate = async () => {
    if (!record.id || duplicating) return;
    setDuplicating(true);
    setError(null);
    try {
      const newRecord = await adminApi.duplicateRecord(tableName, String(record.id));
      onSaved();
      onDuplicated?.(newRecord);
    } catch (err: any) {
      setError(err.message || t("api.adminDuplicateError"));
    } finally {
      setDuplicating(false);
    }
  };

  // Resize support for the modal
  const defaultWidth = isAiPromptTable ? Math.min(900, (typeof window !== "undefined" ? window.innerWidth - 64 : 900)) : undefined;
  const defaultHeight = isAiPromptTable ? Math.min(850, (typeof window !== "undefined" ? window.innerHeight - 64 : 850)) : undefined;
  const { size: editModalSize, containerRef: editModalRef, onPointerDown: editModalPointerDown } = useResizable({
    minWidth: 400,
    minHeight: 350,
    maxWidth: typeof window !== "undefined" ? window.innerWidth - 32 : 1200,
    maxHeight: typeof window !== "undefined" ? window.innerHeight - 32 : 900,
    defaultWidth,
    defaultHeight,
  });

  // WYSIWYG mode for simulator editing & creation
  if (isSimulatorTable && (currentMode === "edit" || isCreate) && tables) {
    return (
      <SimulatorWysiwygModal
        isCreate={isCreate}
        formData={formData}
        onChange={handleChange}
        tables={tables}
        onClose={onClose}
        onSave={handleSave}
        saving={saving}
        error={error}
        onDelete={handleDelete}
        deleting={deleting}
        showDeleteDialog={showDeleteDialog}
        onConfirmDelete={confirmDeleteRecord}
        onCancelDelete={() => setShowDeleteDialog(false)}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        ref={editModalRef}
        className="relative bg-white rounded-xl shadow-2xl flex flex-col m-4"
        style={{
          width: editModalSize.width ? `${editModalSize.width}px` : undefined,
          maxWidth: editModalSize.width ? undefined : "32rem",
          height: editModalSize.height ? `${editModalSize.height}px` : undefined,
          maxHeight: editModalSize.height ? undefined : "85vh",
        }}
      >
        <ResizeHandles onPointerDown={editModalPointerDown} />
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-[#384872]">
            {isCreate ? t("admin.create") : isView ? "" : t("admin.edit")}{isCreate || !isView ? " — " : ""}{TABLE_LABELS[tableName] || tableName}
          </h3>
          <div className="flex items-center gap-1">
            {tableReadOnly && (
              <span className="text-sm text-slate-500 mr-1">{t("admin.readOnly")}</span>
            )}
            {isView && !tableReadOnly && (
              <button
                onClick={() => setCurrentMode("edit")}
                className="p-1.5 rounded-md hover:bg-slate-100 transition-colors"
                title={t("admin.edit")}
              >
                <Pencil className="h-5 w-5 text-slate-500" />
              </button>
            )}
            {!isCreate && record.id && !tableReadOnly && (
              <button
                onClick={handleDuplicate}
                disabled={duplicating}
                className="p-1.5 rounded-md hover:bg-slate-100 transition-colors disabled:opacity-50"
                title="Dupliquer"
              >
                <Copy className="h-5 w-5 text-slate-500" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-slate-100 transition-colors"
            >
              <X className="h-5 w-5 text-slate-500" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {isView && isLicenceTable && <ProductBuyLink product={record} />}
          {isView ? (
            <ViewModeBody
              record={record}
              columns={columnsProp}
              tableName={tableName}
              tables={tables}
              onFkClick={(fkTable, fkId) => {
                // Navigate to the FK record by closing this modal and opening the target
                // For now, just show the value
              }}
            />
          ) : isUserTable ? (
            <UserFormBody formData={formData} onChange={handleChange} tables={tables} />
          ) : isSimulatorTable && tables ? (
            <SimulatorFormBody formData={formData} onChange={handleChange} tables={tables} />
          ) : isLicenceTable && tables ? (
            <LicenceFormBody formData={formData} onChange={handleChange} tables={tables} />
          ) : isScenarioTable && tables ? (
            <ScenarioFormBody formData={formData} onChange={handleChange} tables={tables} />
          ) : isScenarioRequestTable && tables ? (
            <ScenarioRequestFormBody formData={formData} onChange={handleChange} tables={tables} />
          ) : isTargetTable && tables ? (
            <TargetFormBody formData={formData} onChange={handleChange} tables={tables} />
          ) : isScenarioChatMessageTable && tables ? (
            <ScenarioChatMessageFormBody formData={formData} onChange={handleChange} tables={tables} />
          ) : isAiPromptTable ? (
            <AiPromptFormBody formData={formData} onChange={handleChange} isView={isView} />
          ) : isN8nWorkflowTable && tables ? (
            <N8nWorkflowFormBody formData={formData} onChange={handleChange} tables={tables} />
          ) : isAudioSessionTable && tables ? (
            <AudioSessionFormBody formData={formData} onChange={handleChange} tables={tables} />
          ) : isSessionTable && tables ? (
            <SessionFormBody formData={formData} onChange={handleChange} tables={tables} />
          ) : isFeedbackTable && tables ? (
            <FeedbackFormBody formData={formData} onChange={handleChange} tables={tables} />
          ) : isCategoryTable ? (
            <CategoryFormBody formData={formData} onChange={handleChange} />
          ) : (
            <GenericFormBody
              columns={genericColumns}
              formData={formData}
              isCreate={isCreate}
              onChange={handleChange}
            />
          )}

          {/* Hiérarchie d'entités : compte parent + entités rattachées (comptes uniquement, hors création) */}
          {isClientAccountTable && !isCreate && (
            <EntityHierarchySection
              account={formData}
              tables={tables}
              disabled={isView}
            />
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="px-6 py-2 text-sm text-red-600 bg-red-50 border-t border-red-100">
            {error}
          </div>
        )}

        {/* Footer */}
        {isView ? (
          <div className="flex items-center justify-between px-6 py-4 border-t bg-slate-50 rounded-b-xl">
            <div>
              {!tableReadOnly && (<>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDelete}
                disabled={deleting}
                className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
                {t("admin.deleteBtn")}
              </Button>
              <ConfirmDeleteDialog
                open={showDeleteDialog}
                onConfirm={confirmDeleteRecord}
                onCancel={() => setShowDeleteDialog(false)}
                itemName={String(formData.name || formData.email || formData.id || "")}
                loading={deleting}
              />
              </>)}
            </div>
            <Button variant="outline" size="sm" onClick={onClose}>{t("admin.close")}</Button>
          </div>
        ) : (
        <div className={`flex items-center ${isCreate ? "justify-end" : "justify-between"} px-6 py-4 border-t bg-slate-50 rounded-b-xl`}>
          {!isCreate && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDelete}
                disabled={deleting}
                className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
                {t("admin.deleteBtn")}
              </Button>
              <ConfirmDeleteDialog
                open={showDeleteDialog}
                onConfirm={confirmDeleteRecord}
                onCancel={() => setShowDeleteDialog(false)}
                itemName={String(formData.name || formData.email || formData.id || "")}
                loading={deleting}
              />
            </>
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              {t("admin.cancel")}
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="gap-2 bg-[#384872] hover:bg-[#2d3a5c] text-white"
            >
              <Save className="h-4 w-4" />
              {saving ? t("admin.saving") : isCreate ? t("admin.create") : t("admin.saveBtn")}
            </Button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

function DataTable({
  table,
  tableName,
  onRowClick,
  onFkClick,
  lookupMaps,
  viewColumns,
  viewFilters,
}: {
  table: TableData;
  tableName: string;
  onRowClick: (row: Record<string, unknown>) => void;
  onFkClick?: (targetTable: string, id: string) => void;
  lookupMaps?: LookupMaps;
  viewColumns?: string[];
  viewFilters?: Record<string, FilterValue>;
}) {
  const { t } = useTranslation();
  const allColumns = table.columns ?? (table.data.length > 0 ? Object.keys(table.data[0]!) : []);

  // Colonnes visibles
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    if (viewColumns) return new Set(viewColumns);
    const hidden = DEFAULT_HIDDEN[tableName] || GLOBAL_HIDDEN;
    return new Set(allColumns.filter((c) => !hidden.has(c)));
  });

  // Recherche globale
  const [globalSearch, setGlobalSearch] = useState("");

  // Filtres par colonne (ad-hoc, en plus des filtres de vue)
  const [filters, setFilters] = useState<Record<string, FilterValue>>({});

  // Panneau filtres ouvert/fermé
  const [showFilters, setShowFilters] = useState(false);

  // Tri par colonne
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const handleSort = (col: string) => {
    if (sortCol === col) {
      if (sortDir === "asc") {
        setSortDir("desc");
      } else {
        setSortCol(null);
        setSortDir("asc");
      }
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  // Resize colonnes
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const resizingRef = React.useRef<{ col: string; startX: number; startWidth: number } | null>(null);

  const handleResizeStart = (col: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const th = (e.target as HTMLElement).parentElement!;
    resizingRef.current = { col, startX: e.clientX, startWidth: th.offsetWidth };
    const onMouseMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      const newWidth = Math.max(60, resizingRef.current.startWidth + ev.clientX - resizingRef.current.startX);
      setColumnWidths(prev => ({ ...prev, [resizingRef.current!.col]: newWidth }));
    };
    const onMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  // Reset quand tableName change
  useEffect(() => {
    if (viewColumns) {
      setVisibleColumns(new Set(viewColumns));
    } else {
      const hidden = DEFAULT_HIDDEN[tableName] || GLOBAL_HIDDEN;
      setVisibleColumns(new Set(allColumns.filter((c) => !hidden.has(c))));
    }
    setGlobalSearch("");
    setFilters({});
    setShowFilters(false);
    setColumnWidths({});
    setSortCol(null);
    setSortDir("asc");
  }, [tableName]);

  // Sync visibleColumns quand viewColumns change (switch de vue)
  const viewColsKey = viewColumns?.join(",") ?? "";
  useEffect(() => {
    if (viewColumns) {
      setVisibleColumns(new Set(viewColumns));
      setFilters({});
    }
  }, [viewColsKey]);

  // Mettre à jour visibleColumns si allColumns change (refresh)
  useEffect(() => {
    setVisibleColumns((prev) => {
      const validCols = new Set(allColumns);
      const next = new Set<string>();
      for (const c of prev) {
        if (validCols.has(c)) next.add(c);
      }
      if (next.size === 0) {
        if (viewColumns) return new Set(viewColumns.filter((c) => validCols.has(c)));
        const hidden = DEFAULT_HIDDEN[tableName] || GLOBAL_HIDDEN;
        return new Set(allColumns.filter((c) => !hidden.has(c)));
      }
      return next;
    });
  }, [allColumns.join(",")]);

  const setFilter = (col: string, filter: FilterValue | undefined) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (filter) {
        next[col] = filter;
      } else {
        delete next[col];
      }
      return next;
    });
  };

  // Merge view filters + ad-hoc filters
  const mergedFilters = useMemo(() => {
    const merged: Record<string, FilterValue> = {};
    if (viewFilters) {
      for (const [col, f] of Object.entries(viewFilters)) {
        merged[col] = f;
      }
    }
    // Ad-hoc filters override view filters
    for (const [col, f] of Object.entries(filters)) {
      merged[col] = f;
    }
    return merged;
  }, [viewFilters, filters]);

  const activeFilters = Object.entries(mergedFilters).filter(([, f]) => isFilterActive(f));

  const clearAllFilters = () => {
    setFilters({});
    setGlobalSearch("");
  };

  // Colonnes visibles ordonnées — respecter l'ordre de viewColumns si fourni
  const displayColumns = viewColumns
    ? viewColumns.filter((c) => visibleColumns.has(c))
    : allColumns.filter((c) => visibleColumns.has(c));

  // Filtrage des données
  const filteredData = useMemo(() => {
    let rows = table.data;

    // Filtres par colonne (vue + ad-hoc)
    for (const [col, filter] of Object.entries(mergedFilters)) {
      if (isFilterActive(filter)) {
        rows = rows.filter((row) => matchesFilter(row, col, filter));
      }
    }

    // Recherche globale
    if (globalSearch.trim()) {
      const search = globalSearch.trim().toLowerCase();
      rows = rows.filter((row) =>
        displayColumns.some((col) => {
          const resolved = resolveValue(col, row[col], lookupMaps);
          return resolved.toLowerCase().includes(search);
        })
      );
    }

    return rows;
  }, [table.data, mergedFilters, globalSearch, displayColumns, lookupMaps]);

  // Tri des données
  const sortedData = useMemo(() => {
    if (!sortCol) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortCol];
      const bVal = b[sortCol];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortDir === "asc" ? 1 : -1;
      if (bVal == null) return sortDir === "asc" ? -1 : 1;
      // Dates
      if (typeof aVal === "string" && typeof bVal === "string" && /^\d{4}-\d{2}-\d{2}/.test(aVal) && /^\d{4}-\d{2}-\d{2}/.test(bVal)) {
        const diff = new Date(aVal).getTime() - new Date(bVal).getTime();
        return sortDir === "asc" ? diff : -diff;
      }
      // Nombres
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      // Texte
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      const cmp = aStr.localeCompare(bStr);
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filteredData, sortCol, sortDir]);

  if (table.data.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        <Database className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>Aucune donnée</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Recherche globale */}
        <div className="relative flex-1 min-w-[180px] max-w-[320px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher..."
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#384872]/30 focus:border-[#384872]"
          />
        </div>

        {/* Bouton filtres */}
        <Button
          variant={showFilters ? "default" : "outline"}
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className={`gap-1.5 ${showFilters ? "bg-[#384872] text-white hover:bg-[#2d3a5c]" : ""}`}
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filtres
          {activeFilters.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-white/20 text-current">
              {activeFilters.length}
            </span>
          )}
        </Button>

        {/* Sélection de colonnes */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5">
              <Eye className="h-4 w-4" />
              Colonnes
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="max-h-[300px] overflow-y-auto w-[200px]"
          >
            {allColumns.map((col) => (
              <DropdownMenuCheckboxItem
                key={col}
                checked={visibleColumns.has(col)}
                onCheckedChange={(checked) => {
                  setVisibleColumns((prev) => {
                    const next = new Set(prev);
                    if (checked) {
                      next.add(col);
                    } else {
                      next.delete(col);
                    }
                    return next;
                  });
                }}
              >
                <span className="text-xs">{col}</span>
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Compteur */}
        <span className="text-xs text-slate-500 ml-auto whitespace-nowrap">
          {filteredData.length === table.data.length
            ? `${table.data.length} résultat${table.data.length > 1 ? "s" : ""}`
            : `${filteredData.length} / ${table.data.length}`}
        </span>
      </div>

      {/* Panneau filtres */}
      {showFilters && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 p-3 bg-slate-50 border rounded-lg">
          {displayColumns
            .filter((col) => {
              const t = detectColumnType(col);
              return t !== "id" || col === "id";
            })
            .map((col) => {
              const colType = detectColumnType(col);
              return (
                <div key={col} className="space-y-0.5">
                  <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                    {col}
                  </label>
                  {renderFilterControl(col, colType, mergedFilters[col], setFilter, lookupMaps)}
                </div>
              );
            })}
        </div>
      )}

      {/* Badges filtres actifs */}
      {(activeFilters.length > 0 || globalSearch.trim()) && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {globalSearch.trim() && (
            <Badge
              variant="secondary"
              className="gap-1 cursor-pointer hover:bg-slate-200"
              onClick={() => setGlobalSearch("")}
            >
              recherche: "{globalSearch}"
              <X className="h-3 w-3" />
            </Badge>
          )}
          {activeFilters.map(([col, filter]) => (
            <Badge
              key={col}
              variant="secondary"
              className="gap-1 cursor-pointer hover:bg-slate-200"
              onClick={() => setFilter(col, undefined)}
            >
              {col}: {summarizeFilter(filter, col, lookupMaps)}
              <X className="h-3 w-3" />
            </Badge>
          ))}
          <button
            onClick={clearAllFilters}
            className="text-xs text-slate-400 hover:text-slate-600 underline ml-1"
          >
            Tout effacer
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b">
              {displayColumns.map((col) => (
                <th
                  key={col}
                  className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap relative select-none cursor-pointer hover:bg-slate-100 transition-colors"
                  style={{ minWidth: columnWidths[col] ? columnWidths[col] + "px" : undefined }}
                  onClick={() => handleSort(col)}
                >
                  <span className="inline-flex items-center gap-1">
                    {col}
                    {sortCol === col && (
                      sortDir === "asc"
                        ? <ArrowUp className="h-3 w-3 text-[#384872]" />
                        : <ArrowDown className="h-3 w-3 text-[#384872]" />
                    )}
                  </span>
                  <div
                    onMouseDown={(e) => handleResizeStart(col, e)}
                    className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-[#384872]/30"
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td
                  colSpan={displayColumns.length}
                  className="text-center py-8 text-slate-400"
                >
                  Aucun résultat
                </td>
              </tr>
            ) : (
              sortedData.map((row, i) => (
                <tr
                  key={i}
                  onClick={() => onRowClick(row)}
                  className="border-b last:border-b-0 hover:bg-slate-100 transition-colors cursor-pointer"
                >
                  {displayColumns.map((col) => {
                    const fkConfig = FK_DISPLAY[col];
                    const cellValue = row[col];
                    const isClickableFk = onFkClick && fkConfig && cellValue != null && cellValue !== "";
                    return (
                      <td
                        key={col}
                        className="px-3 py-2 text-slate-700 whitespace-nowrap truncate overflow-hidden"
                        title={cellValue != null && typeof cellValue === "object" ? JSON.stringify(cellValue) : String(cellValue ?? "")}
                      >
                        {IMAGE_FIELDS.has(col) && cellValue && String(cellValue).length > 10 ? (
                          <img src={String(cellValue)} alt="" loading="lazy" className="h-7 w-7 rounded-full object-cover" />
                        ) : col === "color" && cellValue && /^#[0-9a-fA-F]{3,8}$/.test(String(cellValue)) ? (
                          <div className="flex items-center gap-2">
                            <span className="inline-block h-5 w-5 rounded-full border border-slate-200 flex-shrink-0" style={{ backgroundColor: String(cellValue) }} />
                            <span className="text-xs text-slate-400 font-mono">{String(cellValue)}</span>
                          </div>
                        ) : isClickableFk ? (
                          <button
                            className="text-[#384872] hover:underline cursor-pointer bg-transparent border-none p-0 font-inherit text-inherit"
                            onClick={(e) => {
                              e.stopPropagation();
                              onFkClick(fkConfig.table, String(cellValue));
                            }}
                          >
                            {resolveValue(col, cellValue, lookupMaps)}
                          </button>
                        ) : (
                          resolveValue(col, cellValue, lookupMaps)
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const ACCOUNT_FIELDS: { key: string; label: string }[] = [
  { key: "name", label: "Nom" },
  { key: "siret", label: "SIRET" },
  { key: "website", label: "Site web" },
  { key: "industry", label: "Secteur d'activit\u00e9" },
  { key: "address", label: "Adresse" },
  { key: "city", label: "Ville" },
  { key: "country", label: "Pays" },
  { key: "linkedinUrl", label: "LinkedIn" },
  { key: "zohoCrmAccountId", label: "Zoho CRM ID" },
  { key: "zohoBillingAccountId", label: "Zoho Billing ID" },
  { key: "logoUrl", label: "Logo" },
];

function AccountFiche({ account }: { account: Record<string, unknown> }) {
  const { t } = useTranslation();
  const str = (key: string) => {
    const v = account[key];
    return v === null || v === undefined ? "" : String(v);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
      {ACCOUNT_FIELDS.map(({ key, label }) => (
        <div key={key} className="flex items-center gap-3">
          <label className="text-sm text-slate-500 w-[140px] text-right flex-shrink-0">
            {label}
          </label>
          <div className="flex-1 px-3 py-2 bg-slate-50 border rounded-md text-sm text-slate-700 min-h-[38px]">
            {key === "logoUrl" && str(key) ? (
              <img src={str(key)} alt="Logo" loading="lazy" className="h-10 w-10 rounded object-contain" />
            ) : (
              str(key) || "\u2014"
            )}
          </div>
        </div>
      ))}
      {/* Dates */}
      <div className="flex items-center gap-3">
        <label className="text-sm text-slate-500 w-[140px] text-right flex-shrink-0">
          {t("admin.createdAt")}
        </label>
        <div className="flex-1 px-3 py-2 bg-slate-50 border rounded-md text-sm text-slate-400 min-h-[38px]">
          {str("createdAt") ? formatDateTime(str("createdAt")) : "—"}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <label className="text-sm text-slate-500 w-[140px] text-right flex-shrink-0">
          {t("admin.updatedAt")}
        </label>
        <div className="flex-1 px-3 py-2 bg-slate-50 border rounded-md text-sm text-slate-400 min-h-[38px]">
          {str("updatedAt") ? formatDateTime(str("updatedAt")) : "—"}
        </div>
      </div>
    </div>
  );
}

type GrantRow = {
  id: string;
  clientAccountId: string;
  licenceId: string;
  quantity: number;
  expiresAt: string | null;
  maxSimulations: number | null;
  durationMonths: number | null;
  rateLimitMode: "monthly" | "one_shot" | null;
  rateLimitScope: "per_user" | "per_account" | null;
  rateLimitSessions: number | null;
  grantedBy: string | null;
  note: string | null;
  createdAt: string;
  granterFirstName: string | null;
  granterLastName: string | null;
  licenceName: string | null;
  licenceCode: string | null;
  licenceColor: string | null;
  licenceLimitType: string | null;
  licenceIsActive: boolean | null;
};

function LicenceGrantsPanel({ accountId, tables }: { accountId: string; tables: Tables }) {
  const { t } = useTranslation();
  const [grants, setGrants] = useState<GrantRow[]>([]);
  const [quotas, setQuotas] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGrant, setEditingGrant] = useState<GrantRow | null>(null);
  const [deletingGrant, setDeletingGrant] = useState<GrantRow | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formLicenceId, setFormLicenceId] = useState("");
  const [formQuantity, setFormQuantity] = useState(1);
  const [formExpiresAt, setFormExpiresAt] = useState("");
  const [formMaxSimulations, setFormMaxSimulations] = useState(10);
  const [formNote, setFormNote] = useState("");
  // Phase 2.5 — extension licences custom (cf. spec/rate-limiter-sessions.md §1.x bis)
  const [formDurationType, setFormDurationType] = useState<"permanent" | "months">("permanent");
  const [formDurationMonths, setFormDurationMonths] = useState(12);
  const [formRateLimitMode, setFormRateLimitMode] = useState<"none" | "monthly" | "one_shot">("none");
  const [formRateLimitScope, setFormRateLimitScope] = useState<"per_user" | "per_account">("per_user");
  const [formRateLimitSessions, setFormRateLimitSessions] = useState(10);

  const allLicences = tables.licences?.data ?? [];

  // Get limitType from selected licence
  const selectedLicence = allLicences.find((l: any) => String(l.id) === formLicenceId) as any;
  const selectedLimitType: string = editingGrant?.licenceLimitType || selectedLicence?.limitType || "permanent";

  // Count used licences per type from user_licences table
  const accountUsers = (tables.users?.data ?? []).filter(
    (u) => String(u.clientAccountId) === accountId
  );
  const accountUserIds = new Set(accountUsers.map((u) => String(u.id)));
  const accountUserLicences = (tables.user_licences?.data ?? []).filter((ul) =>
    accountUserIds.has(String(ul.userId))
  );
  const usedByLicence: Record<string, number> = {};
  for (const ul of accountUserLicences) {
    const lid = String(ul.licenceId);
    usedByLicence[lid] = (usedByLicence[lid] || 0) + 1;
  }

  const loadGrants = async () => {
    try {
      setLoading(true);
      const [grantsData, quotasData] = await Promise.all([
        adminApi.getAccountGrants(accountId),
        accountApi.getLicenceQuotas(accountId),
      ]);
      setGrants(grantsData);
      setQuotas(quotasData);
    } catch (err) {
      console.error("Load grants error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadGrants(); }, [accountId]);

  const openCreateModal = () => {
    setEditingGrant(null);
    setFormLicenceId(allLicences.length > 0 ? String(allLicences[0]!.id) : "");
    setFormQuantity(1);
    setFormExpiresAt("");
    setFormMaxSimulations(10);
    setFormNote("");
    setFormDurationType("permanent");
    setFormDurationMonths(12);
    setFormRateLimitMode("none");
    setFormRateLimitScope("per_user");
    setFormRateLimitSessions(10);
    setShowModal(true);
  };

  const openEditModal = (grant: GrantRow) => {
    setEditingGrant(grant);
    setFormLicenceId(grant.licenceId);
    setFormQuantity(grant.quantity);
    if (grant.maxSimulations) {
      setFormMaxSimulations(grant.maxSimulations);
    }
    setFormExpiresAt(grant.expiresAt ? grant.expiresAt.slice(0, 10) : "");
    setFormNote(grant.note || "");
    // Phase 2.5 — restore extended fields
    const g = grant as any;
    if (g.durationMonths) {
      setFormDurationType("months");
      setFormDurationMonths(g.durationMonths);
    } else if (grant.expiresAt) {
      setFormDurationType("months");
      setFormDurationMonths(12); // approximation, valeur exacte recalculée à la save
    } else {
      setFormDurationType("permanent");
    }
    if (g.rateLimitMode) {
      setFormRateLimitMode(g.rateLimitMode);
      setFormRateLimitScope(g.rateLimitScope || "per_user");
      setFormRateLimitSessions(g.rateLimitSessions || 10);
    } else {
      setFormRateLimitMode("none");
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Phase 2.5 — payload étendu avec durée + rate limit (cf. spec/rate-limiter-sessions.md §1.x bis)
      const grantData: Record<string, any> = {
        quantity: formQuantity,
        note: formNote,
        durationMonths: formDurationType === "months" ? formDurationMonths : null,
        rateLimitMode: formRateLimitMode === "none" ? null : formRateLimitMode,
        rateLimitScope: formRateLimitMode === "none" ? null : formRateLimitScope,
        rateLimitSessions: formRateLimitMode === "none" ? null : formRateLimitSessions,
      };
      // Rétrocompat : si rate limit n'est pas le nouveau modèle, on garde le legacy maxSimulations / expiresAt
      if (formRateLimitMode === "none" && selectedLimitType === "simulations") {
        grantData.maxSimulations = formMaxSimulations;
      }
      if (formDurationType === "permanent" && selectedLimitType === "date") {
        grantData.expiresAt = formExpiresAt || null;
      }
      if (editingGrant) {
        await adminApi.updateAccountGrant(editingGrant.id, grantData);
      } else {
        await adminApi.createAccountGrant({
          clientAccountId: accountId,
          licenceId: formLicenceId,
          ...grantData,
        });
      }
      setShowModal(false);
      await loadGrants();
    } catch (err: any) {
      alert(err.message || t("admin.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await adminApi.deleteAccountGrant(id);
      setDeletingGrant(null);
      await loadGrants();
    } catch (err: any) {
      alert(err.message || t("admin.deleteError"));
    }
  };

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  if (loading) {
    return <div className="text-center py-8 text-slate-400">{t("common.loading")}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {allLicences.map((lic: any) => {
          const licId = String(lic.id);
          const quota = quotas[licId] ?? 0;
          const used = usedByLicence[licId] ?? 0;
          const color = lic.color || "#6B7280";
          const isExhausted = quota > 0 && used >= quota;
          return (
            <div
              key={licId}
              className="rounded-lg border p-3"
              style={{ borderColor: color + "40" }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-sm font-medium text-slate-700">
                  {lic.name?.charAt(0) + lic.name?.slice(1).toLowerCase()}
                </span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold" style={{ color }}>
                  {used}
                </span>
                <span className="text-slate-400 text-sm">/ {quota}</span>
              </div>
              <div className="text-xs text-slate-400 mt-1">
                {isExhausted && quota > 0 ? (
                  <span className="text-red-500 font-medium flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {t("admin.quotaReached")}
                  </span>
                ) : quota === 0 ? (
                  <span className="text-slate-400">{t("admin.noGrants")}</span>
                ) : (
                  <span>{quota - used} {t("account.available")}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Grants table */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">
            {t("admin.grantLicences")}
          </h3>
          <Button
            size="sm"
            onClick={openCreateModal}
            className="gap-2 bg-[#384872] hover:bg-[#2d3a5c] text-white"
          >
            <Plus className="h-4 w-4" />
            {t("admin.addLicences")}
          </Button>
        </div>

        {grants.length === 0 ? (
          <div className="text-center py-8 text-slate-400 border rounded-lg">
            <Award className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p>{t("admin.noGrants")}</p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-slate-500">{t("account.licences")}</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-500">{t("admin.quantity")}</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-500">{t("admin.expiresAt")}</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-500">Rate limit</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-500">Nbre sessions</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-500">Périmètre</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-500">{t("admin.grantedBy")}</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-500">Note</th>
                  <th className="text-right px-4 py-2 font-medium text-slate-500">{t("account.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {grants.map((grant) => (
                  <tr key={grant.id} className="border-b last:border-b-0 hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: grant.licenceColor || "#6B7280" }}
                        />
                        <span className="font-medium">
                          {grant.licenceName ? grant.licenceName.charAt(0) + grant.licenceName.slice(1).toLowerCase() : grant.licenceId.slice(0, 8)}
                        </span>
                        {grant.licenceIsActive === false && (
                          <Badge
                            variant="destructive"
                            className="text-[10px] px-1.5 py-0"
                            title={t("admin.licenceDisabledHint")}
                          >
                            {t("admin.licenceDisabledBadge")}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 font-medium">{grant.quantity}</td>
                    <td className="px-4 py-2">
                      {grant.maxSimulations ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm">{t("admin.maxSimulations")}: {grant.maxSimulations}</span>
                        </div>
                      ) : grant.expiresAt ? (
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5 text-slate-400" />
                          <span className={isExpired(grant.expiresAt) ? "text-red-500" : ""}>
                            {new Date(grant.expiresAt).toLocaleDateString()}
                          </span>
                          {isExpired(grant.expiresAt) && (
                            <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                              {t("admin.expired")}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400">{t("admin.permanent")}</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {grant.rateLimitMode ? (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {grant.rateLimitMode === "monthly" ? "Mensuel" : "One-shot"}
                        </Badge>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 font-medium">
                      {grant.rateLimitSessions != null ? grant.rateLimitSessions : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-2 text-slate-500">
                      {grant.rateLimitScope === "per_user"
                        ? "Par user"
                        : grant.rateLimitScope === "per_account"
                          ? "Par compte"
                          : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-2 text-slate-500">
                      {grant.granterFirstName
                        ? `${grant.granterFirstName} ${grant.granterLastName?.toUpperCase() || ""}`
                        : "—"}
                    </td>
                    <td className="px-4 py-2 text-slate-400 max-w-[200px] truncate" title={grant.note || ""}>
                      {grant.note || "—"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditModal(grant)}
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-[#384872]"
                          title={t("admin.editGrant")}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setDeletingGrant(grant)}
                          className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500"
                          title={t("common.delete")}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal create/edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-[#384872] mb-4">
              {editingGrant ? t("admin.editGrant") : t("admin.addLicences")}
            </h3>
            <div className="space-y-4">
              {!editingGrant && (
                <div>
                  <Label className="text-sm font-medium mb-1">{t("admin.selectLicence")}</Label>
                  <Select value={formLicenceId} onValueChange={setFormLicenceId}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {allLicences.map((lic: any) => (
                        <SelectItem key={lic.id} value={String(lic.id)}>
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: lic.color || "#6B7280" }} />
                            {lic.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-sm font-medium mb-1">{t("admin.quantity")}</Label>
                <Input
                  type="number"
                  min={1}
                  value={formQuantity}
                  onChange={(e) => setFormQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                />
              </div>
              {/* Préréglage pack one-shot : remplit les réglages standard en 1 clic
                  (évite d'oublier le rate limit / le périmètre — cf. incident 08/07). */}
              {selectedLicence?.isOneShot && (
                <div className="rounded-lg border border-[#988EF2]/50 bg-[#988EF2]/10 p-3">
                  <p className="text-xs text-slate-600 mb-2">
                    {t("admin.oneShotPresetHint", {
                      sessions: sessionsPerOneShotPack(selectedLicence?.sessionsPerPack) * formQuantity,
                      months: ONESHOT_VALIDITY_MONTHS,
                    })}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFormDurationType("months");
                      setFormDurationMonths(ONESHOT_VALIDITY_MONTHS);
                      setFormRateLimitMode("one_shot");
                      setFormRateLimitScope("per_account");
                      setFormRateLimitSessions(sessionsPerOneShotPack(selectedLicence?.sessionsPerPack) * formQuantity);
                    }}
                  >
                    {t("admin.oneShotPresetButton")}
                  </Button>
                </div>
              )}

              {/* Phase 2.5 — Durée d'activation */}
              <div>
                <Label className="text-sm font-medium mb-1">Durée d'activation</Label>
                <div className="flex gap-2">
                  <Select value={formDurationType} onValueChange={(v) => setFormDurationType(v as "permanent" | "months")}>
                    <SelectTrigger className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="permanent">Permanent</SelectItem>
                      <SelectItem value="months">Durée limitée</SelectItem>
                    </SelectContent>
                  </Select>
                  {formDurationType === "months" && (
                    <Input
                      type="number"
                      min={1}
                      value={formDurationMonths}
                      onChange={(e) => setFormDurationMonths(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-24"
                      placeholder="mois"
                    />
                  )}
                  {formDurationType === "months" && (
                    <span className="self-center text-sm text-slate-500">mois</span>
                  )}
                </div>
              </div>

              {/* Phase 2.5 — Rate limit */}
              <div>
                <Label className="text-sm font-medium mb-1">Rate limit</Label>
                <Select value={formRateLimitMode} onValueChange={(v) => setFormRateLimitMode(v as "none" | "monthly" | "one_shot")}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun (illimité jusqu'à expiration)</SelectItem>
                    <SelectItem value="monthly">Mensuel (reset 30j)</SelectItem>
                    <SelectItem value="one_shot">One-shot (total absolu)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formRateLimitMode !== "none" && (
                <>
                  <div>
                    <Label className="text-sm font-medium mb-1">Nombre de sessions max</Label>
                    <Input
                      type="number"
                      min={1}
                      value={formRateLimitSessions}
                      onChange={(e) => setFormRateLimitSessions(Math.max(1, parseInt(e.target.value) || 1))}
                    />
                    {formRateLimitMode === "monthly" && (
                      <p className="text-xs text-slate-500 mt-1">Par cycle de 30 jours, depuis la date d'attribution</p>
                    )}
                    {formRateLimitMode === "one_shot" && (
                      <p className="text-xs text-slate-500 mt-1">Total absolu pendant toute la durée de validité</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-1">Périmètre</Label>
                    <Select value={formRateLimitScope} onValueChange={(v) => setFormRateLimitScope(v as "per_user" | "per_account")}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per_user">Par utilisateur (chaque user a son quota)</SelectItem>
                        <SelectItem value="per_account">Par compte (pool partagé entre tous les users)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {/* Legacy maxSimulations : fallback si pas de rate limit nouveau modèle ET licence en mode 'simulations' */}
              {formRateLimitMode === "none" && selectedLimitType === "simulations" && (
                <div>
                  <Label className="text-sm font-medium mb-1">{t("admin.maxSimulations")} (legacy)</Label>
                  <Input
                    type="number"
                    min={1}
                    value={formMaxSimulations}
                    onChange={(e) => setFormMaxSimulations(Math.max(1, parseInt(e.target.value) || 1))}
                  />
                </div>
              )}

              <div>
                <Label className="text-sm font-medium mb-1">{t("admin.grantNote")}</Label>
                <Input
                  value={formNote}
                  onChange={(e) => setFormNote(e.target.value)}
                  placeholder={t("admin.grantNotePlaceholder")}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowModal(false)} disabled={saving}>
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || (!editingGrant && !formLicenceId)}
                className="bg-[#384872] hover:bg-[#2d3a5c] text-white"
              >
                {saving ? t("admin.saving") : t("admin.saveBtn")}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteDialog
        open={deletingGrant !== null}
        onConfirm={() => deletingGrant && handleDelete(deletingGrant.id)}
        onCancel={() => setDeletingGrant(null)}
        itemName={deletingGrant ? `${deletingGrant.licenceName || "Licence"} (x${deletingGrant.quantity})` : ""}
      />
    </div>
  );
}

function AccountDetailView({
  account,
  tables,
  lookupMaps,
  onBack,
  onEditAccount,
  onDeleteAccount,
  onEditRecord,
  onCreateUser,
  onViewAccount,
}: {
  account: Record<string, unknown>;
  tables: Tables;
  lookupMaps: LookupMaps;
  onBack: () => void;
  onEditAccount: () => void;
  onDeleteAccount: () => void;
  onEditRecord: (row: Record<string, unknown>, tableName: string, mode?: "edit" | "view") => void;
  onCreateUser: (accountId: string) => void;
  onViewAccount?: (accountId: string) => void;
}) {
  const { t } = useTranslation();
  const accountId = String(account.id);
  const accountUsers = (tables.users?.data ?? []).filter(
    (u) => String(u.clientAccountId) === accountId
  );
  const accountUserIds = new Set(accountUsers.map((u) => String(u.id)));
  const accountScenarios = (tables.scenarios?.data ?? []).filter((s) =>
    accountUserIds.has(String(s.userId))
  );

  const usersTable: TableData = {
    data: accountUsers,
    count: accountUsers.length,
    columns: tables.users?.columns ?? [],
  };
  const scenariosTable: TableData = {
    data: accountScenarios,
    count: accountScenarios.length,
    columns: tables.scenarios?.columns ?? [],
  };
  const agentsTable: TableData = {
    data: tables.simulators?.data ?? [],
    count: tables.simulators?.count ?? 0,
    columns: tables.simulators?.columns ?? [],
  };

  return (
    <div>
      {/* En-tête */}
      <div className="flex items-center gap-3 mb-1">
        <button
          onClick={onBack}
          className="p-1 rounded-md hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
          title={t("admin.backToList")}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
          {t("admin.accountLabel")}
        </span>
      </div>
      <div className="flex items-center justify-between mb-5">
        <h2
          className="text-2xl font-bold text-[#384872]"
          style={{ fontFamily: "Sora, sans-serif" }}
        >
          {String(account.name || account.companyName || account.id)}
        </h2>
        <div className="flex items-center gap-1">
          {onViewAccount && (
            <Button
              size="sm"
              className="gap-2 bg-[#384872] hover:bg-[#2d3a5c] text-white mr-2"
              onClick={() => onViewAccount(accountId)}
            >
              <Eye className="h-4 w-4" />
              Gérer ce compte
            </Button>
          )}
          <button
            onClick={onEditAccount}
            className="p-2 rounded-md hover:bg-slate-100 transition-colors text-slate-400 hover:text-[#384872]"
            title={t("admin.edit")}
          >
            <Pencil className="h-5 w-5" />
          </button>
          <button
            onClick={onDeleteAccount}
            className="p-2 rounded-md hover:bg-red-50 transition-colors text-slate-400 hover:text-red-500"
            title={t("admin.deleteBtn")}
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Onglets */}
      <Tabs defaultValue="compte">
        {/* Sur mobile : tous les onglets visibles via flex-wrap (sinon les 3
            derniers étaient clippés). h-auto pour éviter le clip vertical de
            la 2ème ligne. Sur desktop : tout reste sur une seule ligne. */}
        <TabsList className="mb-4 flex w-full h-auto flex-wrap justify-start gap-1">
          <TabsTrigger value="compte">{t("admin.accountLabel")}</TabsTrigger>
          <TabsTrigger value="users">
            {t("admin.usersTab")} ({accountUsers.length})
          </TabsTrigger>
          <TabsTrigger value="scenarios">
            {t("admin.targetsTab")} ({accountScenarios.length})
          </TabsTrigger>
          <TabsTrigger value="agents">
            Agents ({agentsTable.count})
          </TabsTrigger>
          <TabsTrigger value="licences">
            {t("admin.licencesTab")}
          </TabsTrigger>
          <TabsTrigger value="dashboard">{t("admin.dashboardTab")}</TabsTrigger>
        </TabsList>

        <TabsContent value="compte">
          <AccountFiche account={account} />
          <EntityHierarchySection account={account} tables={tables} readOnly />
        </TabsContent>

        <TabsContent value="users">
          <div className="flex justify-end mb-3">
            <Button
              size="sm"
              onClick={() => onCreateUser(accountId)}
              className="gap-2 bg-[#384872] hover:bg-[#2d3a5c] text-white"
            >
              <Plus className="h-4 w-4" />
              {t("admin.createUser")}
            </Button>
          </div>
          <DataTable
            table={usersTable}
            tableName="users"
            lookupMaps={lookupMaps}
            onRowClick={(row) => onEditRecord(row, "users")}
          />
        </TabsContent>

        <TabsContent value="scenarios">
          <DataTable
            table={scenariosTable}
            tableName="scenarios"
            lookupMaps={lookupMaps}
            onRowClick={(row) => onEditRecord(row, "scenarios", "view")}
          />
        </TabsContent>

        <TabsContent value="agents">
          <DataTable
            table={agentsTable}
            tableName="simulators"
            lookupMaps={lookupMaps}
            onRowClick={(row) => onEditRecord(row, "simulators")}
          />
        </TabsContent>

        <TabsContent value="licences">
          <LicenceGrantsPanel accountId={accountId} tables={tables} />
        </TabsContent>

        <TabsContent value="dashboard">
          <div className="text-center py-12 text-slate-400">
            <Database className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>{t("admin.statsComingSoon")}</p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

type TrashItemRow = {
  id: string;
  sourceTable: string;
  sourceId: string;
  displayName: string;
  deletedAt: string;
  expiresAt: string;
  deletedBy: string | null;
  deletedByEmail: string | null;
  deletedByFirstName: string | null;
  childCount: number;
};

function TrashPanel() {
  const { t } = useTranslation();
  const [items, setItems] = useState<TrashItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [permanentDeleteItem, setPermanentDeleteItem] = useState<TrashItemRow | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadTrash = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getTrash();
      setItems(data);
    } catch (err) {
      console.error("Load trash error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTrash(); }, []);

  const handleRestore = async (item: TrashItemRow) => {
    setActionLoading(item.id);
    try {
      await adminApi.restoreTrash(item.id);
      await loadTrash();
    } catch (err: any) {
      alert(err.message || t("admin.trashRestoreError"));
    } finally {
      setActionLoading(null);
    }
  };

  const handlePermanentDelete = async () => {
    if (!permanentDeleteItem) return;
    setActionLoading(permanentDeleteItem.id);
    try {
      await adminApi.permanentDeleteTrash(permanentDeleteItem.id);
      setPermanentDeleteItem(null);
      await loadTrash();
    } catch (err: any) {
      alert(err.message || t("admin.deleteError"));
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <Trash2 className="h-5 w-5 text-[#384872]" />
        <h2 className="text-lg font-semibold text-[#384872]">{t("admin.trash")}</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">{t("admin.trashDescription")}</p>

      {items.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Trash2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>{t("admin.trashEmpty")}</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b">
                <th className="text-left px-4 py-2 font-medium text-slate-600">Type</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">{t("admin.name")}</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">{t("admin.trashDeletedAt")}</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">{t("admin.trashExpiresAt")}</th>
                <th className="text-right px-4 py-2 font-medium text-slate-600">{t("account.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <Badge variant="outline" className="text-xs">
                      {t(`admin.trashType.${item.sourceTable}` as any, item.sourceTable)}
                    </Badge>
                  </td>
                  <td className="px-4 py-2">
                    <div className="font-medium">{item.displayName}</div>
                    {item.childCount > 0 && (
                      <div className="text-xs text-slate-400 mt-0.5">
                        {t("admin.trashChildCount", { count: item.childCount })}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-500">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5 text-slate-400" />
                      {new Date(item.deletedAt).toLocaleDateString()}
                    </div>
                    {item.deletedByFirstName && (
                      <div className="text-xs text-slate-400">{item.deletedByFirstName}</div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-500">
                    {new Date(item.expiresAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleRestore(item)}
                        disabled={actionLoading === item.id}
                        className="p-1.5 rounded hover:bg-green-50 text-slate-400 hover:text-green-600"
                        title={t("admin.trashRestore")}
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setPermanentDeleteItem(item)}
                        disabled={actionLoading === item.id}
                        className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500"
                        title={t("admin.trashPermanentDelete")}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDeleteDialog
        open={permanentDeleteItem !== null}
        onConfirm={handlePermanentDelete}
        onCancel={() => setPermanentDeleteItem(null)}
        itemName={permanentDeleteItem?.displayName || ""}
        variant="permanent"
        loading={actionLoading === permanentDeleteItem?.id}
      />
    </div>
  );
}

const ACTION_COLORS: Record<string, string> = {
  create: "bg-green-100 text-green-800",
  update: "bg-blue-100 text-blue-800",
  delete: "bg-red-100 text-red-800",
  restore: "bg-amber-100 text-amber-800",
};

function RequestLogPanel() {
  const [entries, setEntries] = useState<RequestLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState("");
  const [search, setSearch] = useState(""); // filtre chemin (serveur, au rechargement)
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const PAGE_SIZE = 50;

  const loadEntries = async (offset = 0) => {
    setLoading(true);
    try {
      const params: Record<string, any> = { limit: PAGE_SIZE, offset };
      if (method) params.method = method;
      if (search.trim()) params.path = search.trim();
      const result = await adminApi.getRequestLog(params);
      setEntries(result.entries);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEntries(page * PAGE_SIZE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, method]);

  const statusBadge = (s: number | null) => {
    if (!s) return <span className="text-slate-400">—</span>;
    const cls = s >= 500 ? "bg-red-100 text-red-700" : s >= 400 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700";
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{s < 400 ? "✓" : "✗"} {s}</span>;
  };

  const who = (e: RequestLogEntry) => {
    const main = e.userEmail || (e.userId ? e.userId.slice(0, 8) + "…" : "Anonyme");
    return e.asUserEmail ? `${main} (au nom de ${e.asUserEmail})` : main;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Filtrer par chemin (ex. /api/account) puis Entrée…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setPage(0); loadEntries(0); } }}
            className="pl-9 h-9"
          />
        </div>
        <Select value={method || "all"} onValueChange={(v) => { setMethod(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger className="w-[190px] h-9"><SelectValue placeholder="Type d'action" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les actions</SelectItem>
            <SelectItem value="GET">Consultations</SelectItem>
            <SelectItem value="POST">Actions / créations</SelectItem>
            <SelectItem value="PUT">Modifications</SelectItem>
            <SelectItem value="PATCH">Modifications</SelectItem>
            <SelectItem value="DELETE">Suppressions</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => { setPage(0); loadEntries(0); }} className="h-9">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Chargement...</div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>Aucune requête journalisée</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Date</th>
                <th className="text-left px-3 py-2 font-medium">Utilisateur</th>
                <th className="text-left px-3 py-2 font-medium">Action</th>
                <th className="text-left px-3 py-2 font-medium">Résultat</th>
                <th className="text-left px-3 py-2 font-medium">Durée</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <React.Fragment key={e.id}>
                  <tr
                    className="border-t hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => setExpandedId(expandedId === e.id ? null : e.id)}
                  >
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{formatDateTime(e.createdAt)}</td>
                    <td className="px-3 py-2 truncate max-w-[240px]" title={who(e)}>{who(e)}</td>
                    <td className="px-3 py-2">{describeRequest(e.method, e.path)}</td>
                    <td className="px-3 py-2">{statusBadge(e.statusCode)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{e.durationMs != null ? `${e.durationMs} ms` : "—"}</td>
                  </tr>
                  {expandedId === e.id && (
                    <tr className="border-t bg-slate-50/50">
                      <td colSpan={5} className="px-4 py-3">
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-600">
                          <div><span className="text-slate-400">Type technique :</span> <span className="font-mono">{e.method}</span></div>
                          <div><span className="text-slate-400">Statut HTTP :</span> <span className="font-mono">{e.statusCode ?? "—"}</span></div>
                          <div className="col-span-2"><span className="text-slate-400">Chemin :</span> <span className="font-mono">{e.path}</span></div>
                          <div><span className="text-slate-400">Fonction (handler) :</span> <span className="font-mono">{e.handlerName || "—"}</span></div>
                          <div><span className="text-slate-400">Durée :</span> {e.durationMs != null ? `${e.durationMs} ms` : "—"}</div>
                          <div className="col-span-2"><span className="text-slate-400">Utilisateur (id) :</span> <span className="font-mono">{e.userId || "anonyme"}</span></div>
                          {e.asUserEmail && (
                            <div className="col-span-2"><span className="text-slate-400">Réellement effectué par (impersonation) :</span> {e.asUserEmail}</div>
                          )}
                          <div><span className="text-slate-400">IP :</span> <span className="font-mono">{e.ip || "—"}</span></div>
                          <div className="truncate"><span className="text-slate-400">Navigateur :</span> {e.userAgent || "—"}</div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">{entries.length} requête(s) · clic sur une ligne pour le détail technique</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm text-slate-500 flex items-center">Page {page + 1}</span>
          <Button variant="outline" size="sm" disabled={entries.length < PAGE_SIZE} onClick={() => setPage(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}

function AuditLogPanel() {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterTable, setFilterTable] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const PAGE_SIZE = 50;

  const loadEntries = async (offset = 0) => {
    setLoading(true);
    try {
      const params: Record<string, any> = { limit: PAGE_SIZE, offset };
      if (filterTable) params.table = filterTable;
      const result = await adminApi.getAuditLog(params);
      setEntries(result.entries);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEntries(page * PAGE_SIZE);
  }, [page, filterTable]);

  const filtered = entries.filter((e) => {
    if (filterAction && e.action !== filterAction) return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        (e.userEmail || "").toLowerCase().includes(s) ||
        e.tableName.toLowerCase().includes(s) ||
        e.recordId.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const renderJsonDiff = (entry: AuditLogEntry) => {
    if (!entry.oldValues && !entry.newValues) return null;
    return (
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
        {entry.oldValues && (
          <div>
            <div className="font-semibold text-slate-500 mb-1">Avant</div>
            <pre className="bg-red-50 p-2 rounded overflow-auto max-h-48 whitespace-pre-wrap">
              {JSON.stringify(entry.oldValues, null, 2)}
            </pre>
          </div>
        )}
        {entry.newValues && (
          <div>
            <div className="font-semibold text-slate-500 mb-1">Après</div>
            <pre className="bg-green-50 p-2 rounded overflow-auto max-h-48 whitespace-pre-wrap">
              {JSON.stringify(entry.newValues, null, 2)}
            </pre>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Rechercher (email, table, id)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <Select value={filterTable || "all"} onValueChange={(v) => { setFilterTable(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger className="w-[180px] h-9">
            <SelectValue placeholder="Table" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les tables</SelectItem>
            {Object.keys(TABLE_LABELS).map((t) => (
              <SelectItem key={t} value={t}>{TABLE_LABELS[t]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterAction || "all"} onValueChange={(v) => { setFilterAction(v === "all" ? "" : v); }}>
          <SelectTrigger className="w-[140px] h-9">
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            <SelectItem value="create">Création</SelectItem>
            <SelectItem value="update">Modification</SelectItem>
            <SelectItem value="delete">Suppression</SelectItem>
            <SelectItem value="restore">Restauration</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => loadEntries(page * PAGE_SIZE)} className="h-9">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Chargement...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>Aucune entrée d'audit</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Date</th>
                <th className="text-left px-3 py-2 font-medium">Utilisateur</th>
                <th className="text-left px-3 py-2 font-medium">Action</th>
                <th className="text-left px-3 py-2 font-medium">Table</th>
                <th className="text-left px-3 py-2 font-medium">ID</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((entry) => (
                <React.Fragment key={entry.id}>
                  <tr
                    className="border-t hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                  >
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                      {formatDateTime(entry.createdAt)}
                    </td>
                    <td className="px-3 py-2 truncate max-w-[200px]">
                      {entry.userEmail || "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_COLORS[entry.action] || "bg-slate-100"}`}>
                        {entry.action}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">
                        {TABLE_LABELS[entry.tableName] || entry.tableName}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-400 font-mono truncate max-w-[120px]">
                      {entry.recordId.slice(0, 8)}…
                    </td>
                  </tr>
                  {expandedId === entry.id && (
                    <tr className="border-t bg-slate-50/50">
                      <td colSpan={5} className="px-4 py-3">
                        <div className="text-xs text-slate-500 mb-1">
                          Record ID: <span className="font-mono">{entry.recordId}</span>
                        </div>
                        {renderJsonDiff(entry)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">{filtered.length} entrée(s)</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-slate-500 flex items-center">Page {page + 1}</span>
          <Button variant="outline" size="sm" disabled={entries.length < PAGE_SIZE} onClick={() => setPage(page + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AdminPage({ onViewAccount, onNavigate, userProfile }: { onViewAccount?: (accountId: string) => void; onNavigate?: (page: import("../types").PageType) => void; userProfile?: string }) {
  const [tables, setTables] = useState<Tables | null>(null);
  const [activeTab, setActiveTab] = useState("__home__");
  // Sous-onglet actif dans AdminTrialInvitationsTab ("invitations" = Offres d'essai, "coupons" = Coupons).
  // Permet à la home grid d'ouvrir directement le bon sous-onglet.
  const [trialSubTab, setTrialSubTab] = useState<"invitations" | "coupons">("invitations");
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<Record<string, unknown> | null>(null);
  const [editingTableName, setEditingTableName] = useState<string | null>(null);
  const [editingMode, setEditingMode] = useState<"edit" | "create" | "view">("edit");
  const [creatingTable, setCreatingTable] = useState<string | null>(null);
  const [customLicenceWizardOpen, setCustomLicenceWizardOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Record<string, unknown> | null>(null);
  const [creatingUserForAccount, setCreatingUserForAccount] = useState<string | null>(null);
  const [deletingAccountId, setDeletingAccountId] = useState<string | null>(null);
  const [syncingInternal, setSyncingInternal] = useState(false);
  const [syncInternalResult, setSyncInternalResult] = useState<string | null>(null);
  const [translatingSimulators, setTranslatingSimulators] = useState(false);
  const [translatingCriteria, setTranslatingCriteria] = useState(false);
  const [translatingPersonae, setTranslatingPersonae] = useState(false);
  const [translatingOffers, setTranslatingOffers] = useState(false);
  const [translatingContexts, setTranslatingContexts] = useState(false);

  const handleSyncInternal = async () => {
    setSyncingInternal(true);
    setSyncInternalResult(null);
    try {
      const result = await adminApi.syncInternalUsers();
      const msg = t("admin.syncInternalSuccess", {
        created: result.synced.created,
        updated: result.synced.updated,
        linked: result.synced.linked,
      });
      setSyncInternalResult(msg);
      loadTables();
    } catch (err: any) {
      setSyncInternalResult(`${t("admin.syncInternalError")}: ${err.message}`);
    } finally {
      setSyncingInternal(false);
    }
  };

  const handleTranslateSimulators = async () => {
    setTranslatingSimulators(true);
    setSyncInternalResult(null);
    try {
      const result = await adminApi.translateSimulatorsBatch();
      setSyncInternalResult(
        i18n.language === "fr"
          ? `✅ Traduction simulateurs : ${result.translated} traduits sur ${result.scanned} scannés`
          : `✅ Simulator translation: ${result.translated} translated out of ${result.scanned} scanned`
      );
    } catch (err: any) {
      setSyncInternalResult(`❌ ${err.message}`);
    } finally {
      setTranslatingSimulators(false);
    }
  };

  // Rattrapage des traductions de critères dans les langues d'entraînement en usage.
  // Filet du déclenchement automatique (création/modification d'un collaborateur) : s'il a
  // échoué en silence, ce bouton repasse sur les collaborateurs actifs et comble les trous.
  const handleTranslateCriteria = async () => {
    setTranslatingCriteria(true);
    setSyncInternalResult(null);
    try {
      await adminApi.translateCriteriaBatch();
      setSyncInternalResult(
        i18n.language === "fr"
          ? "✅ Rattrapage des critères lancé — il tourne en arrière-plan (suivre les logs « [criteres-i18n] »)."
          : "✅ Criteria catch-up started — running in the background (follow the « [criteres-i18n] » logs)."
      );
    } catch (err: any) {
      setSyncInternalResult(`❌ ${err.message}`);
    } finally {
      setTranslatingCriteria(false);
    }
  };

  const handleTranslatePersonae = async () => {
    setTranslatingPersonae(true);
    setSyncInternalResult(null);
    try {
      const result = await adminApi.translatePersonaeBatch();
      setSyncInternalResult(
        i18n.language === "fr"
          ? `✅ Traduction personae : ${result.translated} traduits sur ${result.scanned} scannés`
          : `✅ Persona translation: ${result.translated} translated out of ${result.scanned} scanned`
      );
    } catch (err: any) {
      setSyncInternalResult(`❌ ${err.message}`);
    } finally {
      setTranslatingPersonae(false);
    }
  };

  const handleTranslateOffers = async () => {
    setTranslatingOffers(true);
    setSyncInternalResult(null);
    try {
      const result = await adminApi.translateOffersBatch();
      setSyncInternalResult(
        i18n.language === "fr"
          ? `✅ Traduction offres : ${result.translated} traduits sur ${result.scanned} scannés`
          : `✅ Offer translation: ${result.translated} translated out of ${result.scanned} scanned`
      );
    } catch (err: any) {
      setSyncInternalResult(`❌ ${err.message}`);
    } finally {
      setTranslatingOffers(false);
    }
  };

  const handleTranslateContexts = async () => {
    setTranslatingContexts(true);
    setSyncInternalResult(null);
    try {
      await adminApi.translateContextsBatch();
      // Fire-and-forget côté serveur (briefs Markdown volumineux) : on ne bloque pas
      // sur le lot, on invite à recharger pour constater le résultat.
      setSyncInternalResult(
        i18n.language === "fr"
          ? "🌐 Traduction des contextes lancée en arrière-plan. Recharge la page dans quelques minutes pour voir le résultat."
          : "🌐 Context translation started in the background. Reload the page in a few minutes to see the result."
      );
    } catch (err: any) {
      setSyncInternalResult(`❌ ${err.message}`);
    } finally {
      setTranslatingContexts(false);
    }
  };

  const loadTables = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminApi.getTables();
      setTables(data);
    } catch (err: any) {
      setError(err.message || t("admin.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTables();
  }, []);

  const lookupMaps = useMemo(() => (tables ? buildLookupMaps(tables) : {}), [tables]);

  // Système de vues
  const currentColumns = useMemo(
    () => tables?.[activeTab]?.columns ?? [],
    [tables, activeTab]
  );
  const { views, activeView, switchView, createView, updateView, duplicateView, deleteView } =
    useListViews(activeTab, currentColumns);
  const [viewEditorOpen, setViewEditorOpen] = useState(false);

  // Wrapper renderFilterControl pour ViewEditorModal (sans colType en argument)
  const renderFilterControlForEditor = (
    col: string,
    currentFilter: ViewFilterValue | undefined,
    setFilter: (col: string, filter: ViewFilterValue | undefined) => void,
    lm?: LookupMaps
  ) => {
    const colType = detectColumnType(col);
    return renderFilterControl(col, colType, currentFilter as FilterValue | undefined, setFilter as any, lm);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
        <span className="ml-3 text-slate-500">{t("admin.loadingTables")}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-red-500 mb-4">{error}</p>
        <Button variant="outline" onClick={loadTables}>
          {t("admin.retry")}
        </Button>
      </div>
    );
  }

  if (!tables) return null;

  const currentTable = tables[activeTab];

  // Clés spéciales de la grille d'accueil qui ouvrent une sous-page React
  // (routée via onNavigate) plutôt qu'un onglet de table interne à AdminPage.
  const NAV_KEYS: Record<string, import("../types").PageType> = {
    __cost_dashboard__: "cost-dashboard",
    __usage_analytics__: "usage-analytics",
    __tech_doc__: "admin-tech-doc",
    __coaching_briefs__: "admin-coaching-briefs",
    __eval_criteria_matrix__: "admin-eval-criteria-matrix",
    __kestio_knowledge__: "admin-kestio-knowledge",
    __llm_routes__: "admin-llm-routes",
    __personality_styles__: "admin-personality-styles",
    __drill__: "admin-drill",
    __banque_de_cas__: "admin-banque-de-cas",
    __relance_rules__: "admin-relance-rules",
    __reusability_kpis__: "admin-reusability-kpis",
    __path_templates__: "admin-path-templates",
    __program_rules__: "admin-program-rules",
  __intent_contexts__: "admin-intent-contexts",
    __portfolios__: "admin-portfolios",
    __legal_documents__: "admin-legal-documents",
  };

  const handleSelectTable = (key: string) => {
    const navTarget = NAV_KEYS[key];
    if (navTarget && onNavigate) {
      onNavigate(navTarget);
      return;
    }
    // Routage spécial : depuis la home grid, les items "trial_invitations" et
    // "trial_coupons" doivent ouvrir l'onglet "Essais gratuits" avec le bon
    // sous-onglet pré-sélectionné (UI custom, pas la table generic).
    if (key === "trial_invitations" || key === "trial_coupons") {
      setTrialSubTab(key === "trial_coupons" ? "coupons" : "invitations");
      setActiveTab("__trial_invitations__");
      setDrawerOpen(false);
      setSelectedAccount(null);
      return;
    }
    setActiveTab(key);
    setDrawerOpen(false);
    setSelectedAccount(null);
  };

  const sidebarContent = (
    <>
      <button
        onClick={() => handleSelectTable("__home__")}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#384872] hover:bg-white/60 rounded-r-lg transition-colors mb-3"
      >
        <Home className="h-4 w-4" />
        <span>Accueil admin</span>
      </button>
      <h2
        className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-3 px-3"
      >
        {t("admin.tablesLabel")}
      </h2>
      <nav className="flex flex-col gap-0.5 flex-1">
        {TABLE_ORDER.map((key) => {
          const table = tables[key];
          const isActive = activeTab === key;
          return (
            <button
              key={key}
              onClick={() => handleSelectTable(key)}
              className={`flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-r-lg transition-colors ${
                isActive
                  ? "bg-white text-[#384872] border-l-[3px] border-[#384872] shadow-sm"
                  : "text-slate-600 hover:bg-white/60 hover:text-slate-800 border-l-[3px] border-transparent"
              }`}
            >
              <span>{TABLE_LABELS[key]}</span>
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  isActive
                    ? "bg-[#384872] text-white"
                    : "bg-slate-200/80 text-slate-700"
                }`}
              >
                {table?.count ?? 0}
              </span>
            </button>
          );
        })}
      </nav>
      <div className="mt-4 px-3 pb-2 space-y-2">
        <button
          onClick={() => handleSelectTable("__trash__")}
          className={`flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium rounded-r-lg transition-colors ${
            activeTab === "__trash__"
              ? "bg-white text-[#384872] border-l-[3px] border-[#384872] shadow-sm"
              : "text-slate-600 hover:bg-white/60 hover:text-slate-800 border-l-[3px] border-transparent"
          }`}
        >
          <Trash2 className="h-4 w-4" />
          {t("admin.trash")}
        </button>
        <button
          onClick={() => handleSelectTable("__audit__")}
          className={`flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium rounded-r-lg transition-colors ${
            activeTab === "__audit__"
              ? "bg-white text-[#384872] border-l-[3px] border-[#384872] shadow-sm"
              : "text-slate-600 hover:bg-white/60 hover:text-slate-800 border-l-[3px] border-transparent"
          }`}
        >
          <FileText className="h-4 w-4" />
          Audit Log
        </button>
        <button
          onClick={() => handleSelectTable("__request_log__")}
          className={`flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium rounded-r-lg transition-colors ${
            activeTab === "__request_log__"
              ? "bg-white text-[#384872] border-l-[3px] border-[#384872] shadow-sm"
              : "text-slate-600 hover:bg-white/60 hover:text-slate-800 border-l-[3px] border-transparent"
          }`}
        >
          <FileText className="h-4 w-4" />
          Journal des requêtes
        </button>
        <button
          onClick={() => handleSelectTable("__badges__")}
          className={`flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium rounded-r-lg transition-colors ${
            activeTab === "__badges__"
              ? "bg-white text-[#384872] border-l-[3px] border-[#384872] shadow-sm"
              : "text-slate-600 hover:bg-white/60 hover:text-slate-800 border-l-[3px] border-transparent"
          }`}
        >
          <Award className="h-4 w-4" style={{ color: activeTab === "__badges__" ? "#F6C847" : undefined }} />
          {i18n.language === "fr" ? "Banque de badges" : "Badge bank"}
        </button>
        <button
          onClick={() => handleSelectTable("__inter_challenges__")}
          className={`flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium rounded-r-lg transition-colors ${
            activeTab === "__inter_challenges__"
              ? "bg-white text-[#384872] border-l-[3px] border-[#384872] shadow-sm"
              : "text-slate-600 hover:bg-white/60 hover:text-slate-800 border-l-[3px] border-transparent"
          }`}
        >
          <Trophy className="h-4 w-4" style={{ color: activeTab === "__inter_challenges__" ? "#F6C847" : undefined }} />
          {i18n.language === "fr" ? "Challenges inter" : "Inter challenges"}
        </button>
        <button
          onClick={() => handleSelectTable("__trial_invitations__")}
          className={`flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium rounded-r-lg transition-colors ${
            activeTab === "__trial_invitations__"
              ? "bg-white text-[#384872] border-l-[3px] border-[#384872] shadow-sm"
              : "text-slate-600 hover:bg-white/60 hover:text-slate-800 border-l-[3px] border-transparent"
          }`}
        >
          <Mail className="h-4 w-4" style={{ color: activeTab === "__trial_invitations__" ? "#7CBF94" : undefined }} />
          {i18n.language === "fr" ? "Essais gratuits" : "Free trials"}
        </button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleSyncInternal}
          disabled={syncingInternal}
          className="w-full gap-2"
        >
          <UsersIcon className="h-4 w-4" />
          {syncingInternal ? t("admin.syncInternalRunning") : t("admin.syncInternal")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleTranslateSimulators}
          disabled={translatingSimulators}
          className="w-full gap-2"
        >
          <Languages className="h-4 w-4" />
          {translatingSimulators
            ? (i18n.language === "fr" ? "Traduction…" : "Translating…")
            : (i18n.language === "fr" ? "🌐 Traduire simulateurs" : "🌐 Translate simulators")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleTranslateCriteria}
          disabled={translatingCriteria}
          className="w-full gap-2"
        >
          <Languages className="h-4 w-4" />
          {translatingCriteria
            ? (i18n.language === "fr" ? "Lancement…" : "Starting…")
            : (i18n.language === "fr" ? "🌐 Traduire critères (langues d'entraînement)" : "🌐 Translate criteria (training languages)")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleTranslatePersonae}
          disabled={translatingPersonae}
          className="w-full gap-2"
        >
          <Languages className="h-4 w-4" />
          {translatingPersonae
            ? (i18n.language === "fr" ? "Traduction…" : "Translating…")
            : (i18n.language === "fr" ? "🌐 Traduire personae" : "🌐 Translate personae")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleTranslateOffers}
          disabled={translatingOffers}
          className="w-full gap-2"
        >
          <Languages className="h-4 w-4" />
          {translatingOffers
            ? (i18n.language === "fr" ? "Traduction…" : "Translating…")
            : (i18n.language === "fr" ? "🌐 Traduire offres" : "🌐 Translate offers")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleTranslateContexts}
          disabled={translatingContexts}
          className="w-full gap-2"
        >
          <Languages className="h-4 w-4" />
          {translatingContexts
            ? (i18n.language === "fr" ? "Traduction…" : "Translating…")
            : (i18n.language === "fr" ? "🌐 Traduire contextes" : "🌐 Translate contexts")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={loadTables}
          className="w-full gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          {t("admin.refreshLabel")}
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b mb-4">
        <div className="flex items-center gap-3">
          {/* Hamburger button - mobile only */}
          <button
            onClick={() => setDrawerOpen(true)}
            className="md:hidden p-1.5 rounded-md hover:bg-slate-100 transition-colors"
            aria-label={t("admin.openMenu")}
          >
            <Menu className="h-5 w-5 text-slate-600" />
          </button>
          <Database className="h-6 w-6" style={{ color: "#384872" }} />
          <h1
            className="text-2xl font-bold"
            style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}
          >
            {t("admin.title")}
          </h1>
        </div>
        <div className="hidden md:flex items-center gap-2">
          {/* Sync + Traduire centralisés dans la section « Actions » de la grille d'accueil.
              On garde uniquement « Rafraîchir » accessible en permanence depuis le header. */}
          <Button
            variant="outline"
            size="sm"
            onClick={loadTables}
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            {t("admin.refreshLabel")}
          </Button>
        </div>
      </div>

      {/* Sync internal result banner */}
      {syncInternalResult && (
        <div className="mb-4 p-3 rounded-md bg-blue-50 border border-blue-200 text-sm text-blue-800 flex items-center justify-between">
          <span>{syncInternalResult}</span>
          <button onClick={() => setSyncInternalResult(null)} className="ml-2 text-blue-500 hover:text-blue-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Mobile drawer overlay */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setDrawerOpen(false)}
          />
          {/* Drawer */}
          <div className="absolute inset-y-0 left-0 w-64 bg-slate-50 shadow-xl flex flex-col p-4 animate-in slide-in-from-left duration-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5" style={{ color: "#384872" }} />
                <span className="font-semibold text-[#384872]">Admin</span>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-1.5 rounded-md hover:bg-slate-200 transition-colors"
                aria-label={t("admin.closeMenu")}
              >
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              {sidebarContent}
            </div>
          </div>
        </div>
      )}

      {/* Accueil : grille de blocs thématiques, pleine largeur */}
      {activeTab === "__home__" ? (
        <div className="space-y-4">
          {onNavigate && (
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onNavigate("preview-rate-limit")}
                className="gap-2"
              >
                👁️ Aperçu UI Rate Limit
              </Button>
            </div>
          )}
          <AdminHomeGrid
            tables={tables}
            onSelectTable={handleSelectTable}
            userProfile={userProfile}
            actions={[
              {
                key: "sync",
                labelKey: "admin.syncInternal",
                Icon: UsersIcon,
                onClick: handleSyncInternal,
                disabled: syncingInternal,
              },
              {
                key: "translate-simulators",
                labelKey: translatingSimulators ? "admin.translating" : "admin.translateSimulators",
                Icon: Languages,
                onClick: handleTranslateSimulators,
                disabled: translatingSimulators,
              },
              {
                key: "translate-criteria",
                labelKey: translatingCriteria ? "admin.translating" : "admin.translateCriteria",
                Icon: Languages,
                onClick: handleTranslateCriteria,
                disabled: translatingCriteria,
              },
              {
                key: "translate-personae",
                labelKey: translatingPersonae ? "admin.translating" : "admin.translatePersonae",
                Icon: Languages,
                onClick: handleTranslatePersonae,
                disabled: translatingPersonae,
              },
              {
                key: "translate-offers",
                labelKey: translatingOffers ? "admin.translating" : "admin.translateOffers",
                Icon: Languages,
                onClick: handleTranslateOffers,
                disabled: translatingOffers,
              },
              {
                key: "translate-contexts",
                labelKey: translatingContexts ? "admin.translating" : "admin.translateContexts",
                Icon: Languages,
                onClick: handleTranslateContexts,
                disabled: translatingContexts,
              },
              {
                key: "refresh",
                labelKey: "admin.refreshLabel",
                Icon: RefreshCw,
                onClick: loadTables,
              },
            ]}
          />
        </div>
      ) : (
      /* Main layout: sidebar + content */
      <div className="flex flex-1 min-h-0 gap-0">
        {/* Sidebar - desktop only */}
        <aside className="hidden md:flex flex-col w-[260px] flex-shrink-0 bg-slate-50 border-r rounded-l-lg p-4">
          {sidebarContent}
        </aside>

        {/* Table content */}
        <div className="flex-1 min-w-0 md:pl-4">
          {activeTab === "__audit__" ? (
            <AuditLogPanel />
          ) : activeTab === "__request_log__" ? (
            <RequestLogPanel />
          ) : activeTab === "__trash__" ? (
            <TrashPanel />
          ) : activeTab === "__badges__" ? (
            <BadgeBankView />
          ) : activeTab === "__inter_challenges__" ? (
            <InterChallengesView viewerProfile={userProfile} />
          ) : activeTab === "__catalog_contexts__" ? (
            <AdminCatalogContextsTab />
          ) : activeTab === "__trial_invitations__" ? (
            <AdminTrialInvitationsTab subTab={trialSubTab} onSubTabChange={setTrialSubTab} />
          ) : activeTab === "client_accounts" && selectedAccount && tables ? (
            <AccountDetailView
              account={selectedAccount}
              tables={tables}
              lookupMaps={lookupMaps}
              onBack={() => setSelectedAccount(null)}
              onEditAccount={() => {
                setEditingRecord(selectedAccount);
                setEditingTableName("client_accounts");
                setEditingMode("edit");
              }}
              onDeleteAccount={() => setDeletingAccountId(String(selectedAccount.id))}
              onEditRecord={(row, tableName, mode) => {
                setEditingRecord(row);
                setEditingTableName(tableName);
                setEditingMode(mode || "edit");
              }}
              onCreateUser={(accountId) => setCreatingUserForAccount(accountId)}
              onViewAccount={onViewAccount}
            />
          ) : currentTable ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <ViewSelector
                  views={views}
                  activeView={activeView}
                  onSwitch={switchView}
                  onEdit={() => setViewEditorOpen(true)}
                  onCreateNew={() => {
                    const newView = createView(
                      t("admin.newView"),
                      activeView.columns,
                      {}
                    );
                    setViewEditorOpen(true);
                  }}
                  onDuplicate={duplicateView}
                  onDelete={deleteView}
                />
                <div className="flex items-center gap-2">
                  {currentTable.readOnly && (
                    <span className="text-sm text-slate-500" data-testid="admin-table-read-only">{t("admin.readOnly")}</span>
                  )}
                  {activeTab === "licences" && !currentTable.readOnly && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCustomLicenceWizardOpen(true)}
                      className="gap-2 border-violet-300 text-violet-700 hover:bg-violet-50"
                    >
                      <Sparkles className="h-4 w-4" />
                      {t("customLicenceWizard.openButton")}
                    </Button>
                  )}
                  {!TABLES_WITHOUT_CREATE.has(activeTab) && !currentTable.readOnly && (
                    <Button
                      size="sm"
                      onClick={() => setCreatingTable(activeTab)}
                      className="gap-2 bg-[#384872] hover:bg-[#2d3a5c] text-white"
                    >
                      <Plus className="h-4 w-4" />
                      {t("admin.create")}
                    </Button>
                  )}
                </div>
              </div>
              <DataTable
                table={currentTable}
                tableName={activeTab}
                lookupMaps={lookupMaps}
                viewColumns={activeView.columns}
                viewFilters={activeView.filters}
                onRowClick={(row) => {
                  if (activeTab === "client_accounts") {
                    setSelectedAccount(row);
                  } else {
                    setEditingRecord(row);
                    setEditingTableName(activeTab);
                    setEditingMode("view");
                  }
                }}
                onFkClick={(targetTable, id) => {
                  const targetData = tables?.[targetTable]?.data;
                  if (!targetData) return;
                  const record = targetData.find((r: Record<string, unknown>) => String(r.id) === id);
                  if (record) {
                    setEditingRecord(record);
                    setEditingTableName(targetTable);
                    setEditingMode("view");
                  }
                }}
              />
            </>
          ) : null}
        </div>
      </div>
      )}

      {/* Modale d'édition / lecture */}
      {editingRecord && (editingTableName || activeTab) && tables && (
        <EditModal
          record={editingRecord}
          columns={tables[editingTableName || activeTab]?.columns ?? []}
          tableName={editingTableName || activeTab}
          mode={editingMode}
          tables={tables}
          onClose={() => {
            setEditingRecord(null);
            setEditingTableName(null);
          }}
          onSaved={() => {
            setEditingRecord(null);
            setEditingTableName(null);
            loadTables();
          }}
          onDuplicated={(newRecord) => {
            // Refresh la liste + bascule la modale sur la nouvelle row en mode edit.
            loadTables();
            setEditingRecord(newRecord);
            setEditingMode("edit");
          }}
        />
      )}

      {/* Modale de création */}
      {creatingTable && tables[creatingTable] && (
        <EditModal
          record={{}}
          columns={tables[creatingTable]!.columns}
          tableName={creatingTable}
          mode="create"
          tables={tables}
          onClose={() => setCreatingTable(null)}
          onSaved={() => {
            setCreatingTable(null);
            loadTables();
          }}
        />
      )}

      {/* Wizard "Nouvelle licence custom" (ADM, devis gros volume — pool de sessions mutualisé) */}
      <CustomLicenceWizard
        open={customLicenceWizardOpen}
        onClose={() => setCustomLicenceWizardOpen(false)}
        onCreated={() => loadTables()}
        clientAccounts={(tables.client_accounts?.data ?? []).map((r: Record<string, unknown>) => ({
          id: String(r.id),
          name: String(r.name || r.id),
        }))}
        simulators={(tables.simulators?.data ?? []).map((r: Record<string, unknown>) => ({
          id: String(r.id),
          name: String(r.name || r.id),
          isActive: r.isActive !== false,
        }))}
      />

      {/* Modale de création user depuis un compte */}
      {creatingUserForAccount && tables.users && (
        <EditModal
          record={{ clientAccountId: creatingUserForAccount }}
          columns={tables.users.columns}
          tableName="users"
          mode="create"
          tables={tables}
          onClose={() => setCreatingUserForAccount(null)}
          onSaved={() => {
            setCreatingUserForAccount(null);
            loadTables();
          }}
        />
      )}

      {/* Modale de confirmation de suppression de compte */}
      {deletingAccountId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeletingAccountId(null)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm m-4 p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-2">{t("admin.confirmDeleteTitle")}</h3>
            <p className="text-sm text-slate-500 mb-6">
              {t("admin.confirmDeleteMessage")}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setDeletingAccountId(null)}>
                {t("admin.cancel")}
              </Button>
              <Button
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={async () => {
                  try {
                    await adminApi.deleteRecord("client_accounts", deletingAccountId);
                    setDeletingAccountId(null);
                    setSelectedAccount(null);
                    loadTables();
                  } catch (err: any) {
                    alert(err.message || t("admin.deleteError"));
                  }
                }}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {t("admin.deleteBtn")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modale éditeur de vue */}
      {viewEditorOpen && activeView && (
        <ViewEditorModal
          view={activeView}
          allColumns={currentColumns}
          lookupMaps={lookupMaps}
          renderFilterControl={renderFilterControlForEditor}
          onClose={() => setViewEditorOpen(false)}
          onSave={(updates) => {
            updateView(activeView.id, updates);
            setViewEditorOpen(false);
          }}
        />
      )}
    </div>
  );
}
